import React, { useEffect, useRef, useState } from 'react'
import { lumi } from '../lib/lumi'
import {Plus, Search, Filter, Edit, Copy, Trash2, Eye, Play, Pause, Square, Calendar, Users, BarChart3, FileText, Printer, Upload, X, Image as ImageIcon, Layers, GitBranch, ChevronLeft, ChevronRight, CheckCircle, Mic, Loader2, Download, Share2, Building2} from 'lucide-react'
import toast from 'react-hot-toast'
import QuestionBuilder, { Question, SurveyPage, getQuestionReferenceLabel, normalizeQuestionPageState } from '../components/QuestionBuilder'
import ResearcherSurveyModal from '../components/ResearcherSurveyModal'
import RichTextEditor from '../components/RichTextEditor'
import PageManager from '../components/PageManager'
import QuestionBranchManager from '../components/QuestionBranchManager'
import { buildSurveyCode, syncSurveyLibrary } from '../lib/surveyLibrary'
import { createClientPortalPasswordCredentials } from '../lib/portalAccess'
import { downloadAudioDirectly } from '../lib/offlineSurveyQueue'
import {
  evaluateDisplayConditions as evaluateRuntimeDisplayConditions,
  getFilteredQuestionOptions,
  getNextSurveyLocation,
  getPreviousVisibleQuestionIndex,
  getResolvedQuestionContent,
  getUnansweredRequiredQuestions,
  isAnswerFilled,
} from '../lib/surveyRuntime'
import {
  getImageDisplayHint,
  getImageDisplayWidthClass,
  getLogoDisplayHint,
  getLogoDisplayImageClass,
  getLogoDisplayPrintHeight,
  getLogoDisplayPrintWidth,
  getLogoDisplayWidthClass,
  ensureHtmlString,
  stripHtml as stripHtmlText,
} from '../lib/surveyPresentation'

type UserProfile = 'admin' | 'researcher'

interface AppSession {
  profile: UserProfile
  userId: string
  email: string
  userName: string
  userRole?: string
  researcherId?: string | null
  researcherName?: string | null
  loginAt: string
}

interface SurveyAssignment {
  _id: string
  surveyId: string
  researcherId: string
  status: 'assigned' | 'in_progress' | 'completed' | 'removed'
  assignedAt: string
  startedAt?: string
  completedAt?: string
  removedAt?: string
  removedReason?: string
}

interface Survey {
  _id: string
  title: string
  description: string
  status: 'draft' | 'active' | 'paused' | 'finished'
  languages: string[]
  startDate?: string
  endDate?: string
  targetAudience?: string
  clientId?: string
  clientName?: string
  tags: string[]
  questions?: Question[]
  pages?: SurveyPage[]
  logoUrl?: string
  logoDisplaySize?: 'small' | 'medium' | 'large' | 'original'
  showSurveyTitle?: boolean
  showPageTitles?: boolean
  showLogoOnEveryPage?: boolean
  audioRecordingEnabled?: boolean
  clientResultsEnabled?: boolean
  clientResultsToken?: string
  publicResponseEnabled?: boolean
  publicResponseToken?: string
  clientResultsPassword?: string
  clientResultsPasswordHash?: string
  clientResultsPasswordSalt?: string
  clientResultsPasswordUpdatedAt?: string
  surveyCode?: string
  libraryFileName?: string
  libraryFileUrl?: string
  libraryVersion?: number
  librarySavedAt?: string
  isDeleted?: boolean
  deletedAt?: string
  removedAssignmentIds?: string[]
  createdAt: string
  updatedAt: string
}

interface Researcher {
  _id: string
  name: string
  email: string
  isActive: boolean
  assignedSurveys?: number
  completedSurveys?: number
  specialization?: string
}

interface Client {
  _id: string
  name: string
  companyName?: string
  contactName?: string
  email?: string
  phone?: string
  isActive: boolean
}

interface AssignmentWithResearcher extends SurveyAssignment {
  researcher?: Researcher | null
}

interface ResponseAudioItem {
  responseId: string
  surveyId: string
  researcherName: string
  submittedAt: string
  audioUrl: string
  audioData?: string
  audioName: string
  durationSec: number | null
}

interface SurveyResponseAnswerRecord {
  questionId: string
  questionTitle?: string
  questionText?: string
  questionType?: string
  answer: any
}

interface SurveyResponseRecord {
  _id: string
  surveyId?: string
  surveyTitle?: string
  researcherName?: string
  researcherId?: string
  language?: string
  status?: string
  submittedAt?: string
  completedAt?: string
  createdAt?: string
  updatedAt?: string
  isDeleted?: boolean
  deletedAt?: string
  deletedBy?: string
  audio_url?: string
  answers?: SurveyResponseAnswerRecord[]
}

type PercentageBase = 'observation' | 'citation'
type ChartColorMode = 'palette' | 'single' | 'custom'

type ResultChartType =
  | 'bar_simple'
  | 'bar_grouped'
  | 'bar_stacked'
  | 'bar_stacked_100'
  | 'bar_simple_3d'
  | 'bar_grouped_3d'
  | 'bar_stacked_3d'
  | 'bar_stacked_100_3d'
  | 'column_simple'
  | 'column_grouped'
  | 'column_stacked'
  | 'column_stacked_100'
  | 'column_simple_3d'
  | 'column_grouped_3d'
  | 'column_stacked_3d'
  | 'column_stacked_100_3d'
  | 'line_simple'
  | 'line_stacked'
  | 'line_markers'
  | 'line_stacked_markers'
  | 'line_simple_3d'
  | 'line_stacked_3d'
  | 'line_markers_3d'
  | 'line_stacked_markers_3d'
  | 'pie'
  | 'donut'
  | 'pie_3d'
  | 'donut_3d'
  | 'scatter_simple'
  | 'scatter_lines'
  | 'scatter_lines_markers'
  | 'map_choropleth'
  | 'radar_simple'
  | 'radar_markers'
  | 'radar_filled'
  | 'waterfall_simple'
  | 'funnel_simple'
  | 'combo_column_line'
  | 'combo_column_line_secondary'
  | 'combo_area_column'
  | 'combo_custom'

const RESULT_CHART_GROUPS: Array<{
  label: string
  options: Array<{ value: ResultChartType; label: string }>
}> = [
  {
    label: 'Barras',
    options: [
      { value: 'bar_simple', label: 'Barras simples' },
      { value: 'bar_grouped', label: 'Barras agrupadas' },
      { value: 'bar_stacked', label: 'Barras empilhadas' },
      { value: 'bar_stacked_100', label: 'Barras empilhadas 100%' },
      { value: 'bar_simple_3d', label: 'Barras simples 3D' },
      { value: 'bar_grouped_3d', label: 'Barras agrupadas 3D' },
      { value: 'bar_stacked_3d', label: 'Barras empilhadas 3D' },
      { value: 'bar_stacked_100_3d', label: 'Barras empilhadas 100% 3D' },
    ],
  },
  {
    label: 'Colunas',
    options: [
      { value: 'column_simple', label: 'Colunas simples' },
      { value: 'column_grouped', label: 'Colunas agrupadas' },
      { value: 'column_stacked', label: 'Colunas empilhadas' },
      { value: 'column_stacked_100', label: 'Colunas empilhadas 100%' },
      { value: 'column_simple_3d', label: 'Colunas simples 3D' },
      { value: 'column_grouped_3d', label: 'Colunas agrupadas 3D' },
      { value: 'column_stacked_3d', label: 'Colunas empilhadas 3D' },
      { value: 'column_stacked_100_3d', label: 'Colunas empilhadas 100% 3D' },
    ],
  },
  {
    label: 'Linhas',
    options: [
      { value: 'line_simple', label: 'Linhas simples' },
      { value: 'line_stacked', label: 'Linhas empilhadas' },
      { value: 'line_markers', label: 'Linhas com marcadores' },
      { value: 'line_stacked_markers', label: 'Linhas empilhadas com marcadores' },
      { value: 'line_simple_3d', label: 'Linhas simples 3D' },
      { value: 'line_stacked_3d', label: 'Linhas empilhadas 3D' },
      { value: 'line_markers_3d', label: 'Linhas com marcadores 3D' },
      { value: 'line_stacked_markers_3d', label: 'Linhas empilhadas com marcadores 3D' },
    ],
  },
  {
    label: 'Circulos',
    options: [
      { value: 'pie', label: 'Pizza' },
      { value: 'donut', label: 'Rosca' },
      { value: 'pie_3d', label: 'Pizza 3D' },
      { value: 'donut_3d', label: 'Rosca 3D' },
    ],
  },
  {
    label: 'Dispersao',
    options: [
      { value: 'scatter_simple', label: 'Dispersao simples' },
      { value: 'scatter_lines', label: 'Dispersao com linhas' },
      { value: 'scatter_lines_markers', label: 'Dispersao com linhas e marcadores' },
    ],
  },
  {
    label: 'Mapa',
    options: [
      { value: 'map_choropleth', label: 'Mapa coropletico' },
    ],
  },
  {
    label: 'Radar',
    options: [
      { value: 'radar_simple', label: 'Radar simples' },
      { value: 'radar_markers', label: 'Radar com marcadores' },
      { value: 'radar_filled', label: 'Radar preenchido' },
    ],
  },
  {
    label: 'Cascata',
    options: [
      { value: 'waterfall_simple', label: 'Cascata simples' },
    ],
  },
  {
    label: 'Funil',
    options: [
      { value: 'funnel_simple', label: 'Funil simples' },
    ],
  },
  {
    label: 'Combinação',
    options: [
      { value: 'combo_column_line', label: 'Coluna clusterizada - linha' },
      { value: 'combo_column_line_secondary', label: 'Coluna clusterizada - linha no eixo secundario' },
      { value: 'combo_area_column', label: 'Area empilhada - coluna clusterizada' },
      { value: 'combo_custom', label: 'Combinação personalizada' },
    ],
  },
]

const SESSION_KEY = 'app_dna_session'
const OTHER_OPTION_ID = '__other__'

const readSession = (): AppSession | null => {
  if (typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(SESSION_KEY)
    if (!raw) return null

    const session = JSON.parse(raw) as AppSession
    const isValidProfile = session.profile === 'admin' || session.profile === 'researcher'

    if (!isValidProfile || !session.userId || !session.email || !session.userName) {
      return null
    }

    return session
  } catch (error) {
    console.error('Erro ao ler sessao local:', error)
    return null
  }
}

const normalizeBrokenCopySuffix = (value: string) => value.replace(/\(\s*C[^)]*pia\s*\)/gi, '(Copia)')

const normalizeRichTextValue = (value?: string) => {
  if (!value) return ''

  return normalizeBrokenCopySuffix(value)
    .replace(/&nbsp;/gi, ' ')
    .replace(/(<\/p>\s*<p>)/gi, '</p> <p>')
}

const generateClientResultsToken = () => `dna-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`
const CLIENT_RESULTS_PUBLIC_BASE_URL = 'https://pesquisasdna.com.br'

const normalizeSurveyRecord = (survey: Survey): Survey => ({
  ...survey,
  title: normalizeRichTextValue(survey.title),
  description: normalizeRichTextValue(survey.description),
  targetAudience: normalizeRichTextValue(survey.targetAudience),
})

const isSurveyVisible = (survey: Pick<Survey, 'isDeleted' | 'deletedAt'>) => (
  survey.isDeleted !== true && !survey.deletedAt
)

interface PrintSurveySettings {
  pageSize: 'a4' | 'letter'
  orientation: 'portrait' | 'landscape'
  density: 'comfortable' | 'compact'
  boundaryStyle: 'cards' | 'lines' | 'none'
  optionLayout: 'list' | 'columns' | 'inline'
  fontSize: 'small' | 'medium' | 'large'
  showHeaderImage: boolean
  headerImageUrl: string
  logoAlign: 'left' | 'center' | 'right'
  headerTitle: string
  headerSubtitle: string
  /** Tres quadros editaveis so para impressao */
  headerBoxLeft: string
  headerBoxCenter: string
  headerBoxRight: string
  /** legado — espelha headerBoxRight */
  headerRightText: string
  showGeneratedAt: boolean
  showQuestionNumbers: boolean
  showInstructions: boolean
  showPageTitles: boolean
  showRequiredMark: boolean
  showImageCaptions: boolean
}

const createDefaultPrintSettings = (survey: Survey): PrintSurveySettings => ({
  pageSize: 'a4',
  orientation: 'portrait',
  density: 'compact',
  boundaryStyle: 'cards',
  optionLayout: 'columns',
  fontSize: 'small',
  showHeaderImage: !!survey.logoUrl,
  headerImageUrl: survey.logoUrl || '',
  logoAlign: 'center',
  headerTitle: normalizeRichTextValue(survey.title),
  headerSubtitle: '',
  headerBoxLeft: '',
  headerBoxCenter: '',
  headerBoxRight: survey.clientName || '',
  headerRightText: survey.clientName || '',
  showGeneratedAt: true,
  showQuestionNumbers: true,
  showInstructions: true,
  showPageTitles: true,
  showRequiredMark: true,
  showImageCaptions: true,
})

const PRINT_FONT_SIZES: Record<PrintSurveySettings['fontSize'], { body: string; title: string; page: string; question: string; option: string }> = {
  small: { body: '11px', title: '22px', page: '15px', question: '12px', option: '11px' },
  medium: { body: '13px', title: '26px', page: '17px', question: '14px', option: '13px' },
  large: { body: '15px', title: '30px', page: '19px', question: '16px', option: '15px' },
}

const SurveyManager: React.FC = () => {
  const session = readSession()
  const isResearcherSession = session?.profile === 'researcher' && !!session.researcherId

  const [surveys, setSurveys] = useState<Survey[]>([])
  const [surveyResponseCounts, setSurveyResponseCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [surveysPage, setSurveysPage] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingSurvey, setEditingSurvey] = useState<Survey | null>(null)
  const [previewQuestion, setPreviewQuestion] = useState<Question | null>(null)
  const [showQuestionBranches, setShowQuestionBranches] = useState(false)
  const [respondingSurvey, setRespondingSurvey] = useState<Survey | null>(null)
  const [testingSurvey, setTestingSurvey] = useState<Survey | null>(null)
  const [surveyAnswers, setSurveyAnswers] = useState<Record<string, any>>({})
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [navigationHistory, setNavigationHistory] = useState<number[]>([])
  const [resultsSurvey, setResultsSurvey] = useState<Survey | null>(null)
  const [assigningSurvey, setAssigningSurvey] = useState<Survey | null>(null)
  const [activeTerminalPage, setActiveTerminalPage] = useState<SurveyPage | null>(null)
  const [printConfigSurvey, setPrintConfigSurvey] = useState<Survey | null>(null)
  const [showPublicLinkModal, setShowPublicLinkModal] = useState<Survey | null>(null)

  const [audioConsent, setAudioConsent] = useState(false)
  const [audioConsentAt, setAudioConsentAt] = useState<string | null>(null)
  const [nextResponseNumber, setNextResponseNumber] = useState(1)

  const [isRecordingAudio, setIsRecordingAudio] = useState(false)
  const [isUploadingAudio, setIsUploadingAudio] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)

  const [audioPreviewUrl, setAudioPreviewUrl] = useState<string | null>(null)
  const [audioUploadedUrl, setAudioUploadedUrl] = useState<string | null>(null)
  const [audioFileName, setAudioFileName] = useState<string | null>(null)
  const [audioRecordedAt, setAudioRecordedAt] = useState<string | null>(null)
  const [audioDurationSec, setAudioDurationSec] = useState<number | null>(null)

  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null)
  const mediaStreamRef = React.useRef<MediaStream | null>(null)
  const audioChunksRef = React.useRef<BlobPart[]>([])
  const recordingIntervalRef = React.useRef<number | null>(null)

  const stripHtml = (value?: string) => stripHtmlText(value)

  const formatTime = (totalSeconds: number) => {
    const m = Math.floor(totalSeconds / 60)
    const s = totalSeconds % 60
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  const sanitizeFileBase = (rawName: string) => {
    const base = stripHtml(rawName)
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^\w\-]+/g, '')
      .slice(0, 50)

    return base || 'Pesquisa'
  }

  const formatTimestampForFile = (d: Date) => {
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    const hh = String(d.getHours()).padStart(2, '0')
    const mi = String(d.getMinutes()).padStart(2, '0')
    const ss = String(d.getSeconds()).padStart(2, '0')
    return `${yyyy}${mm}${dd}_${hh}${mi}${ss}`
  }

  const makeAudioFileName = (surveyTitle: string, responseNumber: number, d: Date) => {
    const base = sanitizeFileBase(surveyTitle)
    const num = String(responseNumber).padStart(3, '0')
    const ts = formatTimestampForFile(d)
    return `${base}_${num}_${ts}.mp3`
  }

  const clearRecordingInterval = () => {
    if (recordingIntervalRef.current !== null) {
      window.clearInterval(recordingIntervalRef.current)
      recordingIntervalRef.current = null
    }
  }

  const stopMediaStream = () => {
    mediaStreamRef.current?.getTracks?.().forEach(t => t.stop())
    mediaStreamRef.current = null
  }

  const resetAudioState = async (deleteRemote: boolean) => {
    try {
      const rec = mediaRecorderRef.current
      if (rec && rec.state !== 'inactive') {
        rec.ondataavailable = null
        rec.onstop = null
        rec.stop()
      }
    } catch {
      // ignore
    } finally {
      mediaRecorderRef.current = null
    }

    clearRecordingInterval()
    stopMediaStream()
    audioChunksRef.current = []

    if (audioPreviewUrl) {
      try { URL.revokeObjectURL(audioPreviewUrl) } catch { /* ignore */ }
    }

    if (deleteRemote && audioUploadedUrl) {
      try {
        await lumi.tools.file.delete([audioUploadedUrl])
      } catch {
        // ignore
      }
    }

    setIsRecordingAudio(false)
    setIsUploadingAudio(false)
    setRecordingSeconds(0)
    setAudioPreviewUrl(null)
    setAudioUploadedUrl(null)
    setAudioFileName(null)
    setAudioRecordedAt(null)
    setAudioDurationSec(null)
  }

  const getSupportedAudioMimeType = () => {
    if (typeof MediaRecorder === 'undefined') return ''
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/ogg',
      'audio/mp4'
    ]
    return candidates.find(t => MediaRecorder.isTypeSupported(t)) || ''
  }

  const startAudioRecording = async () => {
    if (isRecordingAudio || isUploadingAudio) return

    if (!audioConsent) {
      toast.error('Você precisa aceitar o aviso antes de gravar.')
      return
    }

    if (!navigator?.mediaDevices?.getUserMedia) {
      toast.error('Gravação de áudio não é suportada neste navegador/dispositivo.')
      return
    }

    try {
      await resetAudioState(false)

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStreamRef.current = stream

      audioChunksRef.current = []
      const mimeType = getSupportedAudioMimeType()
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)

      mediaRecorderRef.current = recorder

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data)
        }
      }

      recorder.start(250)

      setIsRecordingAudio(true)
      setRecordingSeconds(0)
      setAudioRecordedAt(new Date().toISOString())
      if (!audioConsentAt) setAudioConsentAt(new Date().toISOString())

      recordingIntervalRef.current = window.setInterval(() => {
        setRecordingSeconds(prev => prev + 1)
      }, 1000)
    } catch (error) {
      console.error('Erro ao iniciar gravação:', error)
      toast.error('Erro ao iniciar gravação. Verifique as permissões do microfone.')
      clearRecordingInterval()
      stopMediaStream()
      setIsRecordingAudio(false)
    }
  }

  const cancelAudioRecording = async () => {
    const rec = mediaRecorderRef.current
    if (!rec) {
      await resetAudioState(false)
      return
    }

    try {
      // Evitar disparar onstop criando blob
      rec.onstop = null
      rec.ondataavailable = null
      if (rec.state !== 'inactive') rec.stop()
    } catch {
      // ignore
    }

    await resetAudioState(false)
  }

  const finishAudioRecording = async () => {
    const rec = mediaRecorderRef.current
    if (!rec || rec.state === 'inactive') return

    if (recordingSeconds >= 5 * 60) {
      toast.error('Limite de 5 minutos atingido.')
    }

    setIsUploadingAudio(true)

    try {
      const blob = await new Promise<Blob>((resolve) => {
        rec.onstop = () => {
          const mime = rec.mimeType || 'audio/webm'
          const b = new Blob(audioChunksRef.current, { type: mime })
          resolve(b)
        }

        rec.stop()
      })

      clearRecordingInterval()
      stopMediaStream()
      mediaRecorderRef.current = null

      setIsRecordingAudio(false)
      setAudioDurationSec(recordingSeconds)

      const previewUrl = URL.createObjectURL(blob)
      setAudioPreviewUrl(previewUrl)

      const now = new Date()
      const surveyTitle = respondingSurvey?.title || 'Pesquisa'
      const fileName = makeAudioFileName(surveyTitle, nextResponseNumber, now)
      setAudioFileName(fileName)

      const file = new File([blob], fileName, { type: blob.type || 'audio/webm' })
      const result = await lumi.tools.file.upload([file])

      if (result?.[0]?.fileUrl) {
        setAudioUploadedUrl(result[0].fileUrl)
        toast.success('Áudio enviado com sucesso!')
      } else {
        const err = result?.[0]?.uploadError || 'Falha no upload do áudio'
        toast.error(String(err))
      }
    } catch (error) {
      console.error('Erro ao finalizar gravação:', error)
      toast.error('Erro ao finalizar/gravar o áudio.')
    } finally {
      setIsUploadingAudio(false)
    }
  }


  const buildOrderedQuestions = (survey: Survey): Question[] => {
    const questions = survey.questions || []
    const pages = survey.pages || []
    const questionMap = new Map<string, Question>()
    questions.forEach(q => questionMap.set(q.id, q))

    const orderedIds: string[] = pages.flatMap(p => p.questionIds || [])
    const ordered: Question[] = []
    const addedIds = new Set<string>()

    for (const id of orderedIds) {
      const q = questionMap.get(id)
      if (q && !addedIds.has(id)) {
        ordered.push(q)
        addedIds.add(id)
      }
    }

    for (const q of questions) {
      if (!addedIds.has(q.id)) {
        ordered.push(q)
        addedIds.add(q.id)
      }
    }

    return ordered
  }

  const escapeHtml = (value?: string) => (
    value || ''
  )
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

  const renderPrintRichText = (value?: unknown) => {
    const html = ensureHtmlString(value)

    return html
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, '')
      .replace(/\son\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
      .replace(/\s(?:href|src)\s*=\s*(["'])\s*javascript:[\s\S]*?\1/gi, '')
  }

  const buildPrintableSurveyHtml = (survey: Survey, settings: PrintSurveySettings = createDefaultPrintSettings(survey)) => {
    const logoDisplaySize = survey.logoDisplaySize || 'medium'
    const orderedQuestions = buildOrderedQuestions(survey)
    const fontSizes = PRINT_FONT_SIZES[settings.fontSize]
    const pageMargin = settings.density === 'compact' ? '10mm' : '16mm'
    const questionPrefix = (pageIndex: number, questionIndex: number) => (
      settings.showQuestionNumbers ? `${pageIndex + 1}.${questionIndex + 1} ` : ''
    )
    const openAnswerLabel = settings.showInstructions ? '<div class="question-open">Resposta aberta</div>' : ''
    const pageMap = new Map((survey.pages || []).flatMap((page) => (
      (page.questionIds || []).map((questionId) => [questionId, page])
    )))

    const groupedQuestions = orderedQuestions.reduce<Record<string, Question[]>>((accumulator, question) => {
      const page = pageMap.get(question.id)
      const key = page?.id || 'sem-página'
      if (!accumulator[key]) accumulator[key] = []
      accumulator[key].push(question)
      return accumulator
    }, {})

    const pageBlocks = ((survey.pages && survey.pages.length > 0) ? survey.pages : [{
      id: 'sem-página',
      title: 'Questionario',
      description: '',
      questionIds: orderedQuestions.map((question) => question.id)
    }]).map((page, pageIndex) => {
      const pageQuestions = groupedQuestions[page.id] || []
      if (pageQuestions.length === 0 && page.id !== 'sem-página') {
        return `
          <section class="page-block">
            ${settings.showPageTitles ? `<div class="page-title">Página ${pageIndex + 1}: ${escapeHtml(stripHtmlText(page.title))}</div>` : ''}
            ${settings.showInstructions && page.description ? `<p class="page-description">${escapeHtml(stripHtmlText(page.description || ''))}</p>` : ''}
          </section>
        `
      }

      return `
        <section class="page-block">
          ${settings.showPageTitles ? `<div class="page-title">Página ${pageIndex + 1}: ${escapeHtml(stripHtmlText(page.title))}</div>` : ''}
          ${settings.showInstructions && page.description ? `<p class="page-description">${escapeHtml(stripHtmlText(page.description))}</p>` : ''}
          ${pageQuestions.map((question, questionIndex) => `
            <article class="question-block">
              <div class="question-title">
                ${questionPrefix(pageIndex, questionIndex)}${escapeHtml(stripHtmlText(question.title))}
                ${settings.showRequiredMark && question.required ? '<span class="required">*</span>' : ''}
              </div>
              ${settings.showInstructions && question.description ? `<div class="question-description">${escapeHtml(stripHtmlText(question.description))}</div>` : ''}
              ${question.imageUrl ? `
                <div class="question-image question-image-${question.imageDisplaySize || 'original'}">
                  <img src="${escapeHtml(question.imageUrl)}" alt="${escapeHtml(stripHtmlText(question.title))}" />
                  ${settings.showImageCaptions ? `<span class="image-caption">Imagem: ${escapeHtml(getImageDisplayHint(question.imageDisplaySize))}</span>` : ''}
                </div>
              ` : ''}
              ${question.options && question.options.length > 0 ? `
                <ul class="question-options option-layout-${settings.optionLayout}">
                  ${question.options.map((option) => `<li>${escapeHtml(stripHtmlText(option.text))}</li>`).join('')}
                </ul>
              ` : openAnswerLabel}
            </article>
          `).join('')}
        </section>
      `
    }).join('')

    return `
      <!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(stripHtmlText(survey.title))}</title>
          <style>
            @page { size: ${settings.pageSize.toUpperCase()} ${settings.orientation}; margin: ${pageMargin}; }
            * { box-sizing: border-box; }
            body {
              font-family: Arial, sans-serif;
              color: #0f172a;
              font-size: ${fontSizes.body};
              line-height: ${settings.density === 'compact' ? '1.35' : '1.55'};
              margin: 0;
            }
            .print-shell { max-width: 100%; }
            .header {
              display: flex;
              align-items: center;
              gap: ${settings.density === 'compact' ? '14px' : '24px'};
              margin-bottom: ${settings.density === 'compact' ? '16px' : '28px'};
              border-bottom: 1px solid #cbd5e1;
              padding-bottom: ${settings.density === 'compact' ? '12px' : '20px'};
            }
            .survey-logo { flex: 0 0 auto; }
            .survey-logo img { display: block; width: 100%; height: auto; object-fit: contain; }
            .title { font-size: ${fontSizes.title}; font-weight: 700; margin: 0; }
            .subtitle { margin-top: 6px; color: #475569; line-height: 1.45; }
            .meta { margin-top: 8px; color: #64748b; font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; }
            .page-block { margin-top: ${settings.density === 'compact' ? '14px' : '24px'}; page-break-inside: avoid; }
            .page-title { font-size: ${fontSizes.page}; font-weight: 700; color: #1d4ed8; margin-bottom: 6px; }
            .page-description { color: #475569; margin: 0 0 ${settings.density === 'compact' ? '8px' : '14px'}; }
            .question-block {
              border: 1px solid #e2e8f0;
              border-radius: ${settings.boundaryStyle === 'cards' ? '10px' : '0'};
              padding: ${settings.density === 'compact' ? '8px 10px' : '14px'};
              margin-bottom: ${settings.density === 'compact' ? '8px' : '14px'};
              page-break-inside: avoid;
            }
            .boundary-lines .question-block { border-width: 0 0 1px; padding-left: 0; padding-right: 0; }
            .boundary-none .question-block { border: 0; padding-left: 0; padding-right: 0; }
            .question-title { font-size: ${fontSizes.question}; font-weight: 600; line-height: 1.4; }
            .required { color: #dc2626; }
            .question-description { margin-top: 5px; color: #475569; line-height: 1.45; }
            .question-options { margin: ${settings.density === 'compact' ? '8px' : '12px'} 0 0 0; padding: 0; list-style-position: inside; font-size: ${fontSizes.option}; }
            .question-options li { margin-bottom: ${settings.density === 'compact' ? '3px' : '7px'}; line-height: 1.35; break-inside: avoid; }
            .option-layout-columns { columns: 2; column-gap: 22px; }
            .option-layout-inline { display: flex; flex-wrap: wrap; gap: 6px 18px; list-style: none; }
            .option-layout-inline li::before { content: "□ "; color: #64748b; }
            .question-open { margin-top: 8px; font-style: italic; color: #64748b; }
            .question-image { margin-top: 10px; }
            .question-image img { width: 100%; border-radius: 8px; border: 1px solid #e2e8f0; object-fit: contain; }
            .question-image-small { max-width: 180px; }
            .question-image-medium { max-width: 300px; }
            .question-image-large { max-width: 460px; }
            .question-image-original { max-width: 100%; }
            .image-caption { display: block; margin-top: 4px; font-size: 10px; color: #64748b; }
            @media print {
              body { margin: 0; }
              .page-block { page-break-inside: avoid; }
              .question-block { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <main class="print-shell density-${settings.density} boundary-${settings.boundaryStyle}">
          <header class="header">
            ${settings.showHeaderImage && settings.headerImageUrl ? `
              <div class="survey-logo" style="max-width: ${getLogoDisplayPrintWidth(logoDisplaySize)};">
                <img
                  src="${escapeHtml(settings.headerImageUrl)}"
                  alt="Logo da pesquisa"
                  style="max-height: ${getLogoDisplayPrintHeight(logoDisplaySize)};"
                />
              </div>
            ` : ''}
            <div>
              <h1 class="title">${escapeHtml(settings.headerTitle || stripHtmlText(survey.title))}</h1>
              ${settings.headerSubtitle ? `<p class="subtitle">${escapeHtml(settings.headerSubtitle)}</p>` : ''}
              <div class="meta">Idiomas: ${(survey.languages || ['pt']).join(', ').toUpperCase()} - Perguntas: ${orderedQuestions.length}</div>
            </div>
          </header>
          ${pageBlocks}
          </main>
        </body>
      </html>
    `
  }

  const buildPrintableSurveyHtmlV2 = (survey: Survey, settings: PrintSurveySettings = createDefaultPrintSettings(survey)) => {
    const logoDisplaySize = survey.logoDisplaySize || 'medium'
    const orderedQuestions = buildOrderedQuestions(survey)
    const fontSizes = PRINT_FONT_SIZES[settings.fontSize]
    const pageMargin = settings.density === 'compact' ? '10mm' : '16mm'
    const generatedAt = new Date().toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
    const printBoxLeft = renderPrintRichText(settings.headerBoxLeft).trim()
    const printBoxCenter = renderPrintRichText(settings.headerBoxCenter).trim()
    const printBoxRight = renderPrintRichText(settings.headerBoxRight || settings.headerRightText).trim()
    const hasPrintBoxes = !!(printBoxLeft || printBoxCenter || printBoxRight)
    const printHeaderBoxesHtml = hasPrintBoxes ? `
      <div class="print-header-boxes">
        <div class="print-header-box print-rich-text">${printBoxLeft || '&nbsp;'}</div>
        <div class="print-header-box print-rich-text">${printBoxCenter || '&nbsp;'}</div>
        <div class="print-header-box print-rich-text">${printBoxRight || '&nbsp;'}</div>
      </div>
    ` : ''
    const printHeaderTitle = renderPrintRichText(settings.headerTitle || survey.title)
    const printHeaderSubtitle = renderPrintRichText(settings.headerSubtitle)
    const printLogoHtml = settings.showHeaderImage && settings.headerImageUrl ? `
      <div class="print-page-logo">
        <img src="${escapeHtml(settings.headerImageUrl)}" alt="Logo da pesquisa" />
      </div>
    ` : ''

    const questionPrefix = (pageIndex: number, questionIndex: number) => (
      settings.showQuestionNumbers ? `${pageIndex + 1}.${questionIndex + 1} ` : ''
    )
    const openAnswerLines = (label = 'Resposta aberta') => `
      <div class="open-answer">
        ${settings.showInstructions ? `<div class="open-answer-label">${escapeHtml(label)}</div>` : ''}
        <div class="answer-line"></div>
        <div class="answer-line"></div>
      </div>
    `
    const pageMap = new Map((survey.pages || []).flatMap((page) => (
      (page.questionIds || []).map((questionId) => [questionId, page])
    )))
    const optionsWithOther = (question: Question) => {
      const options = [...(question.options || [])]
      if (question.allowOther) {
        options.push({
          id: OTHER_OPTION_ID,
          text: question.otherLabel?.trim() || 'Outro'
        })
      }
      return options
    }
    const otherLine = (question: Question) => (
      question.allowOther ? `<div class="other-text-line">${escapeHtml(stripHtmlText(question.otherPlaceholder || 'Digite sua resposta'))}: <span></span></div>` : ''
    )
    const renderChoiceOptions = (question: Question, marker: 'single' | 'multiple') => {
      const options = optionsWithOther(question)
      if (options.length === 0) return openAnswerLines()

      return `
        <ul class="question-options option-layout-${settings.optionLayout}">
          ${options.map((option) => `
            <li>
              <span class="choice-marker ${marker}"></span>
              <span>${escapeHtml(stripHtmlText(option.text))}</span>
              ${option.id === OTHER_OPTION_ID ? otherLine(question) : ''}
            </li>
          `).join('')}
        </ul>
      `
    }
    const renderScale = (question: Question, compact = false) => {
      const min = question.minValue ?? 0
      const max = question.maxValue ?? 100
      const step = Math.max(1, question.stepValue ?? 1)
      const spread = Math.max(1, max - min)
      const tickCount = Math.min(11, Math.floor(spread / step) + 1)
      const ticks = Array.from({ length: tickCount }, (_, index) => {
        if (tickCount === 1) return min
        return Math.round(min + (spread * index) / (tickCount - 1))
      })

      return `
        <div class="${compact ? 'slider-scale compact' : 'slider-scale'}">
          <div class="slider-labels">
            <span>${escapeHtml(stripHtmlText(question.minLabel || String(min)))}</span>
            <span>${escapeHtml(stripHtmlText(question.maxLabel || String(max)))}</span>
          </div>
          <div class="slider-track">
            ${ticks.map((tick) => `<span><i></i><b>${escapeHtml(String(tick))}</b></span>`).join('')}
          </div>
        </div>
      `
    }
    const renderMatrixTable = (question: Question, marker: 'single' | 'multiple' = 'single') => {
      const rows = (question.matrixRows && question.matrixRows.length > 0)
        ? question.matrixRows
        : [{ id: 'row_1', label: 'Item 1' }]
      const columns = (question.matrixColumns && question.matrixColumns.length > 0)
        ? question.matrixColumns
        : [
          { id: 'col_1', label: 'Opção 1', value: '1' },
          { id: 'col_2', label: 'Opção 2', value: '2' },
        ]

      return `
        <table class="print-table matrix-table">
          <thead>
            <tr>
              <th>Item</th>
              ${columns.map((column) => `<th>${escapeHtml(stripHtmlText(column.label))}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${rows.map((row) => `
              <tr>
                <td>${escapeHtml(stripHtmlText(row.label))}</td>
                ${columns.map(() => `<td class="mark-cell"><span class="choice-marker ${marker}"></span></td>`).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      `
    }
    const getRatingGlyph = (question: Question) => {
      switch (question.ratingIcon) {
        case 'heart':
          return '♥'
        case 'smile':
          return '☺'
        case 'thumb':
          return '👍'
        case 'star':
        default:
          return '★'
      }
    }
    const renderRatingScale = (question: Question) => {
      const max = Math.max(1, question.maxValue || 5)
      const glyph = getRatingGlyph(question)
      const iconClass = question.ratingIcon || 'star'
      return `
        <div class="rating-scale">
          <div class="slider-labels">
            <span>${escapeHtml(stripHtmlText(question.minLabel || ''))}</span>
            <span>${escapeHtml(stripHtmlText(question.maxLabel || ''))}</span>
          </div>
          <div class="rating-items">
            ${Array.from({ length: max }, (_, index) => `
              <span class="rating-item">
                <span class="rating-glyph rating-glyph-${iconClass}" aria-hidden="true">${glyph}</span>
                <b>${index + 1}</b>
              </span>
            `).join('')}
          </div>
        </div>
      `
    }
    const renderRatingMatrix = (question: Question) => {
      const rows = (question.matrixRows && question.matrixRows.length > 0)
        ? question.matrixRows
        : [{ id: 'row_1', label: 'Item 1' }]
      const max = Math.max(1, question.maxValue || 5)
      const glyph = getRatingGlyph(question)
      const iconClass = question.ratingIcon || 'star'

      return `
        <table class="print-table matrix-table">
          <thead>
            <tr>
              <th>Item</th>
              ${Array.from({ length: max }, (_, index) => `<th><span class="rating-glyph rating-glyph-${iconClass}">${glyph}</span><div>${index + 1}</div></th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${rows.map((row) => `
              <tr>
                <td>${escapeHtml(stripHtmlText(row.label))}</td>
                ${Array.from({ length: max }, () => `<td class="mark-cell"><span class="rating-glyph rating-glyph-${iconClass}">${glyph}</span></td>`).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      `
    }
    const renderSliderMatrix = (question: Question) => {
      const rows = (question.matrixRows && question.matrixRows.length > 0)
        ? question.matrixRows
        : [{ id: 'row_1', label: 'Item 1' }]

      return `
        <div class="matrix-slider-list">
          ${rows.map((row) => `
            <div class="matrix-slider-row">
              <div class="matrix-slider-label">${escapeHtml(stripHtmlText(row.label))}</div>
              ${renderScale(question, true)}
            </div>
          `).join('')}
        </div>
      `
    }
    const renderRanking = (question: Question) => {
      const options = optionsWithOther(question)
      if (options.length === 0) return openAnswerLines()

      return `
        <ol class="ranking-list">
          ${options.map((option) => `
            <li>
              <span class="rank-box"></span>
              <span>${escapeHtml(stripHtmlText(option.text))}</span>
              ${option.id === OTHER_OPTION_ID ? otherLine(question) : ''}
            </li>
          `).join('')}
        </ol>
      `
    }
    const renderClassification = (question: Question) => {
      // Resposta ordenada: lista com caixa de posicao (1, 2, 3...)
      const items = (question.options && question.options.length > 0)
        ? optionsWithOther(question).map((option) => ({ id: option.id, label: option.text }))
        : (question.matrixRows || []).map((row) => ({ id: row.id, label: row.label }))

      if (items.length === 0) return openAnswerLines()

      return `
        <ol class="ranking-list">
          ${items.map((item) => `
            <li>
              <span class="rank-box"></span>
              <span>${escapeHtml(stripHtmlText(item.label))}</span>
              ${item.id === OTHER_OPTION_ID ? otherLine(question) : ''}
            </li>
          `).join('')}
        </ol>
      `
    }
    const renderBestWorst = (question: Question) => {
      const options = optionsWithOther(question)
      if (options.length === 0) return openAnswerLines()

      return `
        <table class="print-table best-worst-table">
          <thead>
            <tr>
              <th>Opção</th>
              <th>Melhor</th>
              <th>Pior</th>
            </tr>
          </thead>
          <tbody>
            ${options.map((option) => `
              <tr>
                <td>
                  ${escapeHtml(stripHtmlText(option.text))}
                  ${option.id === OTHER_OPTION_ID ? otherLine(question) : ''}
                </td>
                <td class="mark-cell"><span class="choice-marker single"></span></td>
                <td class="mark-cell"><span class="choice-marker single"></span></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `
    }
    const renderMultiText = (question: Question) => {
      const boxes = question.textBoxes || []
      if (boxes.length === 0) return openAnswerLines()

      return `
        <div class="multi-text-list">
          ${boxes.map((box) => `
            <div class="multi-text-row">
              <span>${escapeHtml(stripHtmlText(box.label))}</span>
              <i></i>
            </div>
          `).join('')}
        </div>
      `
    }
    const renderQuestionAnswer = (question: Question) => {
      switch (question.type) {
        case 'single':
        case 'dropdown':
          return renderChoiceOptions(question, 'single')
        case 'multiple':
        case 'checkbox':
          return renderChoiceOptions(question, 'multiple')
        case 'ranking':
          return renderRanking(question)
        case 'classification':
          return renderClassification(question)
        case 'bestworst':
          return renderBestWorst(question)
        case 'matrix':
        case 'dropdown_matrix':
          return renderMatrixTable(question, 'single')
        case 'rating':
          return renderRatingScale(question)
        case 'rating_matrix':
          return renderRatingMatrix(question)
        case 'slider':
          return renderScale(question)
        case 'slider_matrix':
          return renderSliderMatrix(question)
        case 'multitext':
          return renderMultiText(question)
        case 'datetime':
          return openAnswerLines('Data/hora')
        case 'number':
          return openAnswerLines('Número')
        case 'email':
          return openAnswerLines('E-mail')
        case 'media':
          return `<div class="media-check"><span class="choice-marker multiple"></span><span>Conteudo visualizado</span></div>`
        case 'text':
        case 'opentext':
        default:
          return openAnswerLines()
      }
    }

    const groupedQuestions = orderedQuestions.reduce<Record<string, Question[]>>((accumulator, question) => {
      const page = pageMap.get(question.id)
      const key = page?.id || 'sem-página'
      if (!accumulator[key]) accumulator[key] = []
      accumulator[key].push(question)
      return accumulator
    }, {})

    const pageBlocks = ((survey.pages && survey.pages.length > 0) ? survey.pages : [{
      id: 'sem-página',
      title: 'Questionario',
      description: '',
      questionIds: orderedQuestions.map((question) => question.id)
    }]).map((page, pageIndex) => {
      const pageQuestions = groupedQuestions[page.id] || []

      return `
        <section class="page-block">
          ${settings.showPageTitles ? `<div class="page-title">Página ${pageIndex + 1}: ${escapeHtml(stripHtmlText(page.title))}</div>` : ''}
          ${settings.showInstructions && page.description ? `<p class="page-description">${escapeHtml(stripHtmlText(page.description || ''))}</p>` : ''}
          ${pageQuestions.map((question, questionIndex) => `
            <article class="question-block">
              <div class="question-title">
                ${questionPrefix(pageIndex, questionIndex)}${escapeHtml(stripHtmlText(question.title))}
                ${settings.showRequiredMark && question.required ? '<span class="required">*</span>' : ''}
              </div>
              ${settings.showInstructions && question.description ? `<div class="question-description">${escapeHtml(stripHtmlText(question.description))}</div>` : ''}
              ${question.imageUrl ? `
                <div class="question-image question-image-${question.imageDisplaySize || 'original'}">
                  <img src="${escapeHtml(question.imageUrl)}" alt="${escapeHtml(stripHtmlText(question.title))}" />
                  ${settings.showImageCaptions ? `<span class="image-caption">Imagem: ${escapeHtml(getImageDisplayHint(question.imageDisplaySize))}</span>` : ''}
                </div>
              ` : ''}
              ${renderQuestionAnswer(question)}
            </article>
          `).join('')}
        </section>
      `
    }).join('')

    return `
      <!doctype html>
      <html lang="pt-BR">
        <head>
          <meta charset="utf-8" />
          <title>${escapeHtml(stripHtmlText(survey.title))}</title>
          <style>
            @page { size: ${settings.pageSize.toUpperCase()} ${settings.orientation}; margin: ${pageMargin}; }
            * { box-sizing: border-box; }
            body {
              font-family: Arial, sans-serif;
              color: #0f172a;
              font-size: ${fontSizes.body};
              line-height: ${settings.density === 'compact' ? '1.35' : '1.55'};
              margin: 0;
            }
            table { border-collapse: collapse; width: 100%; }
            .print-shell { max-width: 100%; }
            .print-document { width: 100%; border-collapse: collapse; }
            .print-document > thead { display: table-header-group; }
            .print-page-header { border-bottom: 1px solid #cbd5e1; padding-bottom: 8px; }
            .print-header-boxes {
              display: grid;
              grid-template-columns: 1fr 1fr 1fr;
              gap: 8px;
              margin-bottom: 8px;
            }
            .print-header-box {
              min-height: 28px;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              padding: 6px 8px;
              font-size: 10px;
              line-height: 1.35;
              color: #334155;
              white-space: pre-wrap;
              word-break: break-word;
            }
            .print-rich-text {
              min-width: 0;
              overflow-wrap: anywhere;
              word-break: normal;
              white-space: normal;
            }
            .print-rich-text p,
            .print-rich-text ul,
            .print-rich-text ol {
              margin: 0 0 0.35em;
            }
            .print-rich-text p:last-child,
            .print-rich-text ul:last-child,
            .print-rich-text ol:last-child {
              margin-bottom: 0;
            }
            .print-rich-text strong,
            .print-rich-text b { font-weight: 700; }
            .print-rich-text em,
            .print-rich-text i { font-style: italic; }
            .print-rich-text u { text-decoration: underline; }
            .print-rich-text s { text-decoration: line-through; }
            .print-rich-text .ql-align-center { text-align: center; }
            .print-rich-text .ql-align-right { text-align: right; }
            .print-rich-text .ql-align-justify { text-align: justify; }
            .print-rich-text .ql-size-small { font-size: 0.75em; }
            .print-rich-text .ql-size-large { font-size: 1.5em; }
            .print-rich-text .ql-size-huge { font-size: 2.5em; }
            .print-page-logo {
              display: flex;
              width: 100%;
              margin-top: 4px;
              justify-content: ${settings.logoAlign === 'left' ? 'flex-start' : settings.logoAlign === 'right' ? 'flex-end' : 'center'};
            }
            .print-page-logo img {
              display: block;
              max-width: ${getLogoDisplayPrintWidth(logoDisplaySize)};
              max-height: ${getLogoDisplayPrintHeight(logoDisplaySize)};
              object-fit: contain;
            }
            .header {
              margin: ${settings.density === 'compact' ? '14px' : '22px'} 0 ${settings.density === 'compact' ? '14px' : '24px'};
              text-align: center;
              border-bottom: 1px solid #cbd5e1;
              padding-bottom: ${settings.density === 'compact' ? '10px' : '16px'};
            }
            .title { font-size: ${fontSizes.title}; font-weight: 700; margin: 0 auto; max-width: 92%; text-align: center; }
            .subtitle { margin: 8px auto 0; max-width: 92%; color: #475569; line-height: 1.45; text-align: center; }
            .meta { margin-top: 8px; color: #64748b; font-size: 10px; letter-spacing: 0.02em; }
            .page-block { margin-top: ${settings.density === 'compact' ? '14px' : '24px'}; }
            .page-title { font-size: ${fontSizes.page}; font-weight: 700; color: #1d4ed8; margin-bottom: 6px; }
            .page-description { color: #475569; margin: 0 0 ${settings.density === 'compact' ? '8px' : '14px'}; }
            .question-block {
              border: 1px solid #e2e8f0;
              border-radius: ${settings.boundaryStyle === 'cards' ? '10px' : '0'};
              padding: ${settings.density === 'compact' ? '8px 10px' : '14px'};
              margin-bottom: ${settings.density === 'compact' ? '8px' : '14px'};
              page-break-inside: avoid;
            }
            .boundary-lines .question-block { border-width: 0 0 1px; padding-left: 0; padding-right: 0; }
            .boundary-none .question-block { border: 0; padding-left: 0; padding-right: 0; }
            .question-title { font-size: ${fontSizes.question}; font-weight: 600; line-height: 1.4; }
            .required { color: #dc2626; }
            .question-description { margin-top: 5px; color: #475569; line-height: 1.45; }
            .question-options { margin: ${settings.density === 'compact' ? '8px' : '12px'} 0 0 0; padding: 0; list-style: none; font-size: ${fontSizes.option}; }
            .question-options li { display: flex; align-items: flex-start; gap: 7px; margin-bottom: ${settings.density === 'compact' ? '4px' : '7px'}; line-height: 1.35; break-inside: avoid; }
            .option-layout-columns { columns: 2; column-gap: 22px; }
            .option-layout-columns li { break-inside: avoid; display: flex; }
            .option-layout-inline { display: flex; flex-wrap: wrap; gap: 6px 18px; }
            .choice-marker { display: inline-block; width: 11px; height: 11px; margin-top: 2px; flex: 0 0 auto; border: 1.4px solid #475569; background: #fff; }
            .choice-marker.single { border-radius: 999px; }
            .choice-marker.multiple { border-radius: 2px; }
            .open-answer { margin-top: 9px; }
            .open-answer-label { margin-bottom: 4px; color: #64748b; font-style: italic; }
            .answer-line { height: 18px; border-bottom: 1px solid #cbd5e1; }
            .other-text-line { display: block; width: 100%; margin: 4px 0 0 18px; color: #64748b; font-size: 10px; }
            .other-text-line span { display: inline-block; min-width: 130px; border-bottom: 1px solid #cbd5e1; }
            .question-image { margin-top: 10px; }
            .question-image img { width: 100%; border-radius: 8px; border: 1px solid #e2e8f0; object-fit: contain; }
            .question-image-small { max-width: 180px; }
            .question-image-medium { max-width: 300px; }
            .question-image-large { max-width: 460px; }
            .question-image-original { max-width: 100%; }
            .image-caption { display: block; margin-top: 4px; font-size: 10px; color: #64748b; }
            .print-table { margin-top: 10px; font-size: ${fontSizes.option}; page-break-inside: avoid; }
            .print-table th,
            .print-table td { border: 1px solid #cbd5e1; padding: ${settings.density === 'compact' ? '5px 6px' : '8px'}; vertical-align: top; }
            .print-table th { background: #f8fafc; color: #334155; font-weight: 700; text-align: center; }
            .print-table td:first-child,
            .print-table th:first-child { text-align: left; }
            .mark-cell { width: 72px; text-align: center; }
            .mark-cell .choice-marker { margin: 0 auto; }
            .best-worst-table th:not(:first-child),
            .best-worst-table td:not(:first-child) { width: 82px; text-align: center; }
            .slider-scale { margin-top: 12px; }
            .slider-scale.compact { margin-top: 4px; }
            .slider-labels { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 5px; color: #475569; font-size: 10px; }
            .slider-track { position: relative; display: flex; justify-content: space-between; gap: 4px; padding-top: 10px; border-top: 2px solid #64748b; }
            .slider-track span { position: relative; min-width: 18px; text-align: center; color: #475569; font-size: 9px; }
            .slider-track i { position: absolute; top: -13px; left: 50%; width: 1px; height: 8px; background: #64748b; content: ""; }
            .slider-track b { font-weight: 400; }
            .rating-items { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 8px; }
            .rating-items .rating-item { display: inline-flex; align-items: center; gap: 5px; }
            .rating-items b { color: #475569; font-weight: 600; }
            .rating-glyph {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 18px;
              height: 18px;
              font-size: 14px;
              line-height: 1;
              color: #64748b;
            }
            .rating-glyph-star { color: #ca8a04; }
            .rating-glyph-heart { color: #e11d48; }
            .rating-glyph-smile { color: #ca8a04; font-size: 16px; }
            .rating-glyph-thumb { color: #2563eb; font-size: 13px; }
            .matrix-slider-list { margin-top: 10px; display: grid; gap: 9px; }
            .matrix-slider-row { page-break-inside: avoid; }
            .matrix-slider-label { margin-bottom: 3px; font-weight: 600; color: #334155; }
            .ranking-list { margin: 10px 0 0; padding: 0; list-style: none; }
            .ranking-list li { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 6px; }
            .rank-box { display: inline-block; width: 28px; height: 18px; border: 1px solid #64748b; background: #fff; flex: 0 0 auto; }
            .multi-text-list { margin-top: 8px; display: grid; gap: 8px; }
            .multi-text-row { display: flex; align-items: flex-end; gap: 8px; }
            .multi-text-row span { color: #334155; }
            .multi-text-row i { flex: 1; border-bottom: 1px solid #cbd5e1; height: 16px; }
            .media-check { display: flex; align-items: center; gap: 8px; margin-top: 10px; color: #334155; }
            @media print {
              body { margin: 0; }
              .print-document > thead { display: table-header-group; }
              .print-document > tfoot { display: table-footer-group; }
              .print-page-header { padding-bottom: 7px; }
              .question-block { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <table class="print-document">
            <thead>
              <tr>
                <td class="print-page-header">
                  ${printHeaderBoxesHtml}
                  ${printLogoHtml}
                </td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <main class="print-shell density-${settings.density} boundary-${settings.boundaryStyle}">
                    <header class="header">
                      <div class="title print-rich-text">${printHeaderTitle || escapeHtml(stripHtmlText(survey.title))}</div>
                      ${printHeaderSubtitle ? `<div class="subtitle print-rich-text">${printHeaderSubtitle}</div>` : ''}
                      ${settings.showGeneratedAt ? `<div class="meta">Gerado em ${escapeHtml(generatedAt)} · ${orderedQuestions.length} perguntas</div>` : ''}
                    </header>
                    ${pageBlocks}
                  </main>
                </td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `
  }

  const buildIndexMap = (orderedQuestions: Question[]): Map<string, number> => {
    const map = new Map<string, number>()
    orderedQuestions.forEach((q, i) => map.set(q.id, i))
    return map
  }

  // Avalia se uma pergunta deve ser exibida com base nas suas displayConditions
  const evaluateDisplayConditions = (
    question: Question,
    allQuestions: Question[],
    answers: Record<string, any>
  ): boolean => {
    const conditions = question.displayConditions
    if (!conditions || conditions.length === 0) return true

    const logic = question.displayLogic || 'AND'

    const results = conditions.map((condition) => {
      const answer = answers[condition.questionId]

      switch (condition.operator) {
        case 'is_answered':
          return answer !== undefined && answer !== null && answer !== ''
        case 'not_answered':
          return answer === undefined || answer === null || answer === ''
        case 'equals':
          if (condition.optionId) {
            if (Array.isArray(answer)) return answer.includes(condition.optionId)
            return answer === condition.optionId
          }
          return String(answer) === String(condition.value)
        case 'not_equals':
          if (condition.optionId) {
            if (Array.isArray(answer)) return !answer.includes(condition.optionId)
            return answer !== condition.optionId
          }
          return String(answer) !== String(condition.value)
        case 'contains':
          if (condition.optionId) {
            if (Array.isArray(answer)) return answer.includes(condition.optionId)
            return false
          }
          return String(answer || '').includes(String(condition.value || ''))
        case 'greater_than':
          return Number(answer) > Number(condition.value)
        case 'less_than':
          return Number(answer) < Number(condition.value)
        default:
          return true
      }
    })

    if (logic === 'OR') return results.some(r => r)
    return results.every(r => r) // AND
  }

  // Avalia os conditionalBranches e retorna o indice-alvo na lista ORDENADA
  // Usa indexMap para lookup rapido e itera pela targetPage para achar a primeira pergunta visivel
  const evaluateConditionalBranches = (
    question: Question,
    orderedQuestions: Question[],
    indexMap: Map<string, number>,
    answers: Record<string, any>,
    pages: SurveyPage[]
  ): number | null => {
    const branches = question.conditionalBranches
    if (!branches || branches.length === 0) return null

    const answer = answers[question.id]
    // Se não ha resposta, não avaliar branches (exceto not_equals que precisa de resposta vazia)
    const hasAnswer = answer !== undefined && answer !== null && answer !== ''

    for (const branch of branches) {
      let matches = false

      switch (branch.condition) {
        case 'equals':
          if (!hasAnswer) { matches = false; break }
          if (Array.isArray(answer)) {
            matches = answer.includes(branch.optionId)
          } else {
            matches = String(answer) === String(branch.optionId)
          }
          break
        case 'not_equals':
          // not_equals so faz sentido se o usuário já respondeu algo diferente
          if (!hasAnswer) { matches = false; break }
          if (Array.isArray(answer)) {
            matches = !answer.includes(branch.optionId)
          } else {
            matches = String(answer) !== String(branch.optionId)
          }
          break
        case 'contains':
          if (!hasAnswer) { matches = false; break }
          if (Array.isArray(answer)) {
            matches = answer.includes(branch.optionId)
          } else if (typeof answer === 'string') {
            matches = answer.includes(branch.optionId)
          }
          break
      }

      if (matches && branch.targetPageId) {
        const targetPage = pages?.find(p => p.id === branch.targetPageId)
        if (targetPage && targetPage.questionIds?.length > 0) {
          // Iterar pelas perguntas da targetPage para achar a primeira visivel
          for (const qId of targetPage.questionIds) {
            const idx = indexMap.get(qId)
            if (idx !== undefined) {
              const targetQ = orderedQuestions[idx]
              if (targetQ && evaluateDisplayConditions(targetQ, orderedQuestions, answers)) {
                return idx
              }
            }
          }
          // Se nenhuma visivel na targetPage, retornar o indice da primeira mesmo
          const fallbackIdx = indexMap.get(targetPage.questionIds[0])
          if (fallbackIdx !== undefined) return fallbackIdx
        }
      }
    }

    return null
  }

  // Encontra o próximo indice com protecao anti-loop (max 50 saltos)
  // Sem restricao de direcao: branch pode ir para frente ou para tras
  const getNextQuestionIndex = (
    currentIndex: number,
    orderedQuestions: Question[],
    indexMap: Map<string, number>,
    answers: Record<string, any>,
    pages: SurveyPage[]
  ): number | null => {
    if (!orderedQuestions || orderedQuestions.length === 0) return null

    const currentQuestion = orderedQuestions[currentIndex]
    const visited = new Set<number>()
    visited.add(currentIndex)
    let maxJumps = 50

    // 1. Verificar desvio condicional
    if (currentQuestion) {
      const branchTarget = evaluateConditionalBranches(
        currentQuestion, orderedQuestions, indexMap, answers, pages
      )
      if (branchTarget !== null && branchTarget !== currentIndex && !visited.has(branchTarget)) {
        // Branch bateu: ir para o alvo (qualquer direcao)
        if (evaluateDisplayConditions(orderedQuestions[branchTarget], orderedQuestions, answers)) {
          return branchTarget
        }
        // Alvo não visivel: buscar próxima visivel a partir do alvo (para frente)
        visited.add(branchTarget)
        for (let i = branchTarget + 1; i < orderedQuestions.length && maxJumps > 0; i++) {
          maxJumps--
          if (!visited.has(i) && evaluateDisplayConditions(orderedQuestions[i], orderedQuestions, answers)) {
            return i
          }
          visited.add(i)
        }
        // Se nada visivel para frente do alvo, não ha próxima
        return null
      }
    }

    // 2. Sem desvio: próxima visivel na sequencia linear
    for (let i = currentIndex + 1; i < orderedQuestions.length; i++) {
      if (evaluateDisplayConditions(orderedQuestions[i], orderedQuestions, answers)) {
        return i
      }
    }

    return null
  }

  const getPreviousQuestionIndex = (
    currentIndex: number,
    orderedQuestions: Question[],
    answers: Record<string, any>
  ): number | null => {
    for (let i = currentIndex - 1; i >= 0; i--) {
      if (evaluateDisplayConditions(orderedQuestions[i], orderedQuestions, answers)) {
        return i
      }
    }
    return null
  }

  const isLastVisibleQuestion = (
    currentIndex: number,
    orderedQuestions: Question[],
    indexMap: Map<string, number>,
    answers: Record<string, any>,
    pages: SurveyPage[]
  ): boolean => {
    return getNextQuestionIndex(currentIndex, orderedQuestions, indexMap, answers, pages) === null
  }

  const isFirstVisibleQuestion = (
    currentIndex: number,
    orderedQuestions: Question[],
    answers: Record<string, any>
  ): boolean => {
    return getPreviousQuestionIndex(currentIndex, orderedQuestions, answers) === null
  }

  const getNextLocation = (
    currentIndex: number,
    orderedQuestions: Question[],
    indexMap: Map<string, number>,
    answers: Record<string, any>,
    pages: SurveyPage[]
  ) => getNextSurveyLocation(currentIndex, orderedQuestions, indexMap, answers, pages)

  const isCurrentStepFinal = (
    currentIndex: number,
    orderedQuestions: Question[],
    indexMap: Map<string, number>,
    answers: Record<string, any>,
    pages: SurveyPage[],
    terminalPage: SurveyPage | null
  ) => {
    if (terminalPage) return true

    const nextLocation = getNextLocation(currentIndex, orderedQuestions, indexMap, answers, pages)
    return nextLocation === null || nextLocation.kind === 'terminal'
  }

  const orderedQuestions = respondingSurvey ? buildOrderedQuestions(respondingSurvey) : []
  const indexMap = buildIndexMap(orderedQuestions)
  const currentQuestion = orderedQuestions[currentQuestionIndex]
  const currentQuestionContent = currentQuestion
    ? getResolvedQuestionContent(currentQuestion, orderedQuestions, surveyAnswers)
    : null
  const currentQuestionOptions = currentQuestion
    ? getFilteredQuestionOptions(currentQuestion, orderedQuestions, surveyAnswers)
    : []

  useEffect(() => {
    loadSurveys()
  }, [])

  const syncResearcherAssignmentStats = async (researcherId: string) => {
    try {
      const [assignmentsResponse, surveysResponse] = await Promise.all([
        lumi.entities.survey_assignments.list({
          filter: { researcherId },
          limit: 1000,
        }) as { list?: SurveyAssignment[] },
        lumi.entities.surveys.list({ limit: 1000 }) as { list?: Survey[] },
      ])

      const removedIds = new Set<string>()
      ;(surveysResponse.list || []).forEach((surveyItem) => {
        ;(surveyItem.removedAssignmentIds || []).forEach((id) => removedIds.add(id))
      })

      const assignments = (assignmentsResponse.list || []).filter(
        assignment => assignment.status !== 'removed' && !removedIds.has(assignment._id)
      )
      const assignedSurveys = assignments.length
      const completedSurveys = assignments.filter(
        assignment => assignment.status === 'completed'
      ).length

      await lumi.entities.researchers.update(researcherId, {
        assignedSurveys,
        completedSurveys,
        updatedAt: new Date().toISOString(),
      })
    } catch (error) {
      console.warn('Não foi possível sincronizar contadores do pesquisador:', error)
    }
  }

  const completeSurveyAssignment = async (surveyId: string, researcherId: string, completedAt: string) => {
    try {
      const assignmentsResponse = await lumi.entities.survey_assignments.list({
        filter: { surveyId, researcherId },
        limit: 100,
      }) as { list?: SurveyAssignment[] }

      const assignments = assignmentsResponse.list || []
      const assignmentToUpdate = assignments.find(
        assignment => assignment.status !== 'completed' && assignment.status !== 'removed'
      ) || assignments[0]

      if (!assignmentToUpdate?._id) {
        return
      }

      await lumi.entities.survey_assignments.update(assignmentToUpdate._id, {
        status: 'completed',
        completedAt,
        startedAt: assignmentToUpdate.startedAt || completedAt,
      })
    } catch (error) {
      console.warn('Não foi possível atualizar a atribuição da pesquisa:', error)
    }
  }

  const loadSurveys = async () => {
    try {
      setLoading(true)
      const response = await lumi.entities.surveys.list({
        sort: { createdAt: -1 },
        limit: 1000,
      })
      const visibleSurveys = (response.list || [])
        .map((survey) => normalizeSurveyRecord(survey as Survey))
        .filter(isSurveyVisible)

      setSurveys(visibleSurveys)
      setLoading(false)

      try {
        const responsesResponse = await lumi.entities.responses.list({
          limit: 1000,
        }) as { list?: SurveyResponseRecord[] }

        const visibleSurveyIds = new Set(visibleSurveys.map((survey) => survey._id))
        const counts = (responsesResponse.list || [])
          .filter((responseItem) => !responseItem.isDeleted && !responseItem.deletedAt)
          .reduce<Record<string, number>>((acc, responseItem) => {
          if (responseItem.surveyId && visibleSurveyIds.has(responseItem.surveyId)) {
            acc[responseItem.surveyId] = (acc[responseItem.surveyId] || 0) + 1
          }
          return acc
          }, {})

        setSurveyResponseCounts(counts)
      } catch (responseCountError) {
        console.warn('Não foi possível carregar a quantidade de respostas por pesquisa:', responseCountError)
        setSurveyResponseCounts({})
      }
    } catch (error) {
      console.error('Erro ao carregar pesquisas:', error)
      toast.error('Erro ao carregar pesquisas')
    } finally {
      setLoading(false)
    }
  }

  const syncSurveyLibraryMetadata = async (survey: Survey) => {
    try {
      const surveyCode = survey.surveyCode || buildSurveyCode(survey.title)
      const library = await syncSurveyLibrary({
        surveyId: survey._id,
        surveyCode,
        title: survey.title,
        description: survey.description,
        status: survey.status,
        languages: survey.languages,
        startDate: survey.startDate,
        endDate: survey.endDate,
        targetAudience: survey.targetAudience,
        tags: survey.tags || [],
        questions: survey.questions || [],
        pages: survey.pages || [],
        logoUrl: survey.logoUrl,
        logoDisplaySize: survey.logoDisplaySize,
        createdAt: survey.createdAt,
        updatedAt: survey.updatedAt
      })

      return {
        surveyCode,
        libraryFileName: library.fileName,
        libraryFileUrl: library.fileUrl,
        libraryVersion: library.version,
        librarySavedAt: library.savedAt
      }
    } catch (error) {
      console.warn('Pesquisa salva sem sincronizar a biblioteca tecnica em .txt:', error)
      return null
    }
  }

  const getSurveyRemovedAssignmentIds = async (surveyId: string, fallback?: string[]): Promise<string[]> => {
    try {
      const freshSurvey = await lumi.entities.surveys.get(surveyId) as Survey
      if (freshSurvey && Array.isArray(freshSurvey.removedAssignmentIds)) {
        return freshSurvey.removedAssignmentIds
      }
    } catch {
      // sem acesso a leitura atualizada; usa o valor conhecido
    }
    return Array.isArray(fallback) ? fallback : []
  }

  const handleAssignResearcher = async (survey: Survey, researcherId: string) => {
    try {
      const now = new Date().toISOString()
      const removedIds = new Set(await getSurveyRemovedAssignmentIds(survey._id, survey.removedAssignmentIds))
      const existingAssignmentsResponse = await lumi.entities.survey_assignments.list({
        filter: { surveyId: survey._id, researcherId },
        limit: 100,
      }) as { list?: SurveyAssignment[] }

      const existingAssignments = (existingAssignmentsResponse.list || []).filter(
        (assignment) => !removedIds.has(assignment._id)
      )
      const existingAssignment = existingAssignments.find((assignment) => (
        assignment.status === 'assigned' || assignment.status === 'in_progress'
      )) || existingAssignments[0]

      if (!existingAssignment) {
        await lumi.entities.survey_assignments.create({
          surveyId: survey._id,
          researcherId,
          status: 'assigned',
          assignedAt: now,
        })

        await syncResearcherAssignmentStats(researcherId)
        toast.success('Pesquisador atribuido com sucesso!')
        return
      }

      if (existingAssignment.status === 'completed' || existingAssignment.status === 'removed') {
        try {
          await lumi.entities.survey_assignments.update(existingAssignment._id, {
            status: 'assigned',
            assignedAt: now,
            startedAt: '',
            completedAt: '',
            removedAt: '',
            removedReason: '',
          })

          await syncResearcherAssignmentStats(researcherId)
          toast.success('Pesquisa reatribuida com sucesso!')
          return
        } catch (reassignError) {
          console.warn('Reatribuição por atualização bloqueada. Criando nova atribuição:', reassignError)
        }

        await lumi.entities.survey_assignments.create({
          surveyId: survey._id,
          researcherId,
          status: 'assigned',
          assignedAt: now,
        })

        await syncResearcherAssignmentStats(researcherId)
        toast.success('Pesquisa reatribuida com sucesso!')
        return
      }

      toast('Esse pesquisador já possui atribuição ativa para esta pesquisa.')
    } catch (error) {
      console.error('Erro ao atribuir pesquisador:', error)
      toast.error('Erro ao criar a atribuição da pesquisa.')
      throw error
    }
  }

  const handleUnassignResearcher = async (survey: Survey, assignmentId: string, researcherId: string) => {
    try {
      let handled = false

      try {
        await lumi.entities.survey_assignments.delete(assignmentId)
        handled = true
      } catch (deleteError) {
        console.warn('Exclusão da atribuição bloqueada. Tentando marcar como retirada:', deleteError)
      }

      if (!handled) {
        try {
          await lumi.entities.survey_assignments.update(assignmentId, {
            status: 'removed',
            removedAt: new Date().toISOString(),
            removedReason: 'Retirado pelo administrador',
          })
          handled = true
        } catch (updateError) {
          console.warn('Atualização da atribuição bloqueada. Registrando retirada na pesquisa:', updateError)
        }
      }

      if (!handled) {
        const currentRemovedIds = await getSurveyRemovedAssignmentIds(survey._id, survey.removedAssignmentIds)
        if (!currentRemovedIds.includes(assignmentId)) {
          await lumi.entities.surveys.update(survey._id, {
            removedAssignmentIds: [...currentRemovedIds, assignmentId],
          })
        }
      }

      await syncResearcherAssignmentStats(researcherId)
      toast.success('Pesquisador retirado da pesquisa.')
    } catch (error) {
      console.error('Erro ao retirar pesquisador da pesquisa:', error)
      toast.error('Erro ao retirar pesquisador da pesquisa.')
      throw error
    }
  }

  const handleOpenPublicLink = async (survey: Survey) => {
    if (survey.publicResponseEnabled && survey.publicResponseToken) {
      setShowPublicLinkModal(survey)
      return
    }

    const token = survey.publicResponseToken || generateClientResultsToken()
    try {
      await lumi.entities.surveys.update(survey._id, {
        publicResponseEnabled: true,
        publicResponseToken: token,
      })
      const updatedSurvey = {
        ...survey,
        publicResponseEnabled: true,
        publicResponseToken: token,
      }
      setSurveys((prev) => prev.map((s) => s._id === survey._id ? updatedSurvey : s))
      setShowPublicLinkModal(updatedSurvey)
      toast.success('Link público ativado!')
    } catch (error) {
      console.warn('Erro ao salvar ativação no banco, abrindo modal com token gerado:', error)
      setShowPublicLinkModal({
        ...survey,
        publicResponseEnabled: true,
        publicResponseToken: token,
      })
    }
  }

  const handleStartResponse = async (survey: Survey) => {
    const session = readSession()

    if (session?.profile !== 'researcher' || !session.researcherId) {
      toast.error('A coleta deve ser realizada com login de pesquisador vinculado.')
      return
    }

    setRespondingSurvey(survey)
    setSurveyAnswers({})
    setActiveTerminalPage(null)
    setCurrentQuestionIndex(0)
    setNavigationHistory([])
  }

  const handleAnswerChange = (questionId: string, value: any) => {
    setSurveyAnswers(prev => ({ ...prev, [questionId]: value }))
  }

  const handleNextQuestion = () => {
    if (!respondingSurvey || orderedQuestions.length === 0) return
    const nextLocation = getNextLocation(
      currentQuestionIndex,
      orderedQuestions,
      indexMap,
      surveyAnswers,
      respondingSurvey.pages || []
    )
    if (nextLocation?.kind === 'question') {
      setNavigationHistory((prev) => [...prev, currentQuestionIndex])
      setActiveTerminalPage(null)
      setCurrentQuestionIndex(nextLocation.index)
    }
    if (nextLocation?.kind === 'terminal') {
      setNavigationHistory((prev) => [...prev, currentQuestionIndex])
      setActiveTerminalPage(nextLocation.page)
    }
  }

  const handlePreviousQuestion = () => {
    if (!respondingSurvey || orderedQuestions.length === 0) return
    if (activeTerminalPage) {
      // Sair da pagina terminal: volta para o ultimo indice do historico
      setActiveTerminalPage(null)
      setNavigationHistory((prev) => {
        if (prev.length > 0) {
          const last = prev[prev.length - 1]
          setCurrentQuestionIndex(last)
          return prev.slice(0, -1)
        }
        return prev
      })
      return
    }
    // Voltar: desempilhar o historico de navegacao (respeita desvios)
    setNavigationHistory((prev) => {
      if (prev.length > 0) {
        const last = prev[prev.length - 1]
        setCurrentQuestionIndex(last)
        return prev.slice(0, -1)
      }
      // Historico vazio: fallback linear (primeiro acesso ou pesquisa sem desvios)
      const prevIndex = getPreviousQuestionIndex(
        currentQuestionIndex,
        orderedQuestions,
        surveyAnswers
      )
      if (prevIndex !== null) {
        setCurrentQuestionIndex(prevIndex)
      }
      return prev
    })
  }

  const handleSubmitResponse = async () => {
    if (!respondingSurvey) return

    if (isRecordingAudio) {
      toast.error('Finalize ou cancele a gravação de áudio antes de enviar.')
      return
    }

    if (isUploadingAudio) {
      toast.error('Aguarde o upload do áudio terminar antes de enviar.')
      return
    }

    const session = readSession()
    const researcherId = session?.profile === 'researcher' ? session.researcherId : null

    if (!researcherId) {
      toast.error('Faça login como pesquisador com cadastro vinculado para enviar respostas.')
      return
    }

    try {
      const submittedAt = new Date().toISOString()
      const cancelledByFilter = activeTerminalPage?.terminalOutcome === 'cancel_filter'
      const responseData = {
        // Campos esperados pela entidade responses
        surveyId: respondingSurvey._id,
        researcherId,
        language: 'pt',
        status: cancelledByFilter ? 'cancelled' : 'completed',
        cancellationReason: cancelledByFilter ? 'filter' : undefined,
        createdAt: submittedAt,
        updatedAt: submittedAt,
        completedAt: cancelledByFilter ? undefined : submittedAt,
        cancelledAt: cancelledByFilter ? submittedAt : undefined,

        // Campos adicionais usados no app
        surveyTitle: respondingSurvey.title,
        answers: Object.entries(surveyAnswers).map(([questionId, answer]) => {
          const question = orderedQuestions.find(q => q.id === questionId)
          return {
            questionId,
            questionText: question?.title || '',
            questionType: question?.type || '',
            answer
          }
        }),
        submittedAt,
        creator: 'user',

        audio_url: audioUploadedUrl || undefined,
        audio_nome: audioFileName || undefined,
        data_gravação: audioRecordedAt || undefined,
        duração_audio: audioDurationSec ?? undefined,
        transcricao_texto: undefined,
        audio_consent: audioConsent,
        audio_consentAt: audioConsentAt || undefined
      }

      await lumi.entities.responses.create(responseData)
      await completeSurveyAssignment(respondingSurvey._id, researcherId, submittedAt)
      await syncResearcherAssignmentStats(researcherId)

      toast.success('Resposta enviada com sucesso!')
      await resetAudioState(false)
      setRespondingSurvey(null)
      setSurveyAnswers({})
      setActiveTerminalPage(null)
      setCurrentQuestionIndex(0)
      setNavigationHistory([])
    } catch (error) {
      console.error('Erro ao salvar resposta:', error)
      toast.error('Erro ao enviar resposta. Tente novamente.')
    }
  }

  const handleCreateSurvey = async (surveyData: Partial<Survey>) => {
    try {

      const normalizedStructure = normalizeQuestionPageState(
        surveyData.questions || [],
        surveyData.pages || []
      )

      const normalizedSurveyData = {
        ...surveyData,
        questions: normalizedStructure.questions,
        pages: normalizedStructure.pages
      }

      if (!normalizedSurveyData.title?.trim()) {
        toast.error('O título da pesquisa é obrigatório')
        return
      }

      if (!normalizedSurveyData.questions || normalizedSurveyData.questions.length === 0) {
        toast.error('Adicione pelo menos uma pergunta a pesquisa')
        return
      }

      const surveyCode = buildSurveyCode(normalizedSurveyData.title)
      const now = new Date().toISOString()
      const newSurveyData = {
        title: normalizedSurveyData.title.trim(),
        description: normalizedSurveyData.description?.trim() || '',
        status: 'draft' as const,
        languages: normalizedSurveyData.languages || ['pt'],
        startDate: normalizedSurveyData.startDate || undefined,
        endDate: normalizedSurveyData.endDate || undefined,
        targetAudience: normalizedSurveyData.targetAudience?.trim() || '',
        clientId: normalizedSurveyData.clientId || undefined,
        clientName: normalizedSurveyData.clientName || undefined,
        tags: normalizedSurveyData.tags || [],
        questions: normalizedSurveyData.questions || [],
        pages: normalizedSurveyData.pages || [],
        logoUrl: normalizedSurveyData.logoUrl || undefined,
        logoDisplaySize: normalizedSurveyData.logoDisplaySize || 'medium',
        showSurveyTitle: normalizedSurveyData.showSurveyTitle !== false,
        showPageTitles: normalizedSurveyData.showPageTitles !== false,
        showLogoOnEveryPage: normalizedSurveyData.showLogoOnEveryPage !== false,
        audioRecordingEnabled: normalizedSurveyData.audioRecordingEnabled === true,
        clientResultsEnabled: normalizedSurveyData.clientResultsEnabled || false,
        clientResultsToken: normalizedSurveyData.clientResultsEnabled ? normalizedSurveyData.clientResultsToken : undefined,
        clientResultsPassword: normalizedSurveyData.clientResultsEnabled ? normalizedSurveyData.clientResultsPassword : undefined,
        clientResultsPasswordHash: normalizedSurveyData.clientResultsEnabled ? normalizedSurveyData.clientResultsPasswordHash : undefined,
        clientResultsPasswordSalt: normalizedSurveyData.clientResultsEnabled ? normalizedSurveyData.clientResultsPasswordSalt : undefined,
        clientResultsPasswordUpdatedAt: normalizedSurveyData.clientResultsEnabled ? normalizedSurveyData.clientResultsPasswordUpdatedAt : undefined,
        publicResponseEnabled: normalizedSurveyData.publicResponseEnabled || false,
        publicResponseToken: normalizedSurveyData.publicResponseEnabled ? normalizedSurveyData.publicResponseToken : undefined,
        surveyCode,
        createdBy: 'admin_001',
        createdAt: now,
        updatedAt: now
      }


      const createdSurvey = await lumi.entities.surveys.create(newSurveyData) as Survey

      let persistedSurvey = createdSurvey
      const libraryMetadata = await syncSurveyLibraryMetadata({
        ...createdSurvey,
        surveyCode,
        questions: normalizedSurveyData.questions || [],
        pages: normalizedSurveyData.pages || []
      })

      if (libraryMetadata) {
        persistedSurvey = await lumi.entities.surveys.update(createdSurvey._id, {
          ...libraryMetadata,
          updatedAt: libraryMetadata.librarySavedAt || now
        }) as Survey
      }

      setSurveys((prev) => {
        const updated = [normalizeSurveyRecord(persistedSurvey), ...prev]
        return updated
      })

      setShowCreateModal(false)
      toast.success(libraryMetadata ? 'Pesquisa criada com biblioteca tecnica sincronizada!' : 'Pesquisa criada com sucesso!')

      setTimeout(() => {
        loadSurveys()
      }, 500)
    } catch (error) {
      console.error('Erro ao criar pesquisa:', error)
      toast.error(`Erro ao criar pesquisa: ${error instanceof Error ? error.message : 'Erro desconhecido'}`)
    }
  }

  const handleUpdateSurvey = async (surveyId: string, updates: Partial<Survey>) => {
    try {

      const existingSurvey = surveys.find((survey) => survey._id === surveyId)

      if (!existingSurvey) {
        toast.error('Pesquisa não encontrada para atualização')
        return
      }

      const sourceQuestions = updates.questions ?? existingSurvey.questions ?? []
      const sourcePages = updates.pages ?? existingSurvey.pages ?? []
      const normalizedStructure = normalizeQuestionPageState(sourceQuestions, sourcePages)

      const now = new Date().toISOString()
      const surveyCode = existingSurvey.surveyCode || buildSurveyCode(updates.title || existingSurvey.title)
      const updatedSurvey = await lumi.entities.surveys.update(surveyId, {
        ...existingSurvey,
        ...updates,
        questions: normalizedStructure.questions,
        pages: normalizedStructure.pages,
        surveyCode,
        updatedAt: now
      }) as Survey


      let persistedSurvey = updatedSurvey
      const libraryMetadata = await syncSurveyLibraryMetadata({
        ...existingSurvey,
        ...updatedSurvey,
        surveyCode,
        questions: normalizedStructure.questions,
        pages: normalizedStructure.pages,
        updatedAt: now
      })

      if (libraryMetadata) {
        persistedSurvey = await lumi.entities.surveys.update(surveyId, {
          ...libraryMetadata,
          updatedAt: libraryMetadata.librarySavedAt || now
        }) as Survey
      }

      setSurveys((prev) => prev.map((survey) => (
        survey._id === surveyId ? normalizeSurveyRecord(persistedSurvey) : survey
      )))
      setEditingSurvey(null)
      toast.success(libraryMetadata ? 'Pesquisa atualizada e biblioteca tecnica sincronizada!' : 'Pesquisa atualizada com sucesso!')
    } catch (error) {
      console.error('Erro ao atualizar pesquisa:', error)
      toast.error(`Erro ao atualizar pesquisa: ${error instanceof Error ? error.message : 'Erro desconhecido'}`)
    }
  }

  const handleDuplicateSurvey = async (survey: Survey) => {
    try {
      const duplicatedSurvey = await lumi.entities.surveys.create({
        title: `${stripHtml(survey.title)} (Copia)`,
        description: survey.description,
        status: 'draft',
        languages: survey.languages,
        startDate: survey.startDate,
        endDate: survey.endDate,
        targetAudience: survey.targetAudience,
        clientId: survey.clientId,
        clientName: survey.clientName,
        tags: survey.tags,
        questions: survey.questions || [],
        pages: survey.pages || [],
        logoUrl: survey.logoUrl || undefined,
        logoDisplaySize: survey.logoDisplaySize || 'medium',
        showSurveyTitle: survey.showSurveyTitle !== false,
        showPageTitles: survey.showPageTitles !== false,
        showLogoOnEveryPage: survey.showLogoOnEveryPage !== false,
        audioRecordingEnabled: survey.audioRecordingEnabled === true,
        createdBy: 'admin_001',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      
      setSurveys(prev => [normalizeSurveyRecord(duplicatedSurvey), ...prev])
      toast.success('Pesquisa duplicada com sucesso!')
    } catch (error) {
      console.error('Erro ao duplicar pesquisa:', error)
      toast.error('Erro ao duplicar pesquisa')
    }
  }

  const handlePrintSurvey = (survey: Survey, settings: PrintSurveySettings = createDefaultPrintSettings(survey)) => {
    const printableHtml = buildPrintableSurveyHtmlV2(survey, settings)

    const attachPrintHandlers = (targetWindow: Window, cleanup?: () => void) => {
      let attempts = 0
      const maxAttempts = 30

      const triggerPrint = () => {
        attempts += 1

        try {
          if (targetWindow.document?.readyState !== 'complete' && attempts < maxAttempts) {
            window.setTimeout(triggerPrint, 200)
            return
          }

          targetWindow.focus()
          targetWindow.print()
        } catch (error) {
          if (attempts < maxAttempts) {
            window.setTimeout(triggerPrint, 200)
            return
          }

          console.error('Erro ao acionar impressao/PDF:', error)
          toast.error('Não foi possível abrir a visualização de impressao/PDF.')
          cleanup?.()
        }
      }

      const finalize = () => {
        targetWindow.removeEventListener('afterprint', finalize)
        cleanup?.()
      }

      targetWindow.addEventListener('afterprint', finalize)
      targetWindow.setTimeout(triggerPrint, 250)
    }

    try {
      const printableWindow = window.open('', '_blank', 'width=1024,height=768')

      if (printableWindow && !printableWindow.closed) {
        printableWindow.document.open()
        printableWindow.document.write(printableHtml)
        printableWindow.document.close()
        attachPrintHandlers(printableWindow)
        return
      }
    } catch (error) {
      console.warn('Falha ao abrir popup de impressao. Tentando fallback interno.', error)
    }

    try {
      const iframe = document.createElement('iframe')
      iframe.style.position = 'fixed'
      iframe.style.right = '0'
      iframe.style.bottom = '0'
      iframe.style.width = '0'
      iframe.style.height = '0'
      iframe.style.border = '0'
      iframe.setAttribute('aria-hidden', 'true')

      const cleanupIframe = () => {
        window.setTimeout(() => {
          iframe.remove()
        }, 500)
      }

      iframe.onload = () => {
        const frameWindow = iframe.contentWindow
        if (!frameWindow) {
          cleanupIframe()
          toast.error('Não foi possível abrir a visualização de impressao/PDF.')
          return
        }

        attachPrintHandlers(frameWindow, cleanupIframe)
      }

      document.body.appendChild(iframe)

      const frameDocument = iframe.contentDocument || iframe.contentWindow?.document
      if (!frameDocument) {
        cleanupIframe()
        toast.error('Não foi possível abrir a visualização de impressao/PDF.')
        return
      }

      frameDocument.open()
      frameDocument.write(printableHtml)
      frameDocument.close()
    } catch (error) {
      console.error('Erro ao preparar impressao/PDF:', error)
      toast.error('Não foi possível abrir a visualização de impressao/PDF.')
    }
  }

  const handleDeleteSurvey = async (surveyId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta pesquisa? Esta ação não pode ser desfeita.')) {
      return
    }

    try {
      const assignmentsResponse = await lumi.entities.survey_assignments.list({
        filter: { surveyId },
        limit: 5000,
      }).catch(() => ({ list: [] as Array<{ _id?: string }> })) as { list?: Array<{ _id?: string }> }

      const relatedAssignments = assignmentsResponse.list || []
      const assignmentDeleteResults = await Promise.allSettled(
        relatedAssignments
          .filter((assignment) => !!assignment._id)
          .map((assignment) => lumi.entities.survey_assignments.delete(assignment._id as string))
      )

      const failedAssignmentDeletes = assignmentDeleteResults.filter((result) => result.status === 'rejected')
      if (failedAssignmentDeletes.length > 0) {
        console.warn('Não foi possível excluir todas as atribuicoes da pesquisa:', failedAssignmentDeletes)
        const now = new Date().toISOString()
        await Promise.allSettled(
          relatedAssignments
            .filter((assignment) => !!assignment._id)
            .map((assignment) => lumi.entities.survey_assignments.update(assignment._id as string, {
              status: 'removed',
              removedAt: now,
              removedReason: 'Pesquisa excluida pelo administrador',
              updatedAt: now,
            }))
        )
      }

      let deleteMode: 'physical' | 'lógical' = 'physical'

      try {
        await lumi.entities.surveys.delete(surveyId)
      } catch (deleteError) {
        console.warn('Exclusão física bloqueada. Aplicando exclusão lógica:', deleteError)
        deleteMode = 'lógical'
        await lumi.entities.surveys.update(surveyId, {
          isDeleted: true,
          deletedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })
      }

      setSurveys(prev => prev.filter(s => s._id !== surveyId))
      toast.success(
        deleteMode === 'lógical'
          ? 'Pesquisa removida da listagem e arquivada.'
          : failedAssignmentDeletes.length > 0
          ? 'Pesquisa excluida. Algumas atribuicoes antigas não puderam ser removidas automaticamente.'
          : 'Pesquisa excluída com sucesso!'
      )
    } catch (error) {
      console.error('Erro ao excluir pesquisa:', error)
      toast.error('Erro ao excluir pesquisa')
    }
  }

  const handleStatusChange = async (surveyId: string, newStatus: Survey['status']) => {
    try {
      await handleUpdateSurvey(surveyId, { status: newStatus })
    } catch (error) {
      console.error('Erro ao alterar status:', error)
      toast.error('Erro ao alterar status')
    }
  }

  const cleanSurveyDisplayText = (value?: string) =>
    stripHtml(value || '')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/[\u0000-\u001F\u007F]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()

  const filteredSurveys = surveys.filter(survey => {
    const matchesSearch = cleanSurveyDisplayText(survey.title).toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cleanSurveyDisplayText(survey.description).toLowerCase().includes(searchTerm.toLowerCase()) ||
                         cleanSurveyDisplayText(survey.clientName).toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || survey.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const surveysTotalPages = Math.max(1, Math.ceil(filteredSurveys.length / 10))
  const surveysCurrentPage = Math.min(surveysPage, surveysTotalPages)
  const surveysPageItems = filteredSurveys.slice((surveysCurrentPage - 1) * 10, surveysCurrentPage * 10)

  const getStatusColor = (status: Survey['status']) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800'
      case 'active': return 'bg-green-100 text-green-800'
      case 'paused': return 'bg-yellow-100 text-yellow-800'
      case 'finished': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: Survey['status']) => {
    switch (status) {
      case 'draft': return 'Rascunho'
      case 'active': return 'Ativa'
      case 'paused': return 'Pausada'
      case 'finished': return 'Finalizada'
      default: return status
    }
  }

  const handleOpenCreateSurvey = () => {
    try {
      window.localStorage.removeItem('survey_form_draft:new')
    } catch {
      // ignore storage errors
    }
    setEditingSurvey(null)
    setShowCreateModal(true)
  }

  const StatusButton: React.FC<{ 
    survey: Survey, 
    newStatus: Survey['status'], 
    icon: React.ElementType,
    label: string,
    color: string 
  }> = ({ survey, newStatus, icon: Icon, label, color }) => (
    <button
      onClick={() => handleStatusChange(survey._id, newStatus)}
      disabled={survey.status === newStatus}
      className={`flex w-full items-center justify-center space-x-1 rounded px-2 py-2 text-sm font-medium transition-colors ${color} ${
        survey.status === newStatus ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'
      }`}
      title={label}
    >
      <Icon size={14} />
      <span>{label}</span>
    </button>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Gerenciar Pesquisas</h1>
        <button
          onClick={handleOpenCreateSurvey}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-colors"
        >
          <Plus size={20} />
          <span>Nova Pesquisa</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar pesquisas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos os Status</option>
              <option value="draft">Rascunhos</option>
              <option value="active">Ativas</option>
              <option value="paused">Pausadas</option>
              <option value="finished">Finalizadas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de pesquisas */}
      <div className="space-y-4">
        {filteredSurveys.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <FileText size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || statusFilter !== 'all' ? 'Nenhuma pesquisa encontrada' : 'Nenhuma pesquisa criada'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || statusFilter !== 'all' 
                ? 'Tente ajustar os filtros de busca'
                : 'Comece criando sua primeira pesquisa'
              }
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button
                onClick={handleOpenCreateSurvey}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Criar Primeira Pesquisa
              </button>
            )}
          </div>
        ) : (
          surveysPageItems.map((survey) => (
            <div key={survey._id} className="overflow-hidden rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-5">
                <div className="w-full min-w-0 overflow-hidden">
                  <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h3 className="min-w-0 whitespace-normal break-normal text-xl font-semibold text-gray-900 [overflow-wrap:normal] [word-break:normal]">
                        {cleanSurveyDisplayText(survey.title)}
                      </h3>
                      {survey.surveyCode && (
                        <p className="mt-1 text-xs font-medium uppercase tracking-[0.18em] text-blue-700">
                          Codigo: {survey.surveyCode}
                        </p>
                      )}
                    </div>
                    <span className={`shrink-0 self-start rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(survey.status)}`}>
                      {getStatusLabel(survey.status)}
                    </span>
                  </div>
                  
                  <p className="mb-3 whitespace-normal break-normal text-gray-600 [overflow-wrap:normal] [word-break:normal]">
                    {cleanSurveyDisplayText(survey.description)}
                  </p>
                  
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                    <div className="flex min-w-0 items-center gap-1">
                      <Calendar size={16} />
                      <span className="whitespace-normal break-words [overflow-wrap:anywhere]">
                        Criado em {new Date(survey.createdAt).toLocaleDateString('pt-BR')}
                      </span>
                    </div>

                    {survey.clientName && (
                      <div className="flex min-w-0 items-center gap-1">
                        <Building2 size={16} />
                        <span className="whitespace-normal break-words [overflow-wrap:anywhere]">
                          Cliente: {cleanSurveyDisplayText(survey.clientName)}
                        </span>
                      </div>
                    )}
                    
                    {survey.targetAudience && (
                      <div className="flex min-w-0 items-center gap-1">
                        <Users size={16} />
                        <span className="whitespace-normal break-words [overflow-wrap:anywhere]">
                          {cleanSurveyDisplayText(survey.targetAudience)}
                        </span>
                      </div>
                    )}
                    
                    <div className="flex min-w-0 items-center gap-1">
                      <span className="whitespace-normal break-words [overflow-wrap:anywhere]">
                        Idiomas: {survey.languages.join(', ').toUpperCase()}
                      </span>
                    </div>

                    {survey.librarySavedAt && (
                      <div className="flex min-w-0 items-center gap-1">
                        <FileText size={16} />
                        <span className="whitespace-normal break-words [overflow-wrap:anywhere]">
                          Biblioteca v{survey.libraryVersion || 1} em {new Date(survey.librarySavedAt).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    )}

                    {survey.questions && survey.questions.length > 0 && (
                      <div className="flex min-w-0 items-center gap-1">
                        <FileText size={16} />
                        <span className="whitespace-normal break-words [overflow-wrap:anywhere]">
                          {survey.questions.length} pergunta{survey.questions.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}

                    <div className="flex min-w-0 items-center gap-1">
                      <BarChart3 size={16} />
                      <span className="whitespace-normal break-words [overflow-wrap:anywhere]">
                        {(surveyResponseCounts[survey._id] || 0)} resposta{surveyResponseCounts[survey._id] === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                  
                  {survey.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {survey.tags.map((tag, index) => (
                        <span key={index} className="max-w-full whitespace-normal break-words rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800 [overflow-wrap:anywhere]">
                          {cleanSurveyDisplayText(tag)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex w-full flex-col gap-3 border-t border-gray-100 pt-4">
                  {/* Controles de status */}
                  <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-4">
                    <StatusButton
                      survey={survey}
                      newStatus="active"
                      icon={Play}
                      label="Ativar"
                      color="bg-green-600 text-white"
                    />
                    <StatusButton
                      survey={survey}
                      newStatus="draft"
                      icon={Pause}
                      label="Desativar"
                      color="bg-gray-700 text-white"
                    />
                    <StatusButton
                      survey={survey}
                      newStatus="paused"
                      icon={Pause}
                      label="Pausar"
                      color="bg-yellow-600 text-white"
                    />
                    <StatusButton
                      survey={survey}
                      newStatus="finished"
                      icon={Square}
                      label="Finalizar"
                      color="bg-red-600 text-white"
                    />
                  </div>

                  <div className="grid w-full grid-cols-2 gap-2 md:grid-cols-4 xl:grid-cols-8">
                    <button
                      onClick={() => setAssigningSurvey(survey)}
                      className="flex w-full items-center justify-center space-x-1 rounded-lg bg-indigo-600 px-3 py-2 text-white transition-colors hover:bg-indigo-700"
                    >
                      <Users size={16} />
                      <span>Atribuir Pesquisador</span>
                    </button>

                    <button
                      onClick={() => setEditingSurvey(survey)}
                      className="flex w-full items-center justify-center space-x-1 rounded-lg bg-blue-600 px-3 py-2 text-white transition-colors hover:bg-blue-700"
                    >
                      <Edit size={16} />
                      <span>Editar</span>
                    </button>

                    <button
                      onClick={() => setTestingSurvey(survey)}
                      className="flex w-full items-center justify-center space-x-1 rounded-lg bg-cyan-700 px-3 py-2 text-white transition-colors hover:bg-cyan-800"
                    >
                      <Eye size={16} />
                      <span>Testar Questionario</span>
                    </button>
                    
                    <button
                      onClick={() => handleDuplicateSurvey(survey)}
                      className="flex w-full items-center justify-center space-x-1 rounded-lg bg-gray-600 px-3 py-2 text-white transition-colors hover:bg-gray-700"
                    >
                      <Copy size={16} />
                      <span>Duplicar</span>
                    </button>
                    
                    <button
                        onClick={() => handleOpenPublicLink(survey)}
                        className="flex w-full items-center justify-center space-x-1 rounded-lg bg-emerald-600 px-3 py-2 text-white transition-colors hover:bg-emerald-700"
                        title="Copiar Link e QR Code de Resposta Pública"
                      >
                        <Share2 size={16} />
                        <span>Link Público</span>
                      </button>
                    
                    {isResearcherSession && (
                      <button
                        onClick={() => handleStartResponse(survey)}
                        className="flex w-full items-center justify-center space-x-1 rounded-lg bg-purple-600 px-3 py-2 text-white transition-colors hover:bg-purple-700"
                      >
                        <FileText size={16} />
                        <span>Responder</span>
                      </button>
                    )}
                    
                    <button
                      onClick={() => setResultsSurvey(survey)}
                      className="flex w-full items-center justify-center space-x-1 rounded-lg bg-green-600 px-3 py-2 text-white transition-colors hover:bg-green-700"
                    >
                      <BarChart3 size={16} />
                      <span>Resultados</span>
                    </button>

                    <button
                      onClick={() => setPrintConfigSurvey(survey)}
                      className="flex w-full items-center justify-center space-x-1 rounded-lg bg-slate-700 px-3 py-2 text-white transition-colors hover:bg-slate-800"
                    >
                      <Printer size={16} />
                      <span>Imprimir/PDF</span>
                    </button>
                    
                    <button
                      onClick={() => handleDeleteSurvey(survey._id)}
                      className="flex w-full items-center justify-center space-x-1 rounded-lg bg-red-600 px-3 py-2 text-white transition-colors hover:bg-red-700"
                    >
                      <Trash2 size={16} />
                      <span>Excluir</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        {filteredSurveys.length > 0 && (
          <div className="mt-4 flex flex-col items-center justify-between gap-3 border-t border-gray-100 pt-4 text-sm text-gray-600 sm:flex-row">
            <span>
              Pagina {surveysCurrentPage} de {surveysTotalPages} · {filteredSurveys.length} registro(s)
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={surveysCurrentPage <= 1}
                onClick={() => setSurveysPage(surveysCurrentPage - 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                type="button"
                disabled={surveysCurrentPage >= surveysTotalPages}
                onClick={() => setSurveysPage(surveysCurrentPage + 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Proxima
              </button>
            </div>
          </div>
        )}
      </div>



      {(showCreateModal || editingSurvey) && (
        <SurveyFormModal
          survey={editingSurvey}
          onSave={editingSurvey ? 
            (data) => handleUpdateSurvey(editingSurvey._id, data) : 
            handleCreateSurvey
          }
          onClose={() => {
            setShowCreateModal(false)
            setEditingSurvey(null)
          }}
          onPreviewQuestion={setPreviewQuestion}
          onShowQRCode={setShowPublicLinkModal}
        />
      )}

      {/* Modal de preview de pergunta */}
      {previewQuestion && (
        <QuestionPreviewModal
          question={previewQuestion}
          onClose={() => setPreviewQuestion(null)}
        />
      )}

      {/* Modal de Resultados */}
      {resultsSurvey && (
        <SurveyResultsModal
          survey={resultsSurvey}
          onClose={() => setResultsSurvey(null)}
        />
      )}

      {assigningSurvey && (
        <AssignResearcherModal
          survey={assigningSurvey}
          onAssign={async (researcherId) => {
            await handleAssignResearcher(assigningSurvey, researcherId)
          }}
          onUnassign={async (assignmentId, researcherId) => {
            await handleUnassignResearcher(assigningSurvey, assignmentId, researcherId)
          }}
          onClose={() => setAssigningSurvey(null)}
        />
      )}

      {printConfigSurvey && (
        <PrintSurveyConfigModal
          survey={printConfigSurvey}
          onClose={() => setPrintConfigSurvey(null)}
          onPrint={(settings) => {
            handlePrintSurvey(printConfigSurvey, settings)
            setPrintConfigSurvey(null)
          }}
        />
      )}

      {showPublicLinkModal && (
        <PublicLinkModal
          survey={showPublicLinkModal}
          onClose={() => setShowPublicLinkModal(null)}
        />
      )}

      {/* Modal de Responder Pesquisa */}
      {respondingSurvey && (() => {
        if (!session?.researcherId) return null

        return (
          <ResearcherSurveyModal
            survey={respondingSurvey}
            researcherId={session.researcherId}
            researcherName={session.researcherName || session.userName}
            onClose={() => {
              setRespondingSurvey(null)
              setSurveyAnswers({})
              setActiveTerminalPage(null)
              setCurrentQuestionIndex(0)
              setNavigationHistory([])
            }}
            onSubmitted={() => {
              setRespondingSurvey(null)
              setSurveyAnswers({})
              setActiveTerminalPage(null)
              setCurrentQuestionIndex(0)
              setNavigationHistory([])
              void loadSurveys()
            }}
          />
        )
      })()}

      {testingSurvey && (
        <ResearcherSurveyModal
          survey={testingSurvey}
          researcherId="teste-admin"
          researcherName="Teste do administrador"
          testMode
          onClose={() => setTestingSurvey(null)}
          onSubmitted={() => setTestingSurvey(null)}
        />
      )}
    </div>
  )
}

// Modal de Responder Pesquisa
// Modal de Resultados da Pesquisa

// Editor de respostas em formato objeto (matrizes etc.) usado na edicao de respostas.
// Componente de nivel de modulo para preservar o foco/cursor entre re-renderizacoes.
const JsonAnswerEditor: React.FC<{
  value: unknown
  onChange: (nextValue: unknown) => void
}> = ({ value, onChange }) => {
  const [text, setText] = useState(() => {
    try { return JSON.stringify(value, null, 2) } catch { return String(value ?? '') }
  })
  const [invalid, setInvalid] = useState(false)
  return (
    <div>
      <textarea
        value={text}
        onChange={(event) => {
          const raw = event.target.value
          setText(raw)
          try {
            onChange(JSON.parse(raw))
            setInvalid(false)
          } catch {
            setInvalid(true)
          }
        }}
        rows={Math.min(8, Math.max(3, text.split('\n').length))}
        className={`w-full rounded-lg border px-3 py-2 font-mono text-xs ${
          invalid ? 'border-red-400 bg-red-50 text-gray-700' : 'border-gray-300 bg-white text-gray-700'
        }`}
      />
      {invalid && <p className="mt-1 text-xs text-red-600">JSON inválido — corrija para salvar este campo.</p>}
    </div>
  )
}

const SurveyResultsModal: React.FC<{
  survey: Survey
  onClose: () => void
}> = ({ survey, onClose }) => {
  const [loading, setLoading] = useState(true)
  const [totalResponses, setTotalResponses] = useState(0)
  const [responses, setResponses] = useState<SurveyResponseRecord[]>([])
  const [questionStats, setQuestionStats] = useState<Array<{
    questionId: string
    questionTitle: string
    questionType: string
    options: Array<{
      optionId: string
      optionText: string
      count: number
      percentage: number
      observationPercentage?: number
      citationPercentage?: number
    }>
    textAnswers: string[]
    textAnalysis?: Array<{
      label: string
      normalized: string
      count: number
      percentage: number
    }>
    answeredObservations?: number
    totalCitations?: number
    average?: number
  }>>([])
  const [responseAudios, setResponseAudios] = useState<ResponseAudioItem[]>([])
  const [downloadingAudioId, setDownloadingAudioId] = useState<string | null>(null)
  const [importingResponses, setImportingResponses] = useState(false)
  const [selectedResponseIds, setSelectedResponseIds] = useState<string[]>([])
  const [deletingResponses, setDeletingResponses] = useState(false)
  const [detailResponse, setDetailResponse] = useState<SurveyResponseRecord | null>(null)
  const [chartPreferences, setChartPreferences] = useState<Record<string, {
    sort: 'survey' | 'asc' | 'desc'
    chart: ResultChartType
    base: PercentageBase
    colorMode: ChartColorMode
    singleColor: string
    colors: Record<string, string>
  }>>({})
  const [textTreatmentAliases, setTextTreatmentAliases] = useState<Record<string, Record<string, string>>>({})
  const [showExportConfig, setShowExportConfig] = useState(false)
  const [exportSearch, setExportSearch] = useState('')
  const [selectedExportQuestionIds, setSelectedExportQuestionIds] = useState<string[]>([])
  const [exportHeaderMode, setExportHeaderMode] = useState<'variable' | 'title' | 'both'>('both')
  const [resultsView, setResultsView] = useState<'respostas' | 'graficos'>('respostas')
  const [chartHighlightedResponseId, setChartHighlightedResponseId] = useState<string | null>(null)
  const [editingResponseId, setEditingResponseId] = useState<string | null>(null)
  const [editingAnswers, setEditingAnswers] = useState<SurveyResponseAnswerRecord[]>([])
  const [savingEdits, setSavingEdits] = useState(false)

  const responseIds = responses
    .map((response) => response._id)
    .filter((responseId): responseId is string => Boolean(responseId))
  const selectedResponseCount = selectedResponseIds.filter((responseId) => responseIds.includes(responseId)).length
  const allResponsesSelected = responseIds.length > 0 && selectedResponseCount === responseIds.length

  const toggleResponseSelection = (responseId: string) => {
    setSelectedResponseIds((previous) => (
      previous.includes(responseId)
        ? previous.filter((id) => id !== responseId)
        : [...previous, responseId]
    ))
  }

  const toggleAllResponses = () => {
    setSelectedResponseIds(allResponsesSelected ? [] : responseIds)
  }

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div')
    tmp.innerHTML = html || ''
    const plainText = tmp.innerText || tmp.textContent || ''
    return plainText
      .replace(/\(\s*C[^)]*pia\s*\)/gi, '(Copia)')
      .replace(/\s+/g, ' ')
      .trim()
  }

  const formatAudioDuration = (durationSec: number | null) => {
    if (durationSec === null || Number.isNaN(durationSec)) return null

    const minutes = Math.floor(durationSec / 60)
    const seconds = durationSec % 60
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  const shareAudio = async (item: ResponseAudioItem) => {
    const shareText = `Audio da resposta ${item.responseId}`
    const shareUrl = item.audioUrl
    const isAbortError = (error: unknown) => (error as Error)?.name === 'AbortError'

    if (navigator.share && shareUrl.startsWith('http')) {
      try {
        await navigator.share({
          title: item.audioName,
          text: shareText,
          url: shareUrl,
        })
        return
      } catch (error) {
        if (isAbortError(error)) return
        console.warn('Compartilhamento do link de áudio recusado, tentando fallback:', error)
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.success('Link do áudio copiado. Abra o WhatsApp e cole no grupo.')
    } catch (error) {
      console.error('Erro ao compartilhar áudio:', error)
      toast.error('Não foi possível compartilhar automaticamente. Use Baixar áudio e envie o arquivo pelo WhatsApp.')
    }
  }

  const triggerBrowserDownload = (url: string, fileName: string) => {
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    link.rel = 'noopener'
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()

    window.setTimeout(() => {
      document.body.removeChild(link)
      if (url.startsWith('blob:')) {
        window.URL.revokeObjectURL(url)
      }
    }, 1500)
  }

  const getAudioDownloadName = (item: ResponseAudioItem, blob?: Blob) => {
    const currentName = (item.audioName || '').trim()
    if (currentName && /\.[a-z0-9]{2,5}$/i.test(currentName)) return currentName

    const contentType = blob?.type || ''
    const extension =
      contentType.includes('ogg') ? 'ogg' :
      contentType.includes('mp4') ? 'mp4' :
      contentType.includes('mpeg') ? 'mp3' :
      'webm'

    return `${currentName || `audio_${item.responseId}`}.${extension}`
  }

  const downloadAudioFile = async (item: ResponseAudioItem) => {
    if (!item.audioUrl) {
      toast.error('Áudio indisponível para download.')
      return
    }

    try {
      setDownloadingAudioId(item.responseId)
      const fileName = getAudioDownloadName(item)
      await downloadAudioDirectly(item.audioUrl, fileName, {
        responseId: item.responseId,
        audioData: item.audioData,
      })
      toast.success('Download do áudio (.mp3) iniciado.')
    } catch (error) {
      console.error('Erro ao baixar áudio:', error)
      toast.error('Não foi possível baixar o áudio diretamente.')
    } finally {
      setDownloadingAudioId(null)
    }
  }

  const getChartPreference = (questionId: string) => (
    chartPreferences[questionId] || {
      sort: 'survey' as const,
      chart: 'bar_simple' as const,
      base: 'observation' as const,
      colorMode: 'palette' as const,
      singleColor: '#2563eb',
      colors: {},
    }
  )

  const updateChartPreference = (
    questionId: string,
    updates: Partial<{
      sort: 'survey' | 'asc' | 'desc'
      chart: ResultChartType
      base: PercentageBase
      colorMode: ChartColorMode
      singleColor: string
      colors: Record<string, string>
    }>
  ) => {
    setChartPreferences((previous) => ({
      ...previous,
      [questionId]: {
        ...getChartPreference(questionId),
        ...updates,
      },
    }))
  }

  const updateOptionChartColor = (questionId: string, optionId: string, color: string) => {
    const preference = getChartPreference(questionId)
    updateChartPreference(questionId, {
      colorMode: 'custom',
      colors: {
        ...preference.colors,
        [optionId]: color,
      },
    })
  }

  const getPercentageForBase = (
    option: { percentage: number; observationPercentage?: number; citationPercentage?: number },
    base: PercentageBase
  ) => (
    base === 'observation'
      ? option.observationPercentage ?? option.percentage
      : option.citationPercentage ?? option.percentage
  )

  const resolveStatOptions = (stat: {
    options: Array<{
      optionId: string
      optionText: string
      count: number
      percentage: number
      observationPercentage?: number
      citationPercentage?: number
    }>
  }, base: PercentageBase = 'observation') => stat.options.map((option) => ({
    ...option,
    percentage: getPercentageForBase(option, base),
  }))

  const getPercentageBaseLabel = (base: PercentageBase = 'observation') => (
    base === 'observation'
      ? 'Total por observação'
      : 'Total por citação'
  )

  const sortStatOptions = (
    options: Array<{ optionId: string; optionText: string; count: number; percentage: number }>,
    sortMode: 'survey' | 'asc' | 'desc'
  ) => {
    const sortedOptions = [...options]
    if (sortMode === 'asc') return sortedOptions.sort((left, right) => left.count - right.count)
    if (sortMode === 'desc') return sortedOptions.sort((left, right) => right.count - left.count)
    return sortedOptions
  }

  const chartColors = ['#2563eb', '#16a34a', '#f97316', '#9333ea', '#dc2626', '#0891b2', '#65a30d', '#be123c']

  const getChartColors = (
    questionId: string,
    options: Array<{ optionId: string }>
  ) => {
    const preference = getChartPreference(questionId)
    if (preference.colorMode === 'single') {
      return options.map(() => preference.singleColor || chartColors[0])
    }
    if (preference.colorMode === 'custom') {
      return options.map((option, index) => preference.colors?.[option.optionId] || chartColors[index % chartColors.length])
    }
    return options.map((_, index) => chartColors[index % chartColors.length])
  }

  const getChartColor = (colors: string[], index: number) =>
    colors[index % Math.max(colors.length, 1)] || chartColors[index % chartColors.length]

  const buildConicGradient = (options: Array<{ percentage: number }>, colors: string[] = chartColors) => {
    let current = 0
    const segments = options.map((option, index) => {
      const start = current
      current += option.percentage
      return `${getChartColor(colors, index)} ${start}% ${current}%`
    })
    return `conic-gradient(${segments.join(', ')})`
  }

  const getOptionPeak = (options: Array<{ count: number; percentage: number }>) =>
    Math.max(...options.map((option) => option.count), 1)

  const getChartPoints = (
    options: Array<{ count: number; percentage: number }>,
    width: number,
    height: number
  ) => {
    const peak = getOptionPeak(options)
    const safeCount = Math.max(options.length - 1, 1)

    return options.map((option, index) => {
      const x = options.length === 1 ? width / 2 : 18 + ((width - 36) * index) / safeCount
      const y = height - 18 - ((height - 36) * option.count) / peak
      return { x, y, option }
    })
  }

  const renderLegend = (options: Array<{ optionId: string; optionText: string; count: number; percentage: number }>, colors: string[] = chartColors) => (
    <div className="grid gap-2 sm:grid-cols-2">
      {options.map((opt, index) => (
        <div key={opt.optionId} className="flex min-w-0 items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 text-sm">
          <span className="flex min-w-0 items-center gap-2 text-gray-700">
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: getChartColor(colors, index) }}
            />
            <span className="min-w-0 truncate">{opt.optionText || opt.optionId}</span>
          </span>
          <span className="shrink-0 font-semibold text-gray-900">{opt.count} ({opt.percentage}%)</span>
        </div>
      ))}
    </div>
  )

  const renderBarsChart = (
    options: Array<{ optionId: string; optionText: string; count: number; percentage: number }>,
    chart: ResultChartType,
    colors: string[] = chartColors
  ) => {
    const peak = getOptionPeak(options)
    const isStacked = chart.includes('stacked')
    const isFull = chart.includes('100')
    const is3d = chart.endsWith('_3d')

    if (isStacked) {
      return (
        <div className={`rounded-xl bg-gray-50 p-4 ${is3d ? 'shadow-inner' : ''}`}>
          <div className={`flex h-14 overflow-hidden rounded-lg border border-white bg-white ${is3d ? 'shadow-lg' : ''}`}>
            {options.map((opt, index) => {
              const width = isFull ? opt.percentage : Math.max((opt.count / peak) * (100 / options.length), opt.count > 0 ? 4 : 0)
              return (
                <div
                  key={opt.optionId}
                  className="flex items-center justify-center text-xs font-semibold text-white"
                  style={{
                    width: `${width}%`,
                    backgroundColor: getChartColor(colors, index),
                    boxShadow: is3d ? 'inset -8px -8px 0 rgba(0,0,0,0.12)' : undefined,
                  }}
                  title={`${opt.optionText}: ${opt.count}`}
                >
                  {opt.percentage >= 8 ? `${opt.percentage}%` : ''}
                </div>
              )
            })}
          </div>
          <div className="mt-4">{renderLegend(options, colors)}</div>
        </div>
      )
    }

    return (
      <div className="space-y-3 rounded-xl bg-gray-50 p-4">
        {options.map((opt, index) => {
          const width = Math.max((opt.count / peak) * 100, opt.count > 0 ? 6 : 0)
          return (
            <div key={opt.optionId}>
              <div className="mb-1 flex items-center justify-between gap-3">
                <span className="min-w-0 flex-1 break-words text-sm text-gray-700 [overflow-wrap:anywhere]">{opt.optionText || opt.optionId}</span>
                <span className="shrink-0 text-sm font-semibold text-gray-900">{opt.count} ({opt.percentage}%)</span>
              </div>
              <div className="h-7 overflow-hidden rounded-full bg-white">
                <div
                  className={`flex h-full items-center justify-end rounded-full pr-2 text-xs font-bold text-white transition-all ${is3d ? 'shadow-md' : ''}`}
                  style={{
                    width: `${width}%`,
                    minWidth: opt.count > 0 ? '2rem' : '0',
                    backgroundColor: getChartColor(colors, index),
                    boxShadow: is3d ? 'inset -8px -8px 0 rgba(0,0,0,0.15)' : undefined,
                  }}
                >
                  {opt.percentage >= 10 ? `${opt.percentage}%` : ''}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderColumnsChart = (
    options: Array<{ optionId: string; optionText: string; count: number; percentage: number }>,
    chart: ResultChartType,
    colors: string[] = chartColors
  ) => {
    const peak = getOptionPeak(options)
    const isStacked = chart.includes('stacked')
    const isFull = chart.includes('100')
    const is3d = chart.endsWith('_3d')

    if (isStacked) {
      return (
        <div className="rounded-xl bg-gray-50 p-4">
          <div className="mx-auto flex h-64 max-w-32 flex-col-reverse overflow-hidden rounded-t-lg bg-white shadow-sm">
            {options.map((opt, index) => {
              const height = isFull ? opt.percentage : Math.max((opt.count / peak) * (100 / options.length), opt.count > 0 ? 4 : 0)
              return (
                <div
                  key={opt.optionId}
                  className="flex items-center justify-center text-xs font-semibold text-white"
                  style={{
                    height: `${height}%`,
                    backgroundColor: getChartColor(colors, index),
                    boxShadow: is3d ? 'inset -8px -8px 0 rgba(0,0,0,0.14)' : undefined,
                  }}
                  title={`${opt.optionText}: ${opt.count}`}
                />
              )
            })}
          </div>
          <div className="mt-4">{renderLegend(options, colors)}</div>
        </div>
      )
    }

    return (
      <div className="flex h-72 items-end gap-3 overflow-x-auto rounded-xl bg-gray-50 p-4">
        {options.map((opt, index) => {
          const height = Math.max((opt.count / peak) * 100, opt.count > 0 ? 6 : 0)
          return (
            <div key={opt.optionId} className="flex h-full min-w-16 flex-1 flex-col justify-end gap-2 text-center">
              <div className="flex flex-1 items-end justify-center">
                <div
                  className={`w-full max-w-16 rounded-t transition-all ${is3d ? 'shadow-lg' : ''}`}
                  style={{
                    height: `${height}%`,
                    backgroundColor: getChartColor(colors, index),
                    boxShadow: is3d ? 'inset -8px -8px 0 rgba(0,0,0,0.14)' : undefined,
                  }}
                  title={`${opt.count} respostas`}
                />
              </div>
              <span className="line-clamp-2 text-xs text-gray-600">{opt.optionText || opt.optionId}</span>
              <span className="text-xs font-semibold text-gray-900">{opt.percentage}%</span>
            </div>
          )
        })}
      </div>
    )
  }

  const renderLineChart = (
    options: Array<{ optionId: string; optionText: string; count: number; percentage: number }>,
    chart: ResultChartType,
    colors: string[] = chartColors
  ) => {
    const points = getChartPoints(options, 520, 220)
    const path = points.map((point) => `${point.x},${point.y}`).join(' ')
    const hasMarkers = chart.includes('markers')
    const is3d = chart.endsWith('_3d')

    return (
      <div className={`rounded-xl bg-gray-50 p-4 ${is3d ? 'shadow-inner' : ''}`}>
        <svg viewBox="0 0 520 220" className="h-64 w-full overflow-visible">
          <polyline points={path} fill="none" stroke={is3d ? '#1d4ed8' : '#2563eb'} strokeWidth={is3d ? 7 : 4} strokeLinejoin="round" strokeLinecap="round" opacity={is3d ? 0.35 : 1} transform={is3d ? 'translate(6 8)' : undefined} />
          <polyline points={path} fill="none" stroke="#2563eb" strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />
          {chart.includes('stacked') && (
            <polygon points={`18,202 ${path} 502,202`} fill="#93c5fd" opacity="0.28" />
          )}
          {(hasMarkers || is3d) && points.map((point, index) => (
            <circle key={index} cx={point.x} cy={point.y} r="6" fill={getChartColor(colors, index)} stroke="#fff" strokeWidth="2" />
          ))}
        </svg>
        {renderLegend(options, colors)}
      </div>
    )
  }

  const renderPieChart = (
    options: Array<{ optionId: string; optionText: string; count: number; percentage: number }>,
    chart: ResultChartType,
    colors: string[] = chartColors
  ) => {
    const isDonut = chart.includes('donut')
    const is3d = chart.endsWith('_3d')

    return (
      <div className="grid gap-4 rounded-xl bg-gray-50 p-4 md:grid-cols-[240px_1fr]">
        <div
          className={`mx-auto h-52 w-52 rounded-full ${is3d ? 'shadow-xl' : ''}`}
          style={{
            background: buildConicGradient(options, colors),
            transform: is3d ? 'perspective(600px) rotateX(16deg)' : undefined,
          }}
        >
          {isDonut && (
            <div className="flex h-full w-full items-center justify-center rounded-full p-12">
              <div className="h-full w-full rounded-full bg-gray-50" />
            </div>
          )}
        </div>
        {renderLegend(options, colors)}
      </div>
    )
  }

  const renderScatterChart = (
    options: Array<{ optionId: string; optionText: string; count: number; percentage: number }>,
    chart: ResultChartType,
    colors: string[] = chartColors
  ) => {
    const points = getChartPoints(options, 520, 220)
    const path = points.map((point) => `${point.x},${point.y}`).join(' ')

    return (
      <div className="rounded-xl bg-gray-50 p-4">
        <svg viewBox="0 0 520 220" className="h-64 w-full">
          {chart !== 'scatter_simple' && (
            <polyline points={path} fill="none" stroke="#2563eb" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
          )}
          {points.map((point, index) => (
            <circle key={index} cx={point.x} cy={point.y} r={chart === 'scatter_lines' ? 4 : 7} fill={getChartColor(colors, index)} />
          ))}
        </svg>
        {renderLegend(options, colors)}
      </div>
    )
  }

  const renderRadarChart = (
    options: Array<{ optionId: string; optionText: string; count: number; percentage: number }>,
    chart: ResultChartType,
    colors: string[] = chartColors
  ) => {
    const peak = getOptionPeak(options)
    const center = 130
    const radius = 92
    const points = options.map((opt, index) => {
      const angle = (Math.PI * 2 * index) / Math.max(options.length, 1) - Math.PI / 2
      const valueRadius = radius * (opt.count / peak)
      return {
        x: center + Math.cos(angle) * valueRadius,
        y: center + Math.sin(angle) * valueRadius,
        axisX: center + Math.cos(angle) * radius,
        axisY: center + Math.sin(angle) * radius,
      }
    })
    const polygon = points.map((point) => `${point.x},${point.y}`).join(' ')

    return (
      <div className="grid gap-4 rounded-xl bg-gray-50 p-4 md:grid-cols-[280px_1fr]">
        <svg viewBox="0 0 260 260" className="h-64 w-full">
          {[0.25, 0.5, 0.75, 1].map((level) => (
            <circle key={level} cx={center} cy={center} r={radius * level} fill="none" stroke="#d1d5db" strokeWidth="1" />
          ))}
          {points.map((point, index) => (
            <line key={index} x1={center} y1={center} x2={point.axisX} y2={point.axisY} stroke="#d1d5db" strokeWidth="1" />
          ))}
          <polygon points={polygon} fill={chart === 'radar_filled' ? '#60a5fa' : 'none'} stroke="#2563eb" strokeWidth="3" opacity={chart === 'radar_filled' ? 0.65 : 1} />
          {chart !== 'radar_simple' && points.map((point, index) => (
            <circle key={index} cx={point.x} cy={point.y} r="5" fill={getChartColor(colors, index)} />
          ))}
        </svg>
        {renderLegend(options, colors)}
      </div>
    )
  }

  const renderSpecialChart = (
    options: Array<{ optionId: string; optionText: string; count: number; percentage: number }>,
    chart: ResultChartType,
    colors: string[] = chartColors
  ) => {
    const peak = getOptionPeak(options)

    if (chart === 'map_choropleth') {
      return (
        <div className="grid gap-4 rounded-xl bg-gray-50 p-4 md:grid-cols-[1fr_280px]">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {options.map((opt, index) => (
              <div
                key={opt.optionId}
                className="min-h-24 rounded-2xl border border-white p-3 text-sm font-semibold text-white shadow-sm"
                style={{
                  backgroundColor: getChartColor(colors, index),
                  opacity: Math.max(opt.percentage / 100, opt.count > 0 ? 0.45 : 0.18),
                }}
              >
                <span className="block break-words [overflow-wrap:anywhere]">{opt.optionText || opt.optionId}</span>
                <span className="mt-2 block text-lg">{opt.percentage}%</span>
              </div>
            ))}
          </div>
          {renderLegend(options, colors)}
        </div>
      )
    }

    if (chart === 'waterfall_simple') {
      let accumulated = 0
      return (
        <div className="flex h-72 items-end gap-3 overflow-x-auto rounded-xl bg-gray-50 p-4">
          {options.map((opt, index) => {
            const previous = accumulated
            accumulated += opt.count
            const bottom = Math.min((previous / Math.max(accumulated, peak)) * 70, 70)
            const height = Math.max((opt.count / peak) * 80, opt.count > 0 ? 8 : 0)
            return (
              <div key={opt.optionId} className="flex h-full min-w-16 flex-1 flex-col justify-end gap-2 text-center">
                <div className="relative flex flex-1 items-end justify-center">
                  <div
                    className="absolute w-full max-w-16 rounded bg-blue-600"
                    style={{ height: `${height}%`, bottom: `${bottom}%`, backgroundColor: getChartColor(colors, index) }}
                  />
                </div>
                <span className="line-clamp-2 text-xs text-gray-600">{opt.optionText || opt.optionId}</span>
              </div>
            )
          })}
        </div>
      )
    }

    if (chart === 'funnel_simple') {
      const sorted = [...options].sort((left, right) => right.count - left.count)
      return (
        <div className="space-y-2 rounded-xl bg-gray-50 p-4">
          {sorted.map((opt, index) => (
            <div
              key={opt.optionId}
              className="mx-auto rounded-lg px-3 py-3 text-center text-sm font-semibold text-white"
              style={{
                width: `${Math.max((opt.count / peak) * 100, opt.count > 0 ? 28 : 12)}%`,
                backgroundColor: getChartColor(colors, index),
              }}
            >
              {opt.optionText || opt.optionId} - {opt.count}
            </div>
          ))}
        </div>
      )
    }

    return renderCombinationChart(options, chart, colors)
  }

  const renderCombinationChart = (
    options: Array<{ optionId: string; optionText: string; count: number; percentage: number }>,
    chart: ResultChartType,
    colors: string[] = chartColors
  ) => {
    const points = getChartPoints(options, 520, 220)
    const path = points.map((point) => `${point.x},${point.y}`).join(' ')
    const peak = getOptionPeak(options)

    return (
      <div className="rounded-xl bg-gray-50 p-4">
        <div className="relative h-72">
          <div className="absolute inset-0 flex items-end gap-3 px-4 pb-8">
            {options.map((opt, index) => (
              <div key={opt.optionId} className="flex h-full min-w-10 flex-1 items-end justify-center">
                <div
                  className="w-full max-w-14 rounded-t bg-blue-600"
                  style={{
                    height: `${Math.max((opt.count / peak) * 82, opt.count > 0 ? 6 : 0)}%`,
                    backgroundColor: getChartColor(colors, index),
                  }}
                />
              </div>
            ))}
          </div>
          <svg viewBox="0 0 520 220" className="absolute inset-0 h-full w-full">
            {(chart === 'combo_area_column' || chart === 'combo_custom') && (
              <polygon points={`18,202 ${path} 502,202`} fill="#bfdbfe" opacity="0.42" />
            )}
            <polyline points={path} fill="none" stroke={chart === 'combo_column_line_secondary' ? '#dc2626' : '#111827'} strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />
            {points.map((point, index) => (
              <circle key={index} cx={point.x} cy={point.y} r="5" fill={chart === 'combo_column_line_secondary' ? '#dc2626' : '#111827'} />
            ))}
          </svg>
        </div>
        {renderLegend(options, colors)}
      </div>
    )
  }

  const renderResultChart = (
    chart: ResultChartType,
    options: Array<{ optionId: string; optionText: string; count: number; percentage: number }>,
    colors: string[] = chartColors
  ) => {
    if (chart.startsWith('bar_')) return renderBarsChart(options, chart, colors)
    if (chart.startsWith('column_')) return renderColumnsChart(options, chart, colors)
    if (chart.startsWith('line_')) return renderLineChart(options, chart, colors)
    if (chart === 'pie' || chart === 'donut' || chart === 'pie_3d' || chart === 'donut_3d') return renderPieChart(options, chart, colors)
    if (chart.startsWith('scatter_')) return renderScatterChart(options, chart, colors)
    if (chart.startsWith('radar_')) return renderRadarChart(options, chart, colors)
    return renderSpecialChart(options, chart, colors)
  }

  const sanitizeFileName = (value: string) =>
    stripHtml(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s-]/g, ' ')
      .trim()
      .replace(/\s+/g, '_')
      .slice(0, 60) || 'resultados_pesquisa'

  const escapeHtml = (value: string) =>
    value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')

  const formatDateTime = (value?: string) => {
    if (!value) return ''

    const parsedDate = new Date(value)
    if (Number.isNaN(parsedDate.getTime())) return value
    return parsedDate.toLocaleString('pt-BR')
  }

  const getOtherLabelText = (question?: Question): string => {
    const custom = question?.otherLabel?.trim()
    return custom ? stripHtml(custom) : 'Outro'
  }

  const isOtherValue = (val: any): boolean => {
    if (typeof val !== 'string') return false
    const v = val.trim().toLowerCase()
    return v === '__other__' || v === '_other_' || v === 'other' || v === '__outro__' || v === '_outro_'
  }

  const resolveOptionValueLabel = (question: Question | undefined, val: any): string => {
    if (val === null || val === undefined || val === '') return ''
    const strVal = String(val)

    if (question?.options?.length) {
      const matchedOpt = question.options.find((opt) => opt.id === strVal)
      if (matchedOpt) return stripHtml(matchedOpt.text)
    }

    if (isOtherValue(strVal)) {
      return getOtherLabelText(question)
    }

    return strVal
  }

  const resolveMatrixValueLabel = (question: Question, value: any): string => {
    if (value === null || value === undefined || value === '') return ''
    const rawValue = String(value)
    const column = (question.matrixColumns || []).find((item) => (
      String(item.id) === rawValue || String(item.value ?? '') === rawValue
    ))
    return stripHtml(column?.label || rawValue)
  }

  const formatObjectAnswer = (value: Record<string, any>, question?: Question) =>
    Object.entries(value)
      .map(([key, item]) => `${key}: ${formatAnswerValue(item, question)}`)
      .join(' | ')

  const formatAnswerValue = (value: any, question?: Question): string => {
    if (value === null || value === undefined) return ''
    if (Array.isArray(value)) return value.map((item) => formatAnswerValue(item, question)).filter(Boolean).join(' | ')
    if (typeof value === 'object') return formatObjectAnswer(value, question)
    return resolveOptionValueLabel(question, value)
  }

  const getAnswerSelectionValues = (answer: any): string[] => {
    if (Array.isArray(answer)) return answer.map((item) => String(item)).filter(Boolean)
    if (answer && typeof answer === 'object' && Array.isArray(answer.values)) {
      return answer.values.map((item: any) => String(item)).filter(Boolean)
    }
    if (answer && typeof answer === 'object' && answer.value !== undefined) {
      return [String(answer.value)].filter(Boolean)
    }
    if (answer === null || answer === undefined || answer === '') return []
    return [String(answer)]
  }

  const getOtherAnswerText = (answer: any) => (
    answer && typeof answer === 'object' && typeof answer.otherText === 'string'
      ? answer.otherText.trim()
      : ''
  )

  const resolveAnswerLabel = (question: Question | undefined, value: any): string => {
    if (value === null || value === undefined || value === '') return ''

    if (Array.isArray(value)) {
      return value.map((item) => resolveAnswerLabel(question, item)).filter(Boolean).join(' | ')
    }

    if (typeof value === 'object') {
      const singleVal = value.value !== undefined ? value.value : null
      const multiVals = Array.isArray(value.values) ? value.values : null
      const otherText = typeof value.otherText === 'string' ? value.otherText.trim() : ''

      if (singleVal !== null || multiVals !== null) {
        const selectionVals = multiVals || [singleVal]
        const resolvedList = selectionVals
          .map((v: any) => resolveOptionValueLabel(question, v))
          .filter(Boolean)

        return resolvedList.join(' | ')
      }

      if (otherText && question?.allowOther) {
        return getOtherLabelText(question)
      }

      return formatObjectAnswer(value, question)
    }

    return resolveOptionValueLabel(question, value)
  }

  const resolveAnswerCells = (question: Question | undefined, answer: any): string[] => {
    if (answer === null || answer === undefined || answer === '') return ['']

    if (question && ['matrix', 'dropdown_matrix', 'rating_matrix', 'slider_matrix'].includes(question.type)) {
      const values = answer && typeof answer === 'object' ? answer : {}
      return (question.matrixRows || []).map((row) => {
        const value = values[row.id]
        return ['matrix', 'dropdown_matrix'].includes(question.type)
          ? resolveMatrixValueLabel(question, value)
          : String(value ?? '')
      })
    }

    if (question?.type === 'multitext') {
      const values = answer && typeof answer === 'object' ? answer : {}
      return (question.textBoxes || []).map((box) => String(values[box.id] ?? ''))
    }

    const isMultiple = question && ['multiple', 'checkbox', 'multipleChoice', 'ranking', 'classification'].includes(question.type)
    if (!isMultiple) return [resolveAnswerLabel(question, answer)]

    const otherLabel = getOtherLabelText(question)

    const cells = getAnswerSelectionValues(answer)
      .map((value) => {
        if (isOtherValue(value)) {
          return otherLabel
        }
        return resolveAnswerLabel(question, value)
      })
      .filter(Boolean)

    return cells.length > 0 ? cells : ['']
  }

  const openExportConfig = () => {
    const questions = survey.questions || []
    setSelectedExportQuestionIds(questions.map((question) => question.id))
    setExportSearch('')
    setExportHeaderMode('both')
    setShowExportConfig(true)
  }

  const handleExportExcel = async (
    selectedQuestionIds = (survey.questions || []).map((question) => question.id),
    headerMode: 'variable' | 'title' | 'both' = 'both'
  ) => {
    if (responses.length === 0) {
      toast.error('Não há respostas para exportar.')
      return
    }

    const questions = (survey.questions || []).filter((question) => selectedQuestionIds.includes(question.id))
    if (questions.length === 0) {
      toast.error('Selecione ao menos uma pergunta para exportar.')
      return
    }
    const answerItemsByResponse = responses.map((response) => {
      const answerItems = Array.isArray(response.answers) ? response.answers : []
      return new Map(
        (answerItems as SurveyResponseAnswerRecord[]).map((answerItem) => [answerItem.questionId, answerItem.answer])
      )
    })

    const questionColumnCounts = questions.map((question) => {
      if (['matrix', 'dropdown_matrix', 'rating_matrix', 'slider_matrix'].includes(question.type)) {
        return Math.max(1, (question.matrixRows || []).length)
      }
      if (question.type === 'multitext') return Math.max(1, (question.textBoxes || []).length)
      if (question.type === 'classification' && question.validation?.maxSelections) return question.validation.maxSelections
      if (!['multiple', 'checkbox', 'multipleChoice', 'ranking', 'classification'].includes(question.type)) return 1
      return Math.max(
        1,
        ...answerItemsByResponse.map((answersByQuestionId) => (
          resolveAnswerCells(question, answersByQuestionId.get(question.id)).filter(Boolean).length
        ))
      )
    })

    const getExportQuestionTitle = (question: Question) => {
      const variable = question.variable?.trim()
      const title = stripHtml(question.title || question.id)
      if (headerMode === 'variable') return variable || title
      if (headerMode === 'title') return title
      return variable ? `${variable} - ${title}` : title
    }

    const headerRow = [
      'ID Resposta',
      'ID Pesquisa',
      'Pesquisa',
      'Pesquisador',
      'ID Pesquisador',
      'Idioma',
      'Status',
      'Enviado em',
      'Tem áudio',
      'URL áudio',
      ...questions.flatMap((question, questionIndex) => {
        const title = getExportQuestionTitle(question)
        const columnCount = questionColumnCounts[questionIndex]
        const answerHeaders = ['matrix', 'dropdown_matrix', 'rating_matrix', 'slider_matrix'].includes(question.type)
          ? (question.matrixRows || []).map((row) => `${title} - ${stripHtml(row.label || row.id)}`)
          : question.type === 'multitext'
            ? (question.textBoxes || []).map((box) => `${title} - ${stripHtml(box.label || box.id)}`)
          : columnCount === 1
          ? [title]
          : Array.from({ length: columnCount }, (_, index) => `${title} - Posição ${index + 1}`)

        return question.allowOther
          ? [...answerHeaders, `${title} - Resposta Outro`]
          : answerHeaders
      }),
    ]

    const rows = responses.map((response, responseIndex) => {
      const answersByQuestionId = answerItemsByResponse[responseIndex]
      const answerCells = questions.flatMap((question, questionIndex) => {
        const columnCount = questionColumnCounts[questionIndex]
        const cells = resolveAnswerCells(question, answersByQuestionId.get(question.id))
        const normalAnswerCells = Array.from({ length: columnCount }, (_, index) => cells[index] || '')

        return question.allowOther
          ? [...normalAnswerCells, getOtherAnswerText(answersByQuestionId.get(question.id))]
          : normalAnswerCells
      })

      return [
        response._id || '',
        response.surveyId || survey._id,
        stripHtml(response.surveyTitle || survey.title || ''),
        response.researcherName || '',
        response.researcherId || '',
        response.language || '',
        response.status || '',
        formatDateTime(response.submittedAt || response.completedAt || response.createdAt),
        response.audio_url ? 'Sim' : 'Não',
        response.audio_url || '',
        ...answerCells,
      ]
    })

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
    const XLSX = await import('xlsx')
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...rows])
    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Respostas')
    XLSX.writeFile(
      workbook,
      `${sanitizeFileName(survey.title || 'resultados')}_${timestamp}.xlsx`,
      {
        bookType: 'xlsx',
        compression: true,
      }
    )

    toast.success('Exportação iniciada.')
  }

  const downloadExcelTable = (html: string, fileSuffix: string) => {
    const blob = new Blob(['\ufeff', html], {
      type: 'application/vnd.ms-excel;charset=utf-8;',
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')

    link.href = url
    link.download = `${sanitizeFileName(survey.title || 'resultados')}_${fileSuffix}_${timestamp}.xls`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const handleExportResultTables = () => {
    if (questionStats.length === 0) {
      toast.error('Não há resultados tabulados para exportar.')
      return
    }

    const sections = questionStats.map((stat, index) => {
      const preference = getChartPreference(stat.questionId)
      const options = resolveStatOptions(stat, preference.base)

      if (options.length > 0) {
        return `
          <h3>${escapeHtml(`Pergunta ${index + 1}: ${stat.questionTitle}`)}</h3>
          <p>${escapeHtml(getPercentageBaseLabel(preference.base))}</p>
          <table border="1">
            <thead>
              <tr>
                <th>Resposta</th>
                <th>Quantidade</th>
                <th>Percentual</th>
                <th>% por observação</th>
                <th>% por citação</th>
              </tr>
            </thead>
            <tbody>
              ${options.map((option) => `
                <tr>
                  <td>${escapeHtml(option.optionText || option.optionId)}</td>
                  <td>${option.count}</td>
                  <td>${option.percentage}%</td>
                  <td>${option.observationPercentage ?? option.percentage}%</td>
                  <td>${option.citationPercentage ?? option.percentage}%</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `
      }

      const mergedTextAnalysis = getMergedTextAnalysis(stat)
      if (mergedTextAnalysis.length) {
        return `
          <h3>${escapeHtml(`Pergunta ${index + 1}: ${stat.questionTitle}`)}</h3>
          <p>Tratamento de respostas espontâneas</p>
          <table border="1">
            <thead>
              <tr>
                <th>Resposta tratada</th>
                <th>Chave normalizada</th>
                <th>Quantidade</th>
                <th>Percentual</th>
              </tr>
            </thead>
            <tbody>
              ${mergedTextAnalysis.map((item) => `
                <tr>
                  <td>${escapeHtml(item.label)}</td>
                  <td>${escapeHtml(item.normalized)}</td>
                  <td>${item.count}</td>
                  <td>${item.percentage}%</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `
      }

      return `
        <h3>${escapeHtml(`Pergunta ${index + 1}: ${stat.questionTitle}`)}</h3>
        <table border="1">
          <tbody>
            <tr><td>Média</td><td>${stat.average ?? ''}</td></tr>
          </tbody>
        </table>
      `
    }).join('<br />')

    const tableHtml = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head><meta charset="utf-8" /></head>
        <body>
          <h2>${escapeHtml(stripHtml(survey.title || 'Resultados'))}</h2>
          ${sections}
        </body>
      </html>
    `

    downloadExcelTable(tableHtml, 'tabelas')
    toast.success('Tabela de resultados exportada.')
  }

  const copyTabulationFallback = async (
    title: string,
    options: Array<{ optionText: string; optionId: string; count: number; percentage: number }>
  ) => {
    const lines = [
      title,
      'Resposta\tQuantidade\tPercentual',
      ...options.map((option) => `${option.optionText || option.optionId}\t${option.count}\t${option.percentage}%`),
    ]
    await navigator.clipboard.writeText(lines.join('\n'))
  }

  const buildChartSvg = (
    title: string,
    options: Array<{ optionText: string; optionId: string; count: number; percentage: number }>,
    chart: ResultChartType,
    colors: string[],
    baseLabel: string
  ) => {
    const width = 960
    const top = 92
    const max = Math.max(...options.map((option) => option.count), 1)
    
    const isColumnChart = chart.startsWith('column_')
    const isPieChart = chart === 'pie' || chart === 'donut' || chart === 'pie_3d' || chart === 'donut_3d'
    const isLineChart = chart.startsWith('line_')
    const isScatterChart = chart.startsWith('scatter_')
    const isRadarChart = chart.startsWith('radar_')
    const isStackedBar = chart.startsWith('bar_') && chart.includes('stacked')

    if (isColumnChart) {
      const chartHeight = 360
      const height = 560
      const columnWidth = Math.max(28, Math.min(76, 640 / Math.max(options.length, 1)))
      const gap = Math.max(14, Math.min(34, 260 / Math.max(options.length, 1)))
      const totalWidth = options.length * (columnWidth + gap) - gap
      const startX = Math.max(40, (width - totalWidth) / 2)
      const baseY = top + chartHeight
      const columns = options.map((option, index) => {
        const heightValue = Math.max((option.count / max) * (chartHeight - 40), option.count > 0 ? 12 : 0)
        const x = startX + index * (columnWidth + gap)
        const y = baseY - heightValue
        const label = escapeHtml(option.optionText || option.optionId)

        return `
          <rect x="${x}" y="${y}" width="${columnWidth}" height="${heightValue}" rx="6" fill="${getChartColor(colors, index)}" />
          <text x="${x + columnWidth / 2}" y="${y - 10}" text-anchor="middle" font-family="Arial" font-size="16" font-weight="700" fill="#111827">${option.percentage}%</text>
          <text x="${x + columnWidth / 2}" y="${baseY + 28}" text-anchor="middle" font-family="Arial" font-size="14" fill="#374151">${label.slice(0, 24)}</text>
          <text x="${x + columnWidth / 2}" y="${baseY + 50}" text-anchor="middle" font-family="Arial" font-size="14" fill="#6b7280">${option.count}</text>
        `
      }).join('')

      return `
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
          <rect width="100%" height="100%" fill="#ffffff" />
          <text x="32" y="42" font-family="Arial" font-size="26" font-weight="700" fill="#111827">${escapeHtml(title)}</text>
          <text x="32" y="70" font-family="Arial" font-size="16" fill="#6b7280">${escapeHtml(baseLabel)}</text>
          <line x1="32" y1="${baseY}" x2="${width - 32}" y2="${baseY}" stroke="#d1d5db" />
          ${columns}
        </svg>
      `
    }

    if (isPieChart) {
      const height = 520
      const cx = 250
      const cy = 285
      const radius = 150
      let angle = -90
      const total = options.reduce((sum, option) => sum + option.count, 0) || 1
      const polar = (degrees: number) => {
        const radians = (Math.PI / 180) * degrees
        return { x: cx + radius * Math.cos(radians), y: cy + radius * Math.sin(radians) }
      }
      const slices = options.map((option, index) => {
        const sweep = (option.count / total) * 360
        const start = polar(angle)
        const end = polar(angle + sweep)
        const largeArc = sweep > 180 ? 1 : 0
        const path = `M ${cx} ${cy} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y} Z`
        angle += sweep
        return `<path d="${path}" fill="${getChartColor(colors, index)}" />`
      }).join('')
      const legend = options.map((option, index) => `
        <rect x="480" y="${150 + index * 34}" width="18" height="18" rx="4" fill="${getChartColor(colors, index)}" />
        <text x="510" y="${164 + index * 34}" font-family="Arial" font-size="16" fill="#111827">${escapeHtml(option.optionText || option.optionId)} - ${option.count} (${option.percentage}%)</text>
      `).join('')

      return `
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
          <rect width="100%" height="100%" fill="#ffffff" />
          <text x="32" y="42" font-family="Arial" font-size="26" font-weight="700" fill="#111827">${escapeHtml(title)}</text>
          <text x="32" y="70" font-family="Arial" font-size="16" fill="#6b7280">${escapeHtml(baseLabel)}</text>
          ${slices}
          ${chart.includes('donut') ? `<circle cx="${cx}" cy="${cy}" r="72" fill="#ffffff" />` : ''}
          ${legend}
        </svg>
      `
    }

    if (isLineChart || isScatterChart) {
      const height = 560
      const chartWidth = 800
      const chartHeight = 360
      const safeCount = Math.max(options.length - 1, 1)
      const points = options.map((option, index) => {
        const x = options.length === 1 ? chartWidth / 2 : 40 + ((chartWidth - 80) * index) / safeCount
        const y = chartHeight - 20 - ((chartHeight - 40) * (option.count / max))
        return { x: x + 40, y: y + top, count: option.count, percentage: option.percentage, label: option.optionText || option.optionId }
      })
      
      const pathString = points.map(p => `${p.x},${p.y}`).join(' ')
      const isLine = isLineChart
      
      const elements = points.map((p, i) => `
        ${!isLine ? `<circle cx="${p.x}" cy="${p.y}" r="8" fill="${getChartColor(colors, i)}" />` : `<circle cx="${p.x}" cy="${p.y}" r="6" fill="${getChartColor(colors, i)}" stroke="#fff" stroke-width="2" />`}
        <text x="${p.x}" y="${p.y - 15}" text-anchor="middle" font-family="Arial" font-size="14" font-weight="700" fill="#111827">${p.percentage}%</text>
        <text x="${p.x}" y="${top + chartHeight + 20}" text-anchor="middle" font-family="Arial" font-size="14" fill="#374151">${escapeHtml(p.label).slice(0, 20)}</text>
      `).join('')

      return `
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
          <rect width="100%" height="100%" fill="#ffffff" />
          <text x="32" y="42" font-family="Arial" font-size="26" font-weight="700" fill="#111827">${escapeHtml(title)}</text>
          <text x="32" y="70" font-family="Arial" font-size="16" fill="#6b7280">${escapeHtml(baseLabel)}</text>
          <line x1="40" y1="${top + chartHeight}" x2="${width - 40}" y2="${top + chartHeight}" stroke="#d1d5db" />
          ${isLine ? `<polyline points="${pathString}" fill="none" stroke="#2563eb" stroke-width="4" stroke-linejoin="round" stroke-linecap="round" />` : ''}
          ${elements}
        </svg>
      `
    }

    if (isRadarChart) {
      const height = 600
      const center = 480
      const cy = 340
      const radius = 180
      
      const points = options.map((opt, index) => {
        const angle = (Math.PI * 2 * index) / Math.max(options.length, 1) - Math.PI / 2
        const valueRadius = radius * (opt.count / max)
        return {
          x: center + Math.cos(angle) * valueRadius,
          y: cy + Math.sin(angle) * valueRadius,
          axisX: center + Math.cos(angle) * radius,
          axisY: cy + Math.sin(angle) * radius,
          labelX: center + Math.cos(angle) * (radius + 40),
          labelY: cy + Math.sin(angle) * (radius + 40),
          label: opt.optionText || opt.optionId,
          percentage: opt.percentage
        }
      })

      const polygonString = points.map(p => `${p.x},${p.y}`).join(' ')
      
      const axes = points.map(p => `<line x1="${center}" y1="${cy}" x2="${p.axisX}" y2="${p.axisY}" stroke="#d1d5db" stroke-width="1" />`).join('')
      const levels = [0.25, 0.5, 0.75, 1].map(level => `<circle cx="${center}" cy="${cy}" r="${radius * level}" fill="none" stroke="#d1d5db" stroke-width="1" />`).join('')
      const labels = points.map(p => `
        <text x="${p.labelX}" y="${p.labelY}" text-anchor="middle" font-family="Arial" font-size="14" fill="#374151">${escapeHtml(p.label).slice(0, 20)}</text>
        <text x="${p.labelX}" y="${p.labelY + 16}" text-anchor="middle" font-family="Arial" font-size="14" font-weight="700" fill="#111827">${p.percentage}%</text>
      `).join('')
      
      const dots = points.map((p, i) => `<circle cx="${p.x}" cy="${p.y}" r="6" fill="${getChartColor(colors, i)}" stroke="#fff" stroke-width="2" />`).join('')

      return `
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
          <rect width="100%" height="100%" fill="#ffffff" />
          <text x="32" y="42" font-family="Arial" font-size="26" font-weight="700" fill="#111827">${escapeHtml(title)}</text>
          <text x="32" y="70" font-family="Arial" font-size="16" fill="#6b7280">${escapeHtml(baseLabel)}</text>
          ${levels}
          ${axes}
          <polygon points="${polygonString}" fill="${chart === 'radar_filled' ? '#60a5fa' : 'none'}" stroke="#2563eb" stroke-width="3" fill-opacity="${chart === 'radar_filled' ? 0.65 : 0}" />
          ${dots}
          ${labels}
        </svg>
      `
    }

    if (isStackedBar) {
      const height = top + 160
      const totalPerc = options.reduce((sum, opt) => sum + opt.percentage, 0) || 100
      let currentX = 40
      
      const segments = options.map((opt, index) => {
        const segWidth = (opt.percentage / totalPerc) * (width - 80)
        const x = currentX
        currentX += segWidth
        return `
          <rect x="${x}" y="${top + 20}" width="${segWidth}" height="40" fill="${getChartColor(colors, index)}" />
          ${segWidth > 40 ? `<text x="${x + segWidth/2}" y="${top + 45}" text-anchor="middle" font-family="Arial" font-size="14" font-weight="700" fill="#ffffff">${opt.percentage}%</text>` : ''}
        `
      }).join('')
      
      const legend = options.map((opt, index) => {
        const lx = 40 + (index % 4) * 220
        const ly = top + 90 + Math.floor(index / 4) * 24
        return `
          <rect x="${lx}" y="${ly}" width="14" height="14" rx="2" fill="${getChartColor(colors, index)}" />
          <text x="${lx + 20}" y="${ly + 12}" font-family="Arial" font-size="14" fill="#374151">${escapeHtml(opt.optionText || opt.optionId).slice(0, 20)} (${opt.percentage}%)</text>
        `
      }).join('')

      return `
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height + Math.ceil(options.length / 4) * 24}" viewBox="0 0 ${width} ${height + Math.ceil(options.length / 4) * 24}">
          <rect width="100%" height="100%" fill="#ffffff" />
          <text x="32" y="42" font-family="Arial" font-size="26" font-weight="700" fill="#111827">${escapeHtml(title)}</text>
          <text x="32" y="70" font-family="Arial" font-size="16" fill="#6b7280">${escapeHtml(baseLabel)}</text>
          <rect x="40" y="${top + 20}" width="${width - 80}" height="40" rx="4" fill="#f3f4f6" />
          ${segments}
          ${legend}
        </svg>
      `
    }

    const barHeight = 42
    const gap = 16
    const height = top + Math.max(options.length, 1) * (barHeight + gap) + 40

    const rows = options.map((option, index) => {
      const y = top + index * (barHeight + gap)
      const barWidth = Math.max((option.count / max) * 520, option.count > 0 ? 16 : 0)
      const label = escapeHtml(option.optionText || option.optionId)

      return `
        <text x="32" y="${y + 27}" font-family="Arial" font-size="20" fill="#111827">${label}</text>
        <rect x="360" y="${y}" width="${barWidth}" height="${barHeight}" rx="6" fill="${getChartColor(colors, index)}" />
        <text x="${370 + barWidth}" y="${y + 27}" font-family="Arial" font-size="18" font-weight="700" fill="#111827">${option.count} (${option.percentage}%)</text>
      `
    }).join('')

    return `
      <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
        <rect width="100%" height="100%" fill="#ffffff" />
        <text x="32" y="42" font-family="Arial" font-size="26" font-weight="700" fill="#111827">${escapeHtml(title)}</text>
        <text x="32" y="70" font-family="Arial" font-size="16" fill="#6b7280">${escapeHtml(baseLabel)}</text>
        ${rows}
      </svg>
    `
  }

  const copyChartToClipboard = async (
    title: string,
    options: Array<{ optionText: string; optionId: string; count: number; percentage: number }>,
    chart: ResultChartType,
    colors: string[],
    baseLabel: string
  ) => {
    if (options.length === 0) return

    try {
      const svg = buildChartSvg(title, options, chart, colors, baseLabel)
      const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
      const svgUrl = window.URL.createObjectURL(svgBlob)
      const image = new Image()

      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve()
        image.onerror = () => reject(new Error('Falha ao gerar imagem do gráfico.'))
        image.src = svgUrl
      })

      const canvas = document.createElement('canvas')
      canvas.width = image.width
      canvas.height = image.height
      const context = canvas.getContext('2d')
      if (!context) throw new Error('Canvas indisponível.')
      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.drawImage(image, 0, 0)
      window.URL.revokeObjectURL(svgUrl)

      const pngBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
      const ClipboardItemCtor = (window as any).ClipboardItem
      if (!pngBlob || !ClipboardItemCtor) throw new Error('Clipboard de imagem indisponível.')

      await navigator.clipboard.write([
        new ClipboardItemCtor({ 'image/png': pngBlob }),
      ])
      toast.success('Gráfico copiado para colar no Excel, Word ou PowerPoint.')
    } catch (error) {
      console.warn('Fallback ao copiar gráfico como tabela:', error)
      await copyTabulationFallback(title, options)
      toast.success('Tabela do gráfico copiada.')
    }
  }

  const normalizeOpenTextCitation = (value: string) => stripHtml(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  const splitOpenTextCitations = (value: any) => String(value || '')
    .split(/[\n\r;,/]+/g)
    .map((item) => item.trim())
    .filter(Boolean)

  const buildTextAnalysis = (answersForQ: any[]) => {
    const citationMap = new Map<string, { label: string; normalized: string; count: number }>()

    answersForQ.forEach((answer) => {
      splitOpenTextCitations(answer).forEach((citation) => {
        const normalized = normalizeOpenTextCitation(citation)
        if (!normalized) return

        const current = citationMap.get(normalized)
        if (current) {
          current.count += 1
          return
        }

        citationMap.set(normalized, {
          label: stripHtml(citation),
          normalized,
          count: 1,
        })
      })
    })

    const total = Array.from(citationMap.values()).reduce((sum, item) => sum + item.count, 0)
    return Array.from(citationMap.values())
      .map((item) => ({
        ...item,
        percentage: total > 0 ? Math.round((item.count / total) * 100) : 0,
      }))
      .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label))
  }

  const getMergedTextAnalysis = (stat: {
    questionId: string
    textAnalysis?: Array<{ label: string; normalized: string; count: number; percentage: number }>
  }) => {
    const aliases = textTreatmentAliases[stat.questionId] || {}
    const sourceItems = stat.textAnalysis || []
    const merged = new Map<string, {
      label: string
      normalized: string
      count: number
      sourceLabels: string[]
    }>()

    sourceItems.forEach((item) => {
      // O alvo pode ser uma resposta existente OU um texto digitado livremente;
      // a unificacao agrupa pelo rotulo final (texto digitado tem prioridade).
      const typedTarget = (aliases[item.normalized] || '').trim()
      const targetLabel = typedTarget || item.label
      const groupKey = targetLabel.toLocaleLowerCase('pt-BR').replace(/\s+/g, ' ')

      const current = merged.get(groupKey)
      if (current) {
        current.count += item.count
        current.sourceLabels.push(item.label)
        return
      }

      merged.set(groupKey, {
        label: targetLabel,
        normalized: groupKey,
        count: item.count,
        sourceLabels: [item.label],
      })
    })

    const total = Array.from(merged.values()).reduce((sum, item) => sum + item.count, 0)
    return Array.from(merged.values())
      .map((item) => ({
        ...item,
        percentage: total > 0 ? Math.round((item.count / total) * 100) : 0,
      }))
      .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label))
  }

  const updateTextTreatmentAlias = (questionId: string, sourceNormalized: string, sourceLabel: string, targetLabel: string) => {
    setTextTreatmentAliases((previous) => {
      const current = { ...(previous[questionId] || {}) }
      const trimmed = (targetLabel || '').trim()
      if (!trimmed || trimmed === sourceLabel.trim()) {
        delete current[sourceNormalized]
      } else {
        current[sourceNormalized] = trimmed
      }
      return {
        ...previous,
        [questionId]: current,
      }
    })
  }

  const detectDelimiter = (content: string) => {
    const firstLine = content.split(/\r?\n/).find((line) => line.trim()) || ''
    const candidates = ['\t', ';', ',']

    const countDelimiter = (delimiter: string) => {
      let count = 0
      let quoted = false

      for (let index = 0; index < firstLine.length; index += 1) {
        const char = firstLine[index]
        const next = firstLine[index + 1]

        if (char === '"' && quoted && next === '"') {
          index += 1
        } else if (char === '"') {
          quoted = !quoted
        } else if (char === delimiter && !quoted) {
          count += 1
        }
      }

      return count
    }

    return candidates
      .map((delimiter) => ({ delimiter, count: countDelimiter(delimiter) }))
      .sort((left, right) => right.count - left.count)[0]?.delimiter || ';'
  }

  const parseDelimitedText = (content: string) => {
    const delimiter = detectDelimiter(content)
    const rows: string[][] = []
    let row: string[] = []
    let cell = ''
    let quoted = false

    for (let index = 0; index < content.length; index += 1) {
      const char = content[index]
      const next = content[index + 1]

      if (char === '"' && quoted && next === '"') {
        cell += '"'
        index += 1
      } else if (char === '"') {
        quoted = !quoted
      } else if (char === delimiter && !quoted) {
        row.push(cell.trim())
        cell = ''
      } else if ((char === '\n' || char === '\r') && !quoted) {
        if (char === '\r' && next === '\n') index += 1
        row.push(cell.trim())
        if (row.some(Boolean)) rows.push(row)
        row = []
        cell = ''
      } else {
        cell += char
      }
    }

    row.push(cell.trim())
    if (row.some(Boolean)) rows.push(row)
    return rows
  }

  const normalizeImportKey = (value: string) => stripHtml(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')

  const parseImportedAnswer = (rawValue: string, question: Question) => {
    const value = rawValue.trim()
    if (!value) return undefined

    const optionByKey = new Map((question.options || []).flatMap((option) => [
      [normalizeImportKey(option.id), option.id],
      [normalizeImportKey((option as any).value ?? ''), option.id],
      [normalizeImportKey(stripHtml(option.text)), option.id],
    ]))

    const parseOption = (item: string) => optionByKey.get(normalizeImportKey(item)) || item.trim()

    if (['multiple', 'checkbox', 'ranking', 'classification'].includes(question.type)) {
      return value.split(/[|;,]/g).map(parseOption).filter(Boolean)
    }

    if (['single', 'dropdown'].includes(question.type)) {
      return parseOption(value)
    }

    if (['rating', 'slider', 'number'].includes(question.type)) {
      const numericValue = Number(value.replace(',', '.'))
      return Number.isNaN(numericValue) ? value : numericValue
    }

    if (['matrix', 'dropdown_matrix', 'rating_matrix', 'slider_matrix', 'multitext'].includes(question.type)) {
      try {
        return JSON.parse(value)
      } catch {
        return value
      }
    }

    return value
  }

  const decodeDelimitedFile = async (file: File): Promise<string> => {
    const buffer = await file.arrayBuffer()
    try {
      return new TextDecoder('utf-8', { fatal: true }).decode(buffer)
    } catch {
      // CSV salvo pelo Excel em pt-BR costuma vir em ANSI/Windows-1252; ler como
      // UTF-8 transformava acentos e cedilha em caracteres especiais.
      return new TextDecoder('windows-1252').decode(buffer)
    }
  }

  const handleImportResponses = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    try {
      setImportingResponses(true)
      const content = await decodeDelimitedFile(file)
      const rows = parseDelimitedText(content)
      if (rows.length < 2) {
        toast.error('Arquivo sem linhas de resposta.')
        return
      }

      const headers = rows[0]
      const questions = survey.questions || []
      const questionByKey = new Map<string, Question>()
      questions.forEach((question, index) => {
        questionByKey.set(normalizeImportKey(question.id), question)
        questionByKey.set(normalizeImportKey(`Q${index + 1}`), question)
        questionByKey.set(normalizeImportKey(stripHtml(question.title)), question)
        if (question.variable?.trim()) {
          questionByKey.set(normalizeImportKey(question.variable), question)
          questionByKey.set(normalizeImportKey(`${question.variable} - ${stripHtml(question.title)}`), question)
        }
      })

      const mappedColumns = headers.map((header) => questionByKey.get(normalizeImportKey(header)) || null)
      const importedAt = new Date().toISOString()
      let importedCount = 0

      for (const row of rows.slice(1)) {
        const answers = row
          .map((cell, index) => {
            const question = mappedColumns[index]
            if (!question) return null
            const answer = parseImportedAnswer(cell, question)
            if (answer === undefined || answer === null || answer === '') return null
            return {
              questionId: question.id,
              questionText: question.title || '',
              questionType: question.type || '',
              answer
            }
          })
          .filter(Boolean)

        if (answers.length === 0) continue

        await lumi.entities.responses.create({
          surveyId: survey._id,
          surveyTitle: survey.title,
          language: 'pt',
          status: 'completed',
          answers,
          submittedAt: importedAt,
          completedAt: importedAt,
          createdAt: importedAt,
          updatedAt: importedAt,
          researcherName: 'Importação',
          creator: 'import_csv'
        })
        importedCount += 1
      }

      toast.success(`${importedCount} resposta(s) importada(s).`)
      await loadResults()
    } catch (error) {
      console.error('Erro ao importar respostas:', error)
      toast.error('Erro ao importar respostas.')
    } finally {
      setImportingResponses(false)
    }
  }

  useEffect(() => {
    loadResults()
  }, [])

  const loadResults = async () => {
    try {
      setLoading(true)
      const res = await lumi.entities.responses.list({
        filter: { surveyId: survey._id },
        limit: 1000
      })
      const allResponses = ((res.list || []) as SurveyResponseRecord[])
        .filter((response) => !response.isDeleted && !response.deletedAt)
      setResponses(allResponses)
      setTotalResponses(allResponses.length)
      setSelectedResponseIds((previous) => previous.filter((responseId) => allResponses.some((response) => response._id === responseId)))
      setResponseAudios(
        allResponses
          .filter((response: any) => !!response.audio_url)
          .map((response: any) => ({
            responseId: response._id,
            surveyId: response.surveyId || survey._id,
            researcherName: response.researcherName || '',
            submittedAt: response.submittedAt || response.completedAt || response.createdAt || '',
            audioUrl: response.audio_url,
            audioData: response.audio_data,
            audioName: response.audio_nome || `${response.surveyId || survey._id}_${response._id}.webm`,
            durationSec: typeof response.duração_audio === 'number' ? response.duração_audio : null,
          }))
      )

      if (allResponses.length === 0) {
        setResponses([])
        setResponseAudios([])
        setQuestionStats([])
        return
      }

      const questions = survey.questions || []
      const stats = questions.map((question) => {
        // Coletar todas as respostas para esta pergunta
        const answersForQ: any[] = []
        allResponses.forEach((response: any) => {
          const answerObj = (response.answers || []).find(
            (a: any) => a.questionId === question.id
          )
          if (answerObj !== undefined && answerObj.answer !== undefined && answerObj.answer !== null && answerObj.answer !== '') {
            answersForQ.push(answerObj.answer)
          }
        })

        const isChoice = ['single', 'multiple', 'checkbox', 'dropdown', 'singleChoice', 'multipleChoice'].includes(question.type)
        const isNumeric = ['rating', 'slider', 'number'].includes(question.type)

        if (isChoice && question.options && question.options.length > 0) {
          const countMap: Record<string, number> = {}
          question.options.forEach(opt => { countMap[opt.id] = 0 })
          if (question.allowOther) countMap[OTHER_OPTION_ID] = 0

          answersForQ.forEach((ans) => {
            getAnswerSelectionValues(ans).forEach((v) => {
              if (countMap[v] !== undefined) countMap[v]++
              else countMap[v] = 1
            })
          })

          const totalCitations = Object.values(countMap).reduce((a, b) => a + b, 0)
          const observationDenominator = allResponses.length

          const baseOptions = question.options.map(opt => ({
            optionId: opt.id,
            optionText: stripHtml(opt.text),
            count: countMap[opt.id] || 0,
          }))
          const optionsWithOther = question.allowOther
            ? [
              ...baseOptions,
              {
                optionId: OTHER_OPTION_ID,
                optionText: stripHtml(question.otherLabel || 'Outro'),
                count: countMap[OTHER_OPTION_ID] || 0,
              },
            ]
            : baseOptions
          const options = optionsWithOther.map((option) => ({
            ...option,
            percentage: observationDenominator > 0 ? Math.round((option.count / observationDenominator) * 100) : 0,
            observationPercentage: observationDenominator > 0 ? Math.round((option.count / observationDenominator) * 100) : 0,
            citationPercentage: totalCitations > 0 ? Math.round((option.count / totalCitations) * 100) : 0,
          }))

          return {
            questionId: question.id,
            questionTitle: stripHtml(question.title),
            questionType: question.type,
            options,
            textAnswers: [],
            answeredObservations: answersForQ.length,
            totalCitations,
          }
        } else if (isNumeric) {
          const nums = answersForQ.map(a => Number(a)).filter(n => !isNaN(n))
          const avg = nums.length > 0 ? Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10 : 0
          return {
            questionId: question.id,
            questionTitle: stripHtml(question.title),
            questionType: question.type,
            options: [],
            textAnswers: [],
            average: avg
          }
        } else {
          const textAnalysis = buildTextAnalysis(answersForQ)
          return {
            questionId: question.id,
            questionTitle: stripHtml(question.title),
            questionType: question.type,
            options: [],
            textAnswers: answersForQ.map(a => String(a)).slice(0, 20),
            textAnalysis,
            answeredObservations: answersForQ.length,
            totalCitations: textAnalysis.reduce((sum, item) => sum + item.count, 0),
          }
        }
      })

      setQuestionStats(stats)
    } catch (error) {
      console.error('Erro ao carregar resultados:', error)
      toast.error('Erro ao carregar resultados')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteResponses = async (responseIdsToDelete: string[]) => {
    const idsToDelete = Array.from(new Set(responseIdsToDelete.filter(Boolean)))
    if (idsToDelete.length === 0) {
      toast.error('Selecione pelo menos uma resposta para excluir.')
      return
    }

    const deletingAll = idsToDelete.length === responseIds.length && responseIds.length > 0
    const confirmed = window.confirm(
      deletingAll
        ? `Excluir todas as ${idsToDelete.length} respostas desta pesquisa? Esta ação não pode ser desfeita.`
        : `Excluir ${idsToDelete.length} resposta(s) selecionada(s)? Esta ação não pode ser desfeita.`
    )
    if (!confirmed) return

    try {
      setDeletingResponses(true)
      const responseById = new Map(responses.map((response) => [response._id, response]))
      const deletedIds: string[] = []
      let logicalDeletedCount = 0

      for (const responseId of idsToDelete) {
        const response = responseById.get(responseId)
        let deleted = false

        try {
          // A exclusão individual é mais compatível com as permissões da entidade
          // e evita falhas do endpoint de exclusão em lote.
          await lumi.entities.responses.delete(responseId)
          deleted = true
        } catch (deleteError) {
          console.warn('Exclusão física da resposta bloqueada. Aplicando remoção lógica:', deleteError)

          try {
            await lumi.entities.responses.update(responseId, {
              isDeleted: true,
              deletedAt: new Date().toISOString(),
              deletedBy: 'admin',
              updatedAt: new Date().toISOString(),
            })
            deleted = true
            logicalDeletedCount += 1
          } catch (updateError) {
            console.error('Não foi possível remover a resposta:', updateError)
          }
        }

        if (deleted) {
          deletedIds.push(responseId)
          if (response?.audio_url) {
            try {
              await lumi.tools.file.delete([response.audio_url])
            } catch (audioError) {
              console.warn('A resposta foi removida, mas o áudio não pôde ser excluído:', audioError)
            }
          }
        }
      }

      const failedCount = idsToDelete.length - deletedIds.length
      setSelectedResponseIds((previous) => previous.filter((responseId) => !deletedIds.includes(responseId)))

      if (deletedIds.length > 0) {
        await loadResults()
        const actionLabel = logicalDeletedCount > 0 ? 'removida(s)' : 'excluída(s)'
        toast.success(`${deletedIds.length} resposta(s) ${actionLabel}.`)
      }
      if (failedCount > 0) {
        toast.error(`Não foi possível excluir ${failedCount} resposta(s).`)
      }
    } catch (error) {
      console.error('Erro ao excluir respostas:', error)
      toast.error('Erro ao excluir respostas.')
    } finally {
      setDeletingResponses(false)
    }
  }

  const startEditingResponse = (response: SurveyResponseRecord) => {
    setEditingResponseId(response._id)
    setEditingAnswers(
      (Array.isArray(response.answers) ? response.answers : []).map((item) => ({ ...item }))
    )
  }

  const cancelEditingResponse = () => {
    setEditingResponseId(null)
    setEditingAnswers([])
  }

  const updateEditingAnswerValue = (index: number, newValue: unknown) => {
    setEditingAnswers((previous) => previous.map((item, i) => (
      i === index ? { ...item, answer: newValue as SurveyResponseAnswerRecord['answer'] } : item
    )))
  }

  const handleSaveEditedResponse = async () => {
    if (!editingResponseId) return
    try {
      setSavingEdits(true)
      const cleanedAnswers = editingAnswers.map((item) => {
        if (Array.isArray(item.answer)) {
          return { ...item, answer: item.answer.map((v) => String(v).trim()).filter(Boolean) }
        }
        return item
      })
      await lumi.entities.responses.update(editingResponseId, {
        answers: cleanedAnswers,
        updatedAt: new Date().toISOString(),
      })
      toast.success('Respostas atualizadas com sucesso!')
      setEditingResponseId(null)
      setEditingAnswers([])
      await loadResults()
    } catch (error) {
      console.error('Erro ao salvar respostas editadas:', error)
      toast.error('Não foi possível salvar as respostas editadas.')
    } finally {
      setSavingEdits(false)
    }
  }

  const renderRespondentPanel = (response: SurveyResponseRecord) => {
    const responseDate = response.submittedAt || response.completedAt || response.createdAt
    const responseIndex = responses.findIndex((r) => r._id === response._id)
    const detailAnswers: SurveyResponseAnswerRecord[] = Array.isArray(response.answers) ? response.answers : []
    const respondentAudio = responseAudios.find((item) => item.responseId === response._id)
    const isSelectedDetail = detailResponse?._id === response._id
    const isEditingThis = editingResponseId === response._id
    return (
      <div className="overflow-hidden rounded-xl border border-blue-300">
        <div className="flex items-center justify-between gap-3 border-b border-blue-200 bg-blue-50 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-blue-900">
              Respostas do Entrevistado {responseIndex + 1}
            </p>
            <p className="mt-0.5 text-xs text-blue-700">
              {response.researcherName || 'Sem pesquisador'}
              {responseDate ? ` · ${new Date(responseDate).toLocaleString('pt-BR')}` : ''}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isEditingThis ? (
              <>
                <button
                  type="button"
                  onClick={handleSaveEditedResponse}
                  disabled={savingEdits}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <CheckCircle size={14} />
                  {savingEdits ? 'Salvando...' : 'Salvar'}
                </button>
                <button
                  type="button"
                  onClick={cancelEditingResponse}
                  disabled={savingEdits}
                  className="rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancelar
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => startEditingResponse(response)}
                disabled={savingEdits || deletingResponses || !!editingResponseId}
                className="inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                title={editingResponseId && editingResponseId !== response._id ? 'Finalize ou cancele a edição em andamento' : 'Editar as respostas deste entrevistado'}
              >
                <Edit size={14} />
                Editar respostas
              </button>
            )}
            {isSelectedDetail && (
              <button
                type="button"
                onClick={() => setDetailResponse(null)}
                className="rounded-lg p-1 text-blue-400 transition-colors hover:bg-blue-100 hover:text-blue-700"
                title="Fechar"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>
        <div className="divide-y divide-blue-100 bg-white">
          {respondentAudio && (
            <div className="space-y-3 border-b border-blue-100 bg-blue-50/60 px-5 py-4">
              <div className="flex flex-col gap-1 text-sm text-gray-700">
                <p className="text-sm font-semibold text-blue-900">Gravação de áudio deste entrevistado</p>
                {respondentAudio.submittedAt && (
                  <p className="text-xs text-blue-700">
                    Enviada em {new Date(respondentAudio.submittedAt).toLocaleString('pt-BR')}
                  </p>
                )}
                {formatAudioDuration(respondentAudio.durationSec) && (
                  <p className="text-xs text-blue-700">Duração: {formatAudioDuration(respondentAudio.durationSec)}</p>
                )}
              </div>

              <audio controls preload="metadata" src={respondentAudio.audioUrl} className="w-full" />

              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => downloadAudioFile(respondentAudio)}
                  disabled={downloadingAudioId === respondentAudio.responseId}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {downloadingAudioId === respondentAudio.responseId ? (
                    <Loader2 size={15} className="animate-spin" />
                  ) : (
                    <Download size={15} />
                  )}
                  {downloadingAudioId === respondentAudio.responseId ? 'Baixando...' : 'Baixar áudio'}
                </button>
                <button
                  type="button"
                  onClick={() => shareAudio(respondentAudio)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50"
                >
                  <Share2 size={15} />
                  Compartilhar áudio
                </button>
              </div>
            </div>
          )}
          {detailAnswers.length === 0 ? (
            <p className="px-5 py-4 text-sm text-gray-400">Nenhuma resposta registrada para este entrevistado.</p>
          ) : (
            detailAnswers.map((answerItem, ai) => {
              const matchedQuestion = survey.questions?.find((q) => q.id === answerItem.questionId)
              const questionLabel = answerItem.questionTitle || answerItem.questionText
                || matchedQuestion?.title || matchedQuestion?.text || `Pergunta ${ai + 1}`
              const formattedAnswer = formatAnswerValue(answerItem.answer, matchedQuestion)

              if (isEditingThis) {
                const draftAnswer = editingAnswers[ai]?.answer
                return (
                  <div key={answerItem.questionId || ai} className="px-5 py-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Pergunta {ai + 1}</p>
                    <p
                      className="mt-0.5 text-sm font-medium text-gray-800"
                      dangerouslySetInnerHTML={{ __html: questionLabel }}
                    />
                    {Array.isArray(draftAnswer) ? (
                      <textarea
                        value={draftAnswer.join('\n')}
                        onChange={(event) => updateEditingAnswerValue(ai, event.target.value.split(/\r?\n/))}
                        rows={Math.min(8, Math.max(2, draftAnswer.length))}
                        placeholder="Uma resposta por linha"
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"
                      />
                    ) : draftAnswer !== null && typeof draftAnswer === 'object' ? (
                      <div className="mt-1">
                        <JsonAnswerEditor
                          value={draftAnswer}
                          onChange={(nextValue) => updateEditingAnswerValue(ai, nextValue)}
                        />
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={draftAnswer ?? ''}
                        onChange={(event) => {
                          const raw = event.target.value
                          const asNumber = typeof answerItem.answer === 'number' && raw.trim() !== '' && !Number.isNaN(Number(raw.replace(',', '.')))
                          updateEditingAnswerValue(ai, asNumber ? Number(raw.replace(',', '.')) : raw)
                        }}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700"
                      />
                    )}
                  </div>
                )
              }

              return (
                <div key={answerItem.questionId || ai} className="px-5 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Pergunta {ai + 1}</p>
                  <p
                    className="mt-0.5 text-sm font-medium text-gray-800"
                    dangerouslySetInnerHTML={{ __html: questionLabel }}
                  />
                  <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">
                    {formattedAnswer || <span className="italic text-gray-400">Sem resposta</span>}
                  </p>
                </div>
              )
            })
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/60 p-0 sm:p-3 lg:p-4">
      <div className="flex h-full w-full flex-col bg-white shadow-2xl sm:h-[calc(100vh-1.5rem)] sm:w-[calc(100vw-1.5rem)] sm:rounded-2xl lg:h-[calc(100vh-2rem)] lg:w-[calc(100vw-2rem)]">
        {/* Header */}
        <div className="flex flex-col gap-4 border-b border-gray-200 px-4 py-4 sm:px-6 sm:py-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-gray-900">Resultados da Pesquisa</h2>
            <p className="mt-1 text-sm text-gray-500">
              {survey.title ? stripHtml(survey.title) : ''}
            </p>
          </div>
          <div className="flex items-center justify-between gap-3 lg:justify-end">
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-1">
              <button
                type="button"
                onClick={() => setResultsView('respostas')}
                className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  resultsView === 'respostas'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <FileText size={16} />
                Respostas
              </button>
              <button
                type="button"
                onClick={() => setResultsView('graficos')}
                className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                  resultsView === 'graficos'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <BarChart3 size={16} />
                Gráficos
              </button>
            </div>
            <label className={`inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50 ${importingResponses ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
              <Upload size={16} />
              <span>{importingResponses ? 'Importando...' : 'Importar respostas'}</span>
              <input
                type="file"
                accept=".csv,.tsv,text/csv,text/tab-separated-values"
                onChange={handleImportResponses}
                disabled={importingResponses}
                className="hidden"
              />
            </label>
            <button
              onClick={openExportConfig}
              disabled={loading || responses.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-300"
            >
              <Download size={16} />
              <span>Exportar respostas</span>
            </button>
            <button
              onClick={handleExportResultTables}
              disabled={loading || responses.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-green-200 bg-white px-4 py-2 text-sm font-medium text-green-700 transition-colors hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FileText size={16} />
              <span>Exportar tabelas</span>
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <X size={24} />
            </button>
          </div>
        </div>

        {showExportConfig && (() => {
          const allQuestions = survey.questions || []
          const normalizedSearch = exportSearch.trim().toLocaleLowerCase('pt-BR')
          const visibleQuestions = allQuestions.filter((question) => {
            if (!normalizedSearch) return true
            return getQuestionReferenceLabel(question).toLocaleLowerCase('pt-BR').includes(normalizedSearch)
          })
          const allSelected = allQuestions.length > 0 && allQuestions.every((question) => selectedExportQuestionIds.includes(question.id))
          const toggleQuestion = (questionId: string) => {
            setSelectedExportQuestionIds((previous) => previous.includes(questionId)
              ? previous.filter((id) => id !== questionId)
              : [...previous, questionId])
          }

          return (
            <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
              <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Configurar exportação da base</h3>
                    <p className="mt-1 text-sm text-gray-600">Escolha as perguntas e o texto dos cabeçalhos do Excel.</p>
                  </div>
                  <button type="button" onClick={() => setShowExportConfig(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"><X size={20} /></button>
                </div>

                <div className="space-y-5 overflow-y-auto p-6">
                  <fieldset>
                    <legend className="mb-2 text-sm font-semibold text-gray-800">Cabeçalhos das perguntas</legend>
                    <div className="grid gap-2 sm:grid-cols-3">
                      {[
                        ['variable', 'Somente variável'],
                        ['title', 'Somente título'],
                        ['both', 'Variável - título'],
                      ].map(([value, label]) => (
                        <label key={value} className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 p-3 text-sm text-gray-700 hover:bg-gray-50">
                          <input type="radio" name="export-header-mode" value={value} checked={exportHeaderMode === value} onChange={() => setExportHeaderMode(value as 'variable' | 'title' | 'both')} />
                          {label}
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div className="space-y-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-800">Perguntas incluídas</h4>
                        <p className="text-xs text-gray-500">{selectedExportQuestionIds.length} de {allQuestions.length} selecionada(s)</p>
                      </div>
                      <label className="inline-flex items-center gap-2 text-sm font-medium text-blue-700">
                        <input type="checkbox" checked={allSelected} onChange={() => setSelectedExportQuestionIds(allSelected ? [] : allQuestions.map((question) => question.id))} />
                        Selecionar todas
                      </label>
                    </div>
                    <div className="relative">
                      <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input value={exportSearch} onChange={(event) => setExportSearch(event.target.value)} placeholder="Buscar por variável ou título" className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
                      {visibleQuestions.map((question) => (
                        <label key={question.id} className="flex cursor-pointer items-start gap-3 p-3 hover:bg-gray-50">
                          <input type="checkbox" checked={selectedExportQuestionIds.includes(question.id)} onChange={() => toggleQuestion(question.id)} className="mt-1" />
                          <span className="min-w-0 text-sm text-gray-800">{getQuestionReferenceLabel(question)}</span>
                          {!question.variable?.trim() && <span className="ml-auto shrink-0 rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">Sem variável</span>}
                        </label>
                      ))}
                      {visibleQuestions.length === 0 && <p className="p-4 text-center text-sm text-gray-500">Nenhuma pergunta encontrada.</p>}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <button type="button" onClick={() => setShowExportConfig(false)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-white">Cancelar</button>
                  <button type="button" onClick={() => { void handleExportExcel(selectedExportQuestionIds, exportHeaderMode); setShowExportConfig(false) }} className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"><Download size={16} />Exportar XLSX</button>
                </div>
              </div>
            </div>
          )
        })()}

        {/* Body */}
        <div className="flex-1 space-y-6 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
            </div>
          ) : (
            <>
              {/* Total de respostas */}
              <div className="flex items-center space-x-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="p-3 bg-green-600 rounded-full">
                  <BarChart3 size={24} className="text-white" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-green-700">{totalResponses}</p>
                  <p className="text-sm text-green-600 font-medium">
                    {totalResponses === 1 ? 'vez respondida' : 'vezes respondida'}
                  </p>
                </div>
              </div>

              {resultsView === 'respostas' && responses.length > 0 && (
                <div className="overflow-hidden rounded-xl border border-red-200">
                  <div className="flex flex-col gap-3 border-b border-red-200 bg-red-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-red-900">Gerenciar respostas</p>
                      <p className="mt-1 text-xs text-red-700">
                        Use o checkbox ou clique no nome do entrevistado para visualizar as respostas dele; o áudio,
                        quando houver, aparece no topo do painel. Sem entrevistado selecionado, as respostas de todos
                        são exibidas abaixo. O selo de microfone indica quem possui gravação. Use o ícone de lixeira
                        para marcar respostas e depois excluí-las.
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleDeleteResponses(selectedResponseIds)}
                        disabled={deletingResponses || selectedResponseCount === 0}
                        className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Trash2 size={14} />
                        {deletingResponses ? 'Excluindo...' : `Excluir selecionadas (${selectedResponseCount})`}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteResponses(responseIds)}
                        disabled={deletingResponses || responseIds.length === 0}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-300 bg-white px-3 py-2 text-xs font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Trash2 size={14} />
                        Excluir todas
                      </button>
                    </div>
                  </div>

                  <div className="divide-y divide-red-100 bg-white">
                    <label className="flex cursor-pointer items-center gap-3 px-4 py-3 text-xs font-semibold text-gray-600 hover:bg-gray-50" title="Marca todas as respostas para exclusão">
                      <input
                        type="checkbox"
                        checked={allResponsesSelected}
                        onChange={toggleAllResponses}
                        disabled={deletingResponses}
                        className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      Marcar todas as respostas para exclusão ({responses.length})
                    </label>
                    <div className="max-h-72 overflow-y-auto">
                      {responses.map((response, index) => {
                        const responseDate = response.submittedAt || response.completedAt || response.createdAt
                        const isDetail = detailResponse?._id === response._id
                        const isMarkedForDelete = selectedResponseIds.includes(response._id)
                        const hasAudio = responseAudios.some((item) => item.responseId === response._id)
                        return (
                          <div
                            key={response._id}
                            className={`flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors ${
                              isDetail ? 'bg-blue-50 border-l-4 border-blue-500' : 'hover:bg-red-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isDetail}
                              onChange={() => setDetailResponse(isDetail ? null : response)}
                              disabled={deletingResponses}
                              onClick={(e) => e.stopPropagation()}
                              title={isDetail ? 'Entrevistado selecionado' : 'Selecionar as respostas deste entrevistado'}
                              className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-red-600 focus:ring-red-500"
                            />
                            <button
                              type="button"
                              className="min-w-0 flex-1 text-left"
                              onClick={() => setDetailResponse(isDetail ? null : response)}
                            >
                              <span className="block font-medium text-gray-900 text-sm">
                                Entrevistado {index + 1}
                                {hasAudio && (
                                  <span
                                    className="ml-2 inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-normal text-blue-700"
                                    title="Este entrevistado possui gravação de áudio"
                                  >
                                    <Mic size={11} />
                                    áudio
                                  </span>
                                )}
                                {isDetail && <span className="ml-2 text-xs font-normal text-blue-600">(visualizando)</span>}
                              </span>
                              <span className="block text-xs text-gray-500">
                                {response.researcherName || 'Sem pesquisador'}
                                {responseDate ? ` · ${new Date(responseDate).toLocaleString('pt-BR')}` : ''}
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleResponseSelection(response._id)
                              }}
                              disabled={deletingResponses}
                              title={isMarkedForDelete ? 'Desmarcar para exclusão' : 'Marcar para exclusão'}
                              aria-label={isMarkedForDelete ? 'Desmarcar para exclusão' : 'Marcar para exclusão'}
                              className={`mt-0.5 shrink-0 rounded-lg p-1.5 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                                isMarkedForDelete
                                  ? 'bg-red-100 text-red-700'
                                  : 'text-gray-400 hover:bg-red-50 hover:text-red-600'
                              }`}
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Painel de detalhe de entrevistado */}
              {resultsView === 'respostas' && detailResponse && (() => {
                const freshDetail = responses.find((r) => r._id === detailResponse._id) || detailResponse
                return renderRespondentPanel(freshDetail)
              })()}

              {resultsView === 'respostas' && !detailResponse && responses.length > 0 && (
                <div className="space-y-4">
                  {responses.map((response) => renderRespondentPanel(response))}
                </div>
              )}

              {resultsView === 'graficos' && responses.length > 0 && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <p className="text-sm font-semibold text-gray-800">Entrevistados ({responses.length})</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Clique em um entrevistado para destacá-lo. Nesta tela não são exibidas as respostas escritas nem
                    os áudios — apenas os gráficos com as informações.
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {responses.map((response, index) => {
                      const isActive = chartHighlightedResponseId === response._id
                      const hasAudio = responseAudios.some((item) => item.responseId === response._id)
                      return (
                        <button
                          key={response._id}
                          type="button"
                          onClick={() => setChartHighlightedResponseId(isActive ? null : response._id)}
                          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                            isActive
                              ? 'border-blue-600 bg-blue-600 text-white'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                        >
                          Entrevistado {index + 1}
                          {hasAudio && <Mic size={12} className={isActive ? 'text-blue-100' : 'text-blue-600'} />}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {resultsView === 'respostas' && responses.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <BarChart3 size={48} className="mx-auto mb-3 opacity-40" />
                  <p className="text-lg font-medium">Nenhuma resposta registrada ainda</p>
                  <p className="text-sm mt-1">Os resultados serão exibidos aqui após a pesquisa ser respondida.</p>
                </div>
              )}
              {resultsView === 'graficos' && totalResponses === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <BarChart3 size={48} className="mx-auto mb-3 opacity-40" />
                  <p className="text-lg font-medium">Nenhuma resposta registrada ainda</p>
                  <p className="text-sm mt-1">Os resultados serão exibidos aqui após a pesquisa ser respondida.</p>
                </div>
              )}
              {resultsView === 'graficos' && questionStats.map((stat, qi) => {
                  const preference = getChartPreference(stat.questionId)
                  const orderedOptions = sortStatOptions(resolveStatOptions(stat, preference.base), preference.sort)
                  const optionColors = getChartColors(stat.questionId, orderedOptions)
                  const mergedTextAnalysis = getMergedTextAnalysis(stat)
                  const sortedTextAnalysis = [...(stat.textAnalysis || [])].sort((left, right) => left.label.localeCompare(right.label))

                  return (
                  <div key={stat.questionId} className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-5 py-3 border-b border-gray-200">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Pergunta {qi + 1}</p>
                      <p className="font-semibold text-gray-800 mt-0.5">{stat.questionTitle}</p>
                    </div>

                    <div className="p-5">
                      {/* Perguntas de escolha */}
                      {stat.options.length > 0 && (
                        <div className="space-y-3">
                          <div className="flex flex-col gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3 lg:flex-row lg:items-end lg:justify-between">
                            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                              Cálculo
                              <select
                                value={preference.base}
                                onChange={(event) => updateChartPreference(stat.questionId, { base: event.target.value as PercentageBase })}
                                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-700"
                              >
                                <option value="observation">Total por observação</option>
                                <option value="citation">Total por citação</option>
                              </select>
                            </label>
                            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                              Ordenação
                              <select
                                value={preference.sort}
                                onChange={(event) => updateChartPreference(stat.questionId, { sort: event.target.value as 'survey' | 'asc' | 'desc' })}
                                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-700"
                              >
                                <option value="survey">Ordem do questionario</option>
                                <option value="desc">Maior para menor</option>
                                <option value="asc">Menor para maior</option>
                              </select>
                            </label>
                            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                              Gráfico
                              <select
                                value={preference.chart}
                                onChange={(event) => updateChartPreference(stat.questionId, { chart: event.target.value as ResultChartType })}
                                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-700"
                              >
                                {RESULT_CHART_GROUPS.map((group) => (
                                  <optgroup key={group.label} label={group.label}>
                                    {group.options.map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                            </label>
                            <button
                              type="button"
                              onClick={() => copyChartToClipboard(stat.questionTitle, orderedOptions, preference.chart, optionColors, getPercentageBaseLabel(preference.base))}
                              className="inline-flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50"
                            >
                              <Copy size={15} />
                              Copiar gráfico
                            </button>
                          </div>

                          <div className="rounded-lg border border-gray-100 bg-white p-3">
                            <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                              <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
                                Cores
                                <select
                                  value={preference.colorMode}
                                  onChange={(event) => updateChartPreference(stat.questionId, { colorMode: event.target.value as ChartColorMode })}
                                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-normal text-gray-700"
                                >
                                  <option value="palette">Paleta automatica</option>
                                  <option value="single">Uma cor para tudo</option>
                                  <option value="custom">Cor por resposta</option>
                                </select>
                              </label>
                              {preference.colorMode === 'single' && (
                                <label className="flex items-center gap-2 text-xs font-medium text-gray-600">
                                  Cor única
                                  <input
                                    type="color"
                                    value={preference.singleColor}
                                    onChange={(event) => updateChartPreference(stat.questionId, { singleColor: event.target.value })}
                                    className="h-9 w-12 rounded border border-gray-300 bg-white p-1"
                                  />
                                </label>
                              )}
                            </div>
                            {preference.colorMode === 'custom' && (
                              <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                {orderedOptions.map((option, index) => (
                                  <label key={option.optionId} className="flex min-w-0 items-center justify-between gap-2 rounded-lg border border-gray-100 px-2 py-1 text-xs text-gray-600">
                                    <span className="min-w-0 truncate">{option.optionText || option.optionId}</span>
                                    <input
                                      type="color"
                                      value={optionColors[index]}
                                      onChange={(event) => updateOptionChartColor(stat.questionId, option.optionId, event.target.value)}
                                      className="h-8 w-10 shrink-0 rounded border border-gray-300 bg-white p-1"
                                    />
                                  </label>
                                ))}
                              </div>
                            )}
                            {renderResultChart(preference.chart, orderedOptions, optionColors)}
                          </div>
                          <div className="overflow-x-auto rounded-lg border border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-600">Resposta</th>
                                  <th className="px-3 py-2 text-right font-semibold text-gray-600">Quantidade</th>
                                  <th className="px-3 py-2 text-right font-semibold text-gray-600">{getPercentageBaseLabel(preference.base)}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 bg-white">
                                {orderedOptions.map((option) => (
                                  <tr key={option.optionId}>
                                    <td className="px-3 py-2 text-gray-700">{option.optionText || option.optionId}</td>
                                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{option.count}</td>
                                    <td className="px-3 py-2 text-right font-semibold text-gray-900">{option.percentage}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {stat.average !== undefined && (
                        <div className="flex items-center space-x-4">
                          <div className="text-4xl font-bold text-green-600">{stat.average}</div>
                          <div className="text-sm text-gray-500">média das respostas</div>
                        </div>
                      )}

                      {/* Texto livre */}
                      {(stat.textAnalysis?.length || stat.textAnswers.length > 0) && (
                        <div className="space-y-4">
                          {mergedTextAnalysis.length ? (
                            <div className="overflow-x-auto rounded-lg border border-gray-200">
                              <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Resposta tratada</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-600">Chave normalizada</th>
                                    <th className="px-3 py-2 text-right font-semibold text-gray-600">Quantidade</th>
                                    <th className="px-3 py-2 text-right font-semibold text-gray-600">Percentual</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                  {mergedTextAnalysis.map((item) => (
                                    <tr key={item.normalized}>
                                      <td className="px-3 py-2 text-gray-700">{item.label}</td>
                                      <td className="px-3 py-2 text-gray-500">{item.normalized}</td>
                                      <td className="px-3 py-2 text-right font-semibold text-gray-900">{item.count}</td>
                                      <td className="px-3 py-2 text-right font-semibold text-gray-900">{item.percentage}%</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : null}
                          {sortedTextAnalysis.length > 1 && (
                            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                              <p className="text-sm font-semibold text-amber-900">Tratamento manual de respostas</p>
                              <p className="mt-1 text-xs text-amber-800">
                                Digite no campo "Unificar como" o padrao desejado (texto livre) ou escolha uma das
                                respostas existentes nas sugestoes, como AG, Adilson e Adilson Girardi. Voltar ao texto
                                original remove a unificacao. As somas aparecem na tabela "Resposta tratada" acima.
                              </p>
                              <datalist id={`unificar-como-${stat.questionId}`}>
                                {sortedTextAnalysis.map((target) => (
                                  <option key={target.normalized} value={target.label} />
                                ))}
                              </datalist>
                              <div className="mt-3 overflow-x-auto rounded-lg border border-amber-100 bg-white">
                                <table className="min-w-full divide-y divide-amber-100 text-sm">
                                  <thead className="bg-amber-50">
                                    <tr>
                                      <th className="px-3 py-2 text-left font-semibold text-amber-900">Resposta encontrada</th>
                                      <th className="px-3 py-2 text-right font-semibold text-amber-900">Quantidade</th>
                                      <th className="px-3 py-2 text-left font-semibold text-amber-900">Unificar como</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-amber-50">
                                    {sortedTextAnalysis.map((item) => (
                                      <tr key={item.normalized}>
                                        <td className="px-3 py-2 text-gray-700">{item.label}</td>
                                        <td className="px-3 py-2 text-right font-semibold text-gray-900">{item.count}</td>
                                        <td className="px-3 py-2">
                                          <input
                                            type="text"
                                            list={`unificar-como-${stat.questionId}`}
                                            value={(textTreatmentAliases[stat.questionId] || {})[item.normalized] ?? item.label}
                                            onChange={(event) => updateTextTreatmentAlias(stat.questionId, item.normalized, item.label, event.target.value)}
                                            placeholder="Digite o padrao de unificacao"
                                            className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm text-gray-700"
                                          />
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Respostas originais</p>
                          {stat.textAnswers.map((txt, i) => (
                            <div key={i} className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700 border border-gray-100">
                              {txt}
                            </div>
                          ))}
                          {totalResponses > 20 && (
                            <p className="text-xs text-gray-400 text-center">Exibindo as primeiras 20 respostas</p>
                          )}
                        </div>
                      )}

                      {stat.options.length === 0 && stat.average === undefined && stat.textAnswers.length === 0 && !stat.textAnalysis?.length && (
                        <p className="text-sm text-gray-400 italic">Nenhuma resposta para esta pergunta.</p>
                      )}
                    </div>
                  </div>
                  )
                })
              }
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-gray-200 px-4 py-4 sm:px-6 sm:py-5">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

const AssignResearcherModal: React.FC<{
  survey: Survey
  onAssign: (researcherId: string) => Promise<void>
  onUnassign: (assignmentId: string, researcherId: string) => Promise<void>
  onClose: () => void
}> = ({ survey, onAssign, onUnassign, onClose }) => {
  const [researchers, setResearchers] = useState<Researcher[]>([])
  const [assignments, setAssignments] = useState<AssignmentWithResearcher[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [savingResearcherId, setSavingResearcherId] = useState<string | null>(null)
  const [removingAssignmentId, setRemovingAssignmentId] = useState<string | null>(null)
  const [pendingUnassign, setPendingUnassign] = useState<{
    assignmentId: string
    researcherId: string
    researcherName: string
  } | null>(null)

  const stripHtmlText = (html: string) => html.replace(/<[^>]*>/g, '')

  const loadAssignmentData = async () => {
    try {
      setLoading(true)

      const [researchersResponse, assignmentsResponse, freshSurvey] = await Promise.all([
        lumi.entities.researchers.list({
          filter: { isActive: true },
          sort: { name: 1 },
          limit: 1000,
        }),
        lumi.entities.survey_assignments.list({
          filter: { surveyId: survey._id },
          sort: { assignedAt: -1 },
          limit: 1000,
        }),
        lumi.entities.surveys.get(survey._id).catch(() => survey) as Promise<Survey>,
      ])

      const researcherList = (researchersResponse.list || []) as Researcher[]
      const assignmentList = (assignmentsResponse.list || []) as SurveyAssignment[]
      const researcherMap = new Map(researcherList.map((researcher) => [researcher._id, researcher]))
      const removedAssignmentIds = new Set(
        Array.isArray(freshSurvey?.removedAssignmentIds) ? freshSurvey.removedAssignmentIds : []
      )
      const visibleAssignmentList = assignmentList.filter((assignment) => (
        assignment.status !== 'removed' && !removedAssignmentIds.has(assignment._id)
      ))

      setResearchers(researcherList)
      setAssignments(
        visibleAssignmentList.map((assignment) => ({
          ...assignment,
          researcher: researcherMap.get(assignment.researcherId) || null,
        }))
      )
    } catch (error) {
      console.error('Erro ao carregar dados de atribuição:', error)
      toast.error('Erro ao carregar pesquisadores e atribuicoes.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadAssignmentData()
  }, [survey._id])

  const assignmentByResearcherId = new Map(
    assignments.map((assignment) => [assignment.researcherId, assignment])
  )

  const filteredResearchers = researchers.filter((researcher) => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return true

    return (
      researcher.name.toLowerCase().includes(query) ||
      researcher.email.toLowerCase().includes(query)
    )
  })

  const getAssignmentStatusLabel = (status: SurveyAssignment['status']) => {
    switch (status) {
      case 'assigned':
        return 'Atribuída'
      case 'in_progress':
        return 'Em andamento'
      case 'completed':
        return 'Concluída'
      default:
        return status
    }
  }

  const getAssignmentStatusColor = (status: SurveyAssignment['status']) => {
    switch (status) {
      case 'assigned':
        return 'bg-blue-100 text-blue-700'
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-700'
      case 'completed':
        return 'bg-green-100 text-green-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const formatDate = (value?: string) => {
    if (!value) return '-'
    return new Date(value).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Atribuir Pesquisador</h2>
            <p className="text-sm text-gray-500 mt-1">
              Pesquisa: {stripHtmlText(survey.title)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-88px)] space-y-6">
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              Se o pesquisador já tiver uma atribuição concluída para esta pesquisa, o sistema reativa a atribuição.
              Se já existir atribuição ativa, o sistema evita duplicação.
            </p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-gray-900">Selecionar Pesquisador</h3>
                <span className="text-sm text-gray-500">
                  {filteredResearchers.length} {filteredResearchers.length !== 1 ? 'disponíveis' : 'disponível'}
                </span>
              </div>

              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar pesquisador por nome ou email..."
                  className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-[420px] overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12 text-gray-500">
                    <Loader2 size={18} className="animate-spin mr-2" />
                    Carregando pesquisadores...
                  </div>
                ) : filteredResearchers.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    Nenhum pesquisador ativo encontrado.
                  </div>
                ) : (
                  filteredResearchers.map((researcher) => {
                    const existingAssignment = assignmentByResearcherId.get(researcher._id)
                    const isBusy =
                      existingAssignment?.status === 'assigned' ||
                      existingAssignment?.status === 'in_progress'
                    const buttonLabel = existingAssignment?.status === 'completed'
                      ? 'Reatribuir'
                      : existingAssignment
                        ? 'Ja atribuido'
                        : 'Atribuir'

                    return (
                      <div
                        key={researcher._id}
                        className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">{researcher.name}</p>
                          <p className="text-sm text-gray-500 truncate">{researcher.email}</p>
                          {researcher.specialization && (
                            <p className="text-xs text-gray-400 mt-1">
                              {researcher.specialization}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {existingAssignment && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAssignmentStatusColor(existingAssignment.status)}`}>
                              {getAssignmentStatusLabel(existingAssignment.status)}
                            </span>
                          )}

                          <button
                            onClick={async () => {
                              try {
                                setSavingResearcherId(researcher._id)
                                await onAssign(researcher._id)
                                await loadAssignmentData()
                              } finally {
                                setSavingResearcherId(null)
                              }
                            }}
                            disabled={isBusy || savingResearcherId === researcher._id}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              isBusy
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                            }`}
                          >
                            {savingResearcherId === researcher._id ? 'Salvando...' : buttonLabel}
                          </button>

                          {existingAssignment && existingAssignment.status !== 'completed' && (
                            <button
                              type="button"
                              onClick={() => {
                                setPendingUnassign({
                                  assignmentId: existingAssignment._id,
                                  researcherId: researcher._id,
                                  researcherName: researcher.name,
                                })
                              }}
                              disabled={removingAssignmentId === existingAssignment._id}
                              className="px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {removingAssignmentId === existingAssignment._id ? 'Retirando...' : 'Retirar'}
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-gray-900">Atribuicoes desta Pesquisa</h3>
                <span className="text-sm text-gray-500">
                  {assignments.length} registro{assignments.length !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-[420px] overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-12 text-gray-500">
                    <Loader2 size={18} className="animate-spin mr-2" />
                    Carregando atribuicoes...
                  </div>
                ) : assignments.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    Nenhum pesquisador atribuido a esta pesquisa ainda.
                  </div>
                ) : (
                  assignments.map((assignment) => (
                    <div key={assignment._id} className="p-4 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {assignment.researcher?.name || `Pesquisador #${assignment.researcherId.slice(-6)}`}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {assignment.researcher?.email || assignment.researcherId}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAssignmentStatusColor(assignment.status)}`}>
                          {getAssignmentStatusLabel(assignment.status)}
                        </span>
                      </div>

                      <div className="text-xs text-gray-400 flex flex-wrap gap-x-4 gap-y-1">
                        <span>Atribuida em: {formatDate(assignment.assignedAt)}</span>
                        {assignment.startedAt && <span>Iniciada em: {formatDate(assignment.startedAt)}</span>}
                        {assignment.completedAt && <span>Concluida em: {formatDate(assignment.completedAt)}</span>}
                      </div>

                      {assignment.status !== 'completed' && (
                        <button
                          type="button"
                          onClick={() => {
                            setPendingUnassign({
                              assignmentId: assignment._id,
                              researcherId: assignment.researcherId,
                              researcherName: assignment.researcher?.name || `Pesquisador #${assignment.researcherId.slice(-6)}`,
                            })
                          }}
                          disabled={removingAssignmentId === assignment._id}
                          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Trash2 size={15} />
                          {removingAssignmentId === assignment._id ? 'Retirando...' : 'Retirar entrevistador'}
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {pendingUnassign && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900">Retirar pesquisador</h3>
            <p className="text-sm text-gray-600 mt-2">
              Retirar <span className="font-medium text-gray-900">{pendingUnassign.researcherName}</span> desta
              pesquisa? Ele deixara de ver este questionario na area do pesquisador.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setPendingUnassign(null)}
                disabled={removingAssignmentId === pendingUnassign.assignmentId}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={async () => {
                  const target = pendingUnassign
                  setPendingUnassign(null)
                  if (!target) return
                  try {
                    setRemovingAssignmentId(target.assignmentId)
                    await onUnassign(target.assignmentId, target.researcherId)
                  } finally {
                    setRemovingAssignmentId(null)
                    await loadAssignmentData()
                  }
                }}
                disabled={removingAssignmentId === pendingUnassign.assignmentId}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {removingAssignmentId === pendingUnassign.assignmentId ? 'Retirando...' : 'Confirmar retirada'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// garante que toda pergunta presente em survey.questions tenha seu ID
const normalizeSurveyPages = (
  questions: Question[],
  rawPages: SurveyPage[]
): SurveyPage[] => {
  if (questions.length === 0) return rawPages

  const mappedIds = new Set<string>(rawPages.flatMap(p => p.questionIds || []))

  const orphanIds = questions.map(q => q.id).filter(id => !mappedIds.has(id))

  if (orphanIds.length === 0) {
    return rawPages.map(p => ({ ...p, questionIds: p.questionIds || [] }))
  }

  if (rawPages.length > 0) {
    return rawPages.map((p, idx) =>
      idx === 0
        ? { ...p, questionIds: [...(p.questionIds || []), ...orphanIds] }
        : { ...p, questionIds: p.questionIds || [] }
    )
  }

  return [
    {
      id: `page_${Date.now()}`,
      title: 'Página 1',
      description: '',
      questionIds: questions.map(q => q.id),
      order: 0,
    } as SurveyPage,
  ]
}

const PrintSurveyConfigModal: React.FC<{
  survey: Survey
  onClose: () => void
  onPrint: (settings: PrintSurveySettings) => void
}> = ({ survey, onClose, onPrint }) => {
  const [settings, setSettings] = useState<PrintSurveySettings>(() => createDefaultPrintSettings(survey))

  const updateSetting = <K extends keyof PrintSurveySettings>(key: K, value: PrintSurveySettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between border-b border-gray-200 p-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Configurar impressao/PDF</h2>
            <p className="mt-1 text-sm text-gray-600">{stripHtmlText(survey.title)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-[minmax(0,1.1fr)_minmax(300px,0.9fr)]">
          <div className="space-y-6 p-6">
            <section>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.12em] text-gray-500">Página</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-gray-700">Formato</span>
                  <select
                    value={settings.pageSize}
                    onChange={(event) => updateSetting('pageSize', event.target.value as PrintSurveySettings['pageSize'])}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="a4">A4</option>
                    <option value="letter">Carta</option>
                  </select>
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-gray-700">Orientação</span>
                  <select
                    value={settings.orientation}
                    onChange={(event) => updateSetting('orientation', event.target.value as PrintSurveySettings['orientation'])}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="portrait">Retrato</option>
                    <option value="landscape">Paisagem</option>
                  </select>
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-gray-700">Densidade</span>
                  <select
                    value={settings.density}
                    onChange={(event) => updateSetting('density', event.target.value as PrintSurveySettings['density'])}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="compact">Compacta</option>
                    <option value="comfortable">Confortavel</option>
                  </select>
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-gray-700">Tamanho da fonte</span>
                  <select
                    value={settings.fontSize}
                    onChange={(event) => updateSetting('fontSize', event.target.value as PrintSurveySettings['fontSize'])}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="small">Pequena</option>
                    <option value="medium">Media</option>
                    <option value="large">Grande</option>
                  </select>
                </label>
              </div>
            </section>

            <section>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.12em] text-gray-500">Cabecalho da impressao</h3>
              <div className="space-y-4">
                <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 text-xs text-amber-900">
                  O titulo da pesquisa aparece uma unica vez no corpo. Use os 3 quadros para textos livres (empresa, cliente, etc.) no topo de cada pagina — sem repetir data/titulo.
                </div>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-gray-700">Titulo impresso (centro da capa)</span>
                  <RichTextEditor
                    value={settings.headerTitle}
                    onChange={(value) => updateSetting('headerTitle', value)}
                    height="100px"
                  />
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-gray-700">Subtitulo opcional</span>
                  <RichTextEditor
                    value={settings.headerSubtitle}
                    onChange={(value) => updateSetting('headerSubtitle', value)}
                    height="100px"
                  />
                </label>
                <div>
                  <p className="mb-1 text-sm font-medium text-gray-700">3 quadros de texto (topo de cada pagina)</p>
                  <p className="mb-2 text-xs text-gray-500">Use as ferramentas abaixo para aplicar tamanho, negrito, itálico, cor e alinhamento no PDF.</p>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div>
                      <span className="mb-1 block text-xs font-medium text-gray-600">Esquerda</span>
                      <RichTextEditor
                        value={settings.headerBoxLeft}
                        onChange={(value) => updateSetting('headerBoxLeft', value)}
                        placeholder="Texto livre"
                        height="125px"
                      />
                    </div>
                    <div>
                      <span className="mb-1 block text-xs font-medium text-gray-600">Centro</span>
                      <RichTextEditor
                        value={settings.headerBoxCenter}
                        onChange={(value) => updateSetting('headerBoxCenter', value)}
                        placeholder="Texto livre"
                        height="125px"
                      />
                    </div>
                    <div>
                      <span className="mb-1 block text-xs font-medium text-gray-600">Direita</span>
                      <RichTextEditor
                        value={settings.headerBoxRight}
                        onChange={(value) => setSettings((prev) => ({
                          ...prev,
                          headerBoxRight: value,
                          headerRightText: value,
                        }))}
                        placeholder="Empresa / cliente"
                        height="125px"
                      />
                    </div>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    checked={settings.showHeaderImage}
                    onChange={(event) => updateSetting('showHeaderImage', event.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Exibir logo na impressao (todas as paginas)
                </label>
                {settings.showHeaderImage && (
                  <>
                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-gray-700">URL da logo</span>
                      <input
                        value={settings.headerImageUrl}
                        onChange={(event) => updateSetting('headerImageUrl', event.target.value)}
                        placeholder="https://..."
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-2 block text-sm font-medium text-gray-700">Alinhamento da logo</span>
                      <select
                        value={settings.logoAlign}
                        onChange={(event) => updateSetting('logoAlign', event.target.value as PrintSurveySettings['logoAlign'])}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="left">Esquerda</option>
                        <option value="center">Centralizado</option>
                        <option value="right">Direita</option>
                      </select>
                    </label>
                  </>
                )}
              </div>
            </section>
          </div>

          <div className="space-y-6 border-t border-gray-200 bg-gray-50 p-6 lg:border-l lg:border-t-0">
            <section>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.12em] text-gray-500">Perguntas e respostas</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-gray-700">Area das perguntas</span>
                  <select
                    value={settings.boundaryStyle}
                    onChange={(event) => updateSetting('boundaryStyle', event.target.value as PrintSurveySettings['boundaryStyle'])}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="cards">Quadros</option>
                    <option value="lines">Linhas</option>
                    <option value="none">Sem delimitação</option>
                  </select>
                </label>
                <label className="block">
                  <span className="mb-2 block text-sm font-medium text-gray-700">Opções de resposta</span>
                  <select
                    value={settings.optionLayout}
                    onChange={(event) => updateSetting('optionLayout', event.target.value as PrintSurveySettings['optionLayout'])}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="columns">Colunas</option>
                    <option value="list">Lista vertical</option>
                    <option value="inline">Linha compacta</option>
                  </select>
                </label>
              </div>
            </section>

            <section>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.12em] text-gray-500">Exibicao</h3>
              <div className="space-y-3">
                {[
                  ['showQuestionNumbers', 'Números das questoes'],
                  ['showInstructions', 'Instrucoes e descricoes'],
                  ['showPageTitles', 'Títulos das páginas'],
                  ['showRequiredMark', 'Marcação de obrigatórias'],
                  ['showImageCaptions', 'Legenda de tamanho das imagens'],
                  ['showGeneratedAt', 'Data/hora da geração'],
                ].map(([key, label]) => (
                  <label key={key} className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={settings[key as keyof PrintSurveySettings] as boolean}
                      onChange={(event) => updateSetting(key as keyof PrintSurveySettings, event.target.checked as never)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </section>

            <div className="rounded-lg border border-blue-100 bg-white p-4 text-sm text-gray-600">
              A configuração compacta com respostas em colunas reduz o número de páginas e preserva a ordem das páginas e perguntas cadastradas.
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 p-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onPrint(settings)}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-900"
          >
            <Printer size={16} />
            Imprimir/PDF
          </button>
        </div>
      </div>
    </div>
  )
}

const SurveyFormModal: React.FC<{
  survey?: Survey | null
  onSave: (data: Partial<Survey>) => void
  onClose: () => void
  onPreviewQuestion: (question: Question) => void
  onShowQRCode?: (survey: Survey) => void
}> = ({ survey, onSave, onClose, onPreviewQuestion, onShowQRCode }) => {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState(() => {
    const rawQuestions = survey?.questions || []
    const rawPages = survey?.pages || []
    const normalizedStructure = normalizeQuestionPageState(rawQuestions, rawPages)

    return {
      title: normalizeRichTextValue(survey?.title),
      description: normalizeRichTextValue(survey?.description),
      targetAudience: normalizeRichTextValue(survey?.targetAudience),
      languages: survey?.languages || ['pt'],
      tags: survey?.tags?.join(', ') || '',
      startDate: survey?.startDate?.split('T')[0] || '',
      endDate: survey?.endDate?.split('T')[0] || '',
      questions: normalizedStructure.questions,
      clientId: survey?.clientId || '',
      clientName: survey?.clientName || '',
      logoUrl: survey?.logoUrl || '',
      logoDisplaySize: survey?.logoDisplaySize || 'medium',
      showSurveyTitle: survey?.showSurveyTitle !== false,
      showPageTitles: survey?.showPageTitles !== false,
      showLogoOnEveryPage: survey?.showLogoOnEveryPage !== false,
      audioRecordingEnabled: survey ? survey.audioRecordingEnabled !== false : true,
      publicResponseEnabled: survey?.publicResponseEnabled || false,
      publicResponseToken: survey?.publicResponseToken || '',
      clientResultsEnabled: survey?.clientResultsEnabled || false,
      clientResultsToken: survey?.clientResultsToken || '',
      clientResultsPassword: survey?.clientResultsPassword || '',
      clientResultsPasswordHash: survey?.clientResultsPasswordHash || '',
      clientResultsPasswordSalt: survey?.clientResultsPasswordSalt || '',
      clientResultsPasswordUpdatedAt: survey?.clientResultsPasswordUpdatedAt || '',
      pages: normalizedStructure.pages,
    }
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [clients, setClients] = useState<Client[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [showPageManager, setShowPageManager] = useState(false)
  const [showQuestionBranches, setShowQuestionBranches] = useState(false)
  const draftKey = survey?._id
    ? `survey_form_draft:${survey._id}:${survey.updatedAt || 'current'}`
    : 'survey_form_draft:new'
  const initialSnapshotRef = useRef(JSON.stringify({ currentStep: 1, formData }))
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoadingClients(true)
        const response = await lumi.entities.clients.list({
          sort: { name: 1 },
          limit: 1000
        }) as { list?: Client[] }
        setClients(response.list || [])
      } catch (error) {
        console.warn('Não foi possível carregar clientes:', error)
        setClients([])
      } finally {
        setLoadingClients(false)
      }
    }

    void loadClients()
  }, [])

  useEffect(() => {
    if (!survey) return

    try {
      const rawDraft = window.localStorage.getItem(draftKey)
      if (!rawDraft) return

      const parsedDraft = JSON.parse(rawDraft) as {
        currentStep?: number
        formData?: typeof formData
      }

      if (parsedDraft.formData) setFormData(parsedDraft.formData)
      if (typeof parsedDraft.currentStep === 'number') setCurrentStep(parsedDraft.currentStep)
    } catch (error) {
      console.warn('Não foi possível restaurar o rascunho da pesquisa:', error)
    }
  }, [draftKey, survey])

  useEffect(() => {
    const snapshot = JSON.stringify({ currentStep, formData })
    const isDirty = snapshot !== initialSnapshotRef.current
    setHasUnsavedChanges(isDirty)

    if (!isDirty) return

    const timeoutId = window.setTimeout(() => {
      try {
        window.localStorage.setItem(draftKey, snapshot)
      } catch (error) {
        console.warn('Não foi possível salvar o rascunho local da pesquisa:', error)
      }
    }, 600)

    return () => window.clearTimeout(timeoutId)
  }, [currentStep, draftKey, formData])

  useEffect(() => {
    if (!hasUnsavedChanges) return

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const clearDraft = () => {
    try {
      window.localStorage.removeItem(draftKey)
    } catch {
      // ignore
    }
  }

  const requestClose = () => {
    if (isSubmitting) return
    if (hasUnsavedChanges && !confirm('Existem alterações não salvas. Deseja sair e descartar o rascunho atual?')) {
      return
    }
    if (!survey) clearDraft()
    onClose()
  }

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      requestClose()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    if (isSubmitting) return
    
    if (currentStep === 1) {
      if (!formData.title.trim()) {
        toast.error('O título da pesquisa é obrigatório')
        return
      }
      setCurrentStep(2)
      return
    }

    const normalizedStructure = normalizeQuestionPageState(formData.questions, formData.pages)

    if (!formData.title.trim()) {
      toast.error('O título da pesquisa é obrigatório')
      return
    }

    if (normalizedStructure.questions.length === 0) {
      toast.error('Adicione pelo menos uma pergunta a pesquisa')
      return
    }

    const hasExistingPortalPassword = Boolean(
      survey?.clientResultsPasswordHash && survey?.clientResultsPasswordSalt
    )
    const hasNewPortalPassword = formData.clientResultsPassword.trim().length > 0

    if (formData.clientResultsEnabled && !hasExistingPortalPassword && !hasNewPortalPassword) {
      toast.error('Informe a senha do portal do cliente.')
      return
    }

    setIsSubmitting(true)

    try {
      const clientResultsToken = formData.clientResultsToken.trim() || generateClientResultsToken()
      const selectedClient = clients.find((client) => client._id === formData.clientId)
      const portalPasswordCredentials = formData.clientResultsEnabled && hasNewPortalPassword
        ? await createClientPortalPasswordCredentials(formData.clientResultsPassword.trim())
        : null
      const submitData: Partial<Survey> = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        targetAudience: formData.targetAudience.trim() || undefined,
        clientId: formData.clientId || undefined,
        clientName: selectedClient?.name || formData.clientName || undefined,
        languages: formData.languages,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        questions: normalizedStructure.questions,
        pages: normalizedStructure.pages,
        logoUrl: formData.logoUrl || undefined,
        logoDisplaySize: formData.logoDisplaySize || 'medium',
        showSurveyTitle: formData.showSurveyTitle !== false,
        showPageTitles: formData.showPageTitles !== false,
        showLogoOnEveryPage: formData.showLogoOnEveryPage !== false,
        audioRecordingEnabled: formData.audioRecordingEnabled === true,
        publicResponseEnabled: formData.publicResponseEnabled,
        publicResponseToken: formData.publicResponseEnabled ? (formData.publicResponseToken.trim() || generateClientResultsToken()) : undefined,
        clientResultsEnabled: formData.clientResultsEnabled,
        clientResultsToken: formData.clientResultsEnabled ? clientResultsToken : undefined,
        clientResultsPassword: undefined,
        clientResultsPasswordHash: formData.clientResultsEnabled
          ? portalPasswordCredentials?.clientResultsPasswordHash || formData.clientResultsPasswordHash || survey?.clientResultsPasswordHash
          : undefined,
        clientResultsPasswordSalt: formData.clientResultsEnabled
          ? portalPasswordCredentials?.clientResultsPasswordSalt || formData.clientResultsPasswordSalt || survey?.clientResultsPasswordSalt
          : undefined,
        clientResultsPasswordUpdatedAt: formData.clientResultsEnabled
          ? portalPasswordCredentials?.clientResultsPasswordUpdatedAt || formData.clientResultsPasswordUpdatedAt || survey?.clientResultsPasswordUpdatedAt
          : undefined
      }

      if (formData.startDate) {
        submitData.startDate = new Date(formData.startDate).toISOString()
      }
      
      if (formData.endDate) {
        submitData.endDate = new Date(formData.endDate).toISOString()
      }

      await onSave(submitData)
      clearDraft()
      initialSnapshotRef.current = JSON.stringify({ currentStep: 1, formData: submitData })
      setHasUnsavedChanges(false)
    } catch (error) {
      console.error('Error in form submission:', error)
      toast.error('Erro ao salvar pesquisa')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleLanguageChange = (lang: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        languages: [...prev.languages, lang]
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        languages: prev.languages.filter(l => l !== lang)
      }))
    }
  }

  const handleQuestionsChange = (questions: Question[]) => {
    setFormData((prev) => ({ ...prev, questions }))
  }

  const handleQuestionStructureChange = (questions: Question[], pages: SurveyPage[]) => {
    setFormData((prev) => ({
      ...prev,
      questions,
      pages,
    }))
  }

  const handlePagesChange = (pages: SurveyPage[]) => {
    setFormData((prev) => {
      const normalizedStructure = normalizeQuestionPageState(prev.questions, pages)
      return {
        ...prev,
        questions: normalizedStructure.questions,
        pages: normalizedStructure.pages
      }
    })
  }

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor, selecione apenas arquivos de imagem')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB')
      return
    }

    try {
      setUploadingLogo(true)
      const result = await lumi.tools.file.upload([file])
      
      if (result[0]?.fileUrl) {
        setFormData(prev => ({ ...prev, logoUrl: result[0].fileUrl! }))
        toast.success('Logo enviado com sucesso!')
      } else if (result[0]?.uploadError) {
        toast.error(`Erro ao enviar logo: ${result[0].uploadError}`)
      }
    } catch (error) {
      console.error('Erro ao fazer upload do logo:', error)
      toast.error('Erro ao fazer upload do logo')
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleRemoveLogo = async () => {
    if (!formData.logoUrl) return

    const currentLogoUrl = formData.logoUrl
    const confirmed = window.confirm('Deseja remover o logo desta pesquisa?')
    if (!confirmed) return

    try {
      setFormData(prev => ({ ...prev, logoUrl: '' }))
      toast.success('Logo removido com sucesso!')

      try {
        await lumi.tools.file.delete([currentLogoUrl])
      } catch (deleteError) {
        console.warn('Não foi possível excluir o arquivo remoto do logo:', deleteError)
        toast('O logo foi removido da pesquisa, mas o arquivo remoto não pode ser excluido.')
      }
    } catch (error) {
      console.error('Erro ao remover logo:', error)
      toast.error('Erro ao remover logo')
    }
  }

  return (
    <div 
      className="fixed inset-0 z-[100] flex h-[100dvh] w-[100dvw] items-stretch justify-stretch overflow-hidden bg-black bg-opacity-50 p-0"
      onClick={handleBackdropClick}
    >
      <div 
        className="flex h-[100dvh] max-h-[100dvh] w-[100dvw] max-w-none flex-col overflow-hidden rounded-none bg-white"
        onClick={handleModalClick}
      >
        <div className="flex h-full min-h-0 flex-col">
          {/* Header com steps */}
          <div className="flex items-center justify-between gap-4 p-6 border-b border-gray-200 shrink-0">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {survey ? 'Editar Pesquisa' : 'Nova Pesquisa'}
              </h2>
              <div className="flex items-center space-x-4 mt-2">
                <div className={`flex items-center space-x-2 ${currentStep === 1 ? 'text-blue-600' : currentStep > 1 ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    currentStep === 1 ? 'bg-blue-100' : currentStep > 1 ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    1
                  </div>
                  <span className="text-sm">Informações Basicas</span>
                </div>
                <div className={`w-8 h-px ${currentStep > 1 ? 'bg-green-300' : 'bg-gray-300'}`}></div>
                <div className={`flex items-center space-x-2 ${currentStep === 2 ? 'text-blue-600' : currentStep > 2 ? 'text-green-600' : 'text-gray-400'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    currentStep === 2 ? 'bg-blue-100' : currentStep > 2 ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    2
                  </div>
                  <span className="text-sm">Perguntas</span>
                </div>
              </div>
            </div>
            <button
              onClick={requestClose}
              className="text-gray-400 hover:text-gray-600 p-2"
              type="button"
              disabled={isSubmitting}
            >
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col overflow-hidden" data-focus-scope="survey-form">
            {currentStep === 1 && (
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Upload de Logo/Imagem */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo da Empresa/Cliente
                  </label>
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                    <div className="w-full max-w-sm">
                      {formData.logoUrl ? (
                        <div className={`relative overflow-hidden rounded-xl border border-gray-300 bg-gray-50 p-4 ${getLogoDisplayWidthClass(formData.logoDisplaySize || 'medium')}`}>
                          <img
                            src={formData.logoUrl}
                            alt="Logo"
                            className={`mx-auto h-auto w-full object-contain ${getLogoDisplayImageClass(formData.logoDisplaySize || 'medium')}`}
                          />
                          <button
                            type="button"
                            onClick={handleRemoveLogo}
                            className="absolute right-2 top-2 rounded-full bg-red-600 p-1 text-white transition-colors hover:bg-red-700"
                            title="Remover logo"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ) : (
                        <label className="flex h-40 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-blue-500">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                            disabled={uploadingLogo}
                          />
                          {uploadingLogo ? (
                            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
                          ) : (
                            <>
                              <Upload size={32} className="mb-2 text-gray-400" />
                              <span className="px-2 text-center text-xs text-gray-500">Clique para enviar</span>
                            </>
                          )}
                        </label>
                      )}
                    </div>
                    <div className="flex-1 space-y-4">
                      <p className="text-sm text-gray-600 mb-2">
                        Adicione o logo da sua empresa ou do cliente para personalizar a pesquisa.
                      </p>
                      <ul className="text-xs text-gray-500 space-y-1">
                        <li>- Formatos aceitos: JPG, PNG, GIF, SVG</li>
                        <li>- Tamanho máximo: 5MB</li>
                        <li>- Recomendado: imagens quadradas ou com fundo transparente</li>
                      </ul>
                      <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">
                          Tamanho do logo na pesquisa
                        </label>
                        <select
                          value={formData.logoDisplaySize || 'medium'}
                          onChange={(event) => setFormData((prev) => ({ ...prev, logoDisplaySize: event.target.value as Survey['logoDisplaySize'] }))}
                          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="small">Pequeno</option>
                          <option value="medium">Médio</option>
                          <option value="large">Grande</option>
                          <option value="original">Original</option>
                        </select>
                        <p className="mt-2 text-xs text-gray-500">
                          Tamanho selecionado: <strong>{getLogoDisplayHint(formData.logoDisplaySize || 'medium')}</strong>. Esse ajuste controla o logo exibido na abertura da pesquisa e na impressao/PDF.
                        </p>
                      </div>
                      <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                        <label className="flex items-start gap-3 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={formData.showLogoOnEveryPage !== false}
                            onChange={(event) => setFormData((prev) => ({ ...prev, showLogoOnEveryPage: event.target.checked }))}
                            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span>Mostrar logo em todas as telas da aplicação</span>
                        </label>
                        <label className="flex items-start gap-3 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={formData.showSurveyTitle !== false}
                            onChange={(event) => setFormData((prev) => ({ ...prev, showSurveyTitle: event.target.checked }))}
                            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span>Mostrar título da pesquisa na aplicação</span>
                        </label>
                        <label className="flex items-start gap-3 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={formData.showPageTitles !== false}
                            onChange={(event) => setFormData((prev) => ({ ...prev, showPageTitles: event.target.checked }))}
                            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span>Mostrar título das páginas na aplicação</span>
                        </label>
                        <label className="flex items-start gap-3 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            checked={formData.audioRecordingEnabled === true}
                            onChange={(event) => setFormData((prev) => ({ ...prev, audioRecordingEnabled: event.target.checked }))}
                            className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span>Esta pesquisa terá gravação de áudio</span>
                        </label>
                        <p className="text-xs text-gray-500">
                          Quando desmarcado, a aplicação abre direto na introducao da pesquisa, sem perguntar ao participante sobre gravação.
                        </p>
                      </div>
                      <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                        <label className="flex items-start gap-3 text-sm font-medium text-blue-900">
                          <input
                            type="checkbox"
                            checked={formData.clientResultsEnabled}
                            onChange={(event) => setFormData((prev) => ({
                              ...prev,
                              clientResultsEnabled: event.target.checked,
                              clientResultsToken: prev.clientResultsToken || generateClientResultsToken()
                            }))}
                            className="mt-0.5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span>Liberar portal de resultados para cliente</span>
                        </label>
                        {formData.clientResultsEnabled && (
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div>
                              <label className="mb-1 block text-xs font-medium text-blue-900">
                                Token do link
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={formData.clientResultsToken}
                                  onChange={(event) => setFormData((prev) => ({ ...prev, clientResultsToken: event.target.value }))}
                                  className="min-w-0 flex-1 rounded-lg border border-blue-200 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <button
                                  type="button"
                                  onClick={() => setFormData((prev) => ({ ...prev, clientResultsToken: generateClientResultsToken() }))}
                                  className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
                                >
                                  Gerar
                                </button>
                              </div>
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-medium text-blue-900">
                                {survey?.clientResultsPasswordHash || survey?.clientResultsPassword
                                  ? 'Nova senha de acesso'
                                  : 'Senha de acesso'}
                              </label>
                              <input
                                type="password"
                                value={formData.clientResultsPassword}
                                onChange={(event) => setFormData((prev) => ({ ...prev, clientResultsPassword: event.target.value }))}
                                placeholder={survey?.clientResultsPasswordHash || survey?.clientResultsPassword ? 'Deixe em branco para manter a senha atual' : ''}
                                className="w-full rounded-lg border border-blue-200 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <p className="mt-1 text-xs text-blue-800">
                                Esta senha libera apenas o portal de resultados. O cliente não possui login no app administrativo.
                              </p>
                            </div>
                            {formData.clientResultsToken && (
                              <div className="md:col-span-2">
                                <label className="mb-1 block text-xs font-medium text-blue-900">
                                  Link do cliente
                                </label>
                                <input
                                  type="text"
                                  readOnly
                                  value={`${CLIENT_RESULTS_PUBLIC_BASE_URL}/client-results/${formData.clientResultsToken}`}
                                  className="w-full rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm text-blue-900"
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="space-y-3 rounded-lg border border-green-200 bg-green-50 p-4">
                        <label className="flex items-start gap-3 text-sm font-medium text-green-900">
                          <input
                            type="checkbox"
                            checked={formData.publicResponseEnabled}
                            onChange={(event) => setFormData((prev) => ({
                              ...prev,
                              publicResponseEnabled: event.target.checked,
                              publicResponseToken: prev.publicResponseToken || generateClientResultsToken()
                            }))}
                            className="mt-0.5 rounded border-green-300 text-green-600 focus:ring-green-500"
                          />
                          <span>Liberar link de resposta pública (sem e-mail/senha)</span>
                        </label>
                        {formData.publicResponseEnabled && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                              <div>
                                <label className="mb-1 block text-xs font-medium text-green-900">
                                  Token do link público
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={formData.publicResponseToken}
                                    onChange={(event) => setFormData((prev) => ({ ...prev, publicResponseToken: event.target.value }))}
                                    className="min-w-0 flex-1 rounded-lg border border-green-200 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-green-500"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setFormData((prev) => ({ ...prev, publicResponseToken: generateClientResultsToken() }))}
                                    className="rounded-lg bg-green-600 px-3 py-2 text-sm text-white hover:bg-green-700 hover:text-white"
                                  >
                                    Gerar
                                  </button>
                                </div>
                              </div>
                              <div className="flex flex-col justify-end">
                                <p className="text-xs text-green-800 pb-2">
                                  Este token torna o link único. Qualquer pessoa com o link poderá responder à pesquisa sem autenticação.
                                </p>
                              </div>
                            </div>
                            {formData.publicResponseToken && (
                              <div>
                                <label className="mb-1 block text-xs font-medium text-green-900">
                                  Link para responder
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    readOnly
                                    value={`${CLIENT_RESULTS_PUBLIC_BASE_URL}/responder-pesquisa/${formData.publicResponseToken}`}
                                    className="w-full rounded-lg border border-green-200 bg-white px-3 py-2 text-sm text-green-900"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard.writeText(`${CLIENT_RESULTS_PUBLIC_BASE_URL}/responder-pesquisa/${formData.publicResponseToken}`)
                                      toast.success('Link copiado para a área de transferência!')
                                    }}
                                    className="rounded-lg bg-green-600 px-3 py-2 text-sm text-white hover:bg-green-700 hover:text-white whitespace-nowrap"
                                  >
                                    Copiar
                                  </button>
                                  {onShowQRCode && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        onShowQRCode({
                                          _id: survey?._id || 'new',
                                          title: formData.title || 'Pesquisa',
                                          description: formData.description || '',
                                          status: 'active',
                                          languages: formData.languages,
                                          tags: [],
                                          createdAt: '',
                                          updatedAt: '',
                                          publicResponseEnabled: true,
                                          publicResponseToken: formData.publicResponseToken
                                        })
                                      }}
                                      className="rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white hover:bg-emerald-700 hover:text-white whitespace-nowrap"
                                    >
                                      QR Code
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cliente do estudo
                  </label>
                  <select
                    value={formData.clientId}
                    onChange={(event) => {
                      const selectedClient = clients.find((client) => client._id === event.target.value)
                      setFormData((prev) => ({
                        ...prev,
                        clientId: selectedClient?._id || '',
                        clientName: selectedClient?.name || ''
                      }))
                    }}
                    disabled={loadingClients}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                  >
                    <option value="">{loadingClients ? 'Carregando clientes...' : 'Sem cliente vinculado'}</option>
                    {clients
                      .filter((client) => client.isActive || client._id === formData.clientId)
                      .map((client) => (
                        <option key={client._id} value={client._id}>
                          {client.name}{client.companyName ? ` - ${client.companyName}` : ''}
                        </option>
                      ))}
                  </select>
                  <p className="mt-2 text-xs text-gray-500">
                    Use este campo para indicar a qual cliente a pesquisa pertence e liberar resultados por cliente.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título da Pesquisa *
                  </label>
                  <RichTextEditor
                    value={formData.title}
                    onChange={(value) => setFormData(prev => ({ ...prev, title: value }))}
                    placeholder="Digite o título da pesquisa"
                    height="100px"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição *
                  </label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                    placeholder="Descreva os objetivos e contexto da pesquisa"
                    height="150px"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Público-alvo
                  </label>
                  <RichTextEditor
                    value={formData.targetAudience}
                    onChange={(value) => setFormData(prev => ({ ...prev, targetAudience: value }))}
                    placeholder="Ex: Clientes ativos, Usuarios beta, etc."
                    height="80px"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Idiomas Suportados
                  </label>
                  <div className="flex flex-wrap gap-4">
                    {[
                      { code: 'pt', label: 'Portugues' },
                      { code: 'en', label: 'Ingles' },
                      { code: 'es', label: 'Espanhol' }
                    ].map((lang) => (
                      <label key={lang.code} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.languages.includes(lang.code)}
                          onChange={(e) => handleLanguageChange(lang.code, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{lang.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data de Início
                    </label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data de Término
                    </label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (separadas por vírgula)
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ex: satisfação, produto, experiência"
                  />
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="flex-1 min-h-0 flex flex-col overflow-hidden px-6 pb-6 pt-6">
                <div className="shrink-0 flex flex-col gap-4 border-b border-gray-200 pb-4 md:flex-row md:items-center md:justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Perguntas da Pesquisa</h3>
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowPageManager(true)}
                      className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <Layers size={18} />
                      <span>Gerenciar Páginas</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowQuestionBranches(true)}
                      className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                      <GitBranch size={18} />
                      <span>Desvios de Perguntas</span>
                    </button>
                  </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto pr-1">
                  <QuestionBuilder
                    questions={formData.questions}
                    onChange={handleQuestionsChange}
                    onStructureChange={handleQuestionStructureChange}
                    onPreview={onPreviewQuestion}
                    pages={formData.pages}
                    onPagesChange={handlePagesChange}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-col gap-4 border-t border-gray-200 p-6 shrink-0 sm:flex-row sm:items-center sm:justify-between">
              <div>
                {currentStep === 2 && (
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={isSubmitting}
                  >
                    Voltar
                  </button>
                )}
              </div>

              <div className="flex flex-wrap justify-end gap-4">
                <button
                  type="button"
                  onClick={requestClose}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isSubmitting && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  <span>
                    {isSubmitting 
                      ? 'Salvando...' 
                      : currentStep === 1 
                        ? 'Próximo' 
                        : (survey ? 'Atualizar Pesquisa' : 'Criar Pesquisa')
                    }
                  </span>
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {showPageManager && (
        <PageManager
          pages={formData.pages}
          questions={formData.questions}
          onPagesChange={handlePagesChange}
          onQuestionsChange={handleQuestionsChange}
          onClose={() => setShowPageManager(false)}
        />
      )}

      {/* Modal de desvios de perguntas */}
      {false && showQuestionBranches && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Desvios de Perguntas</h2>
              <button
                onClick={() => setShowQuestionBranches(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <p className="text-gray-600 mb-4">
                Configure as regras de caminho da pesquisa com base nas respostas do usuário.
              </p>
              
              <div className="text-center py-12 text-gray-500">
                Esta tela esta em revisao.
              </div>
            </div>
            
            <div className="flex justify-end space-x-4 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowQuestionBranches(false)}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {showQuestionBranches && (
        <QuestionBranchManager
          questions={formData.questions}
          onQuestionsChange={handleQuestionsChange}
          onClose={() => setShowQuestionBranches(false)}
        />
      )}
    </div>
  )
}

// Modal de preview de pergunta
const QuestionPreviewModal: React.FC<{
  question: Question
  onClose: () => void
}> = ({ question, onClose }) => {
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation()
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const renderQuestionPreview = () => {
    switch (question.type) {
      case 'text':
      case 'opentext':
        return (
          <textarea
            placeholder="Digite sua resposta..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
            rows={4}
            disabled
          />
        )

      case 'email':
        return (
          <input
            type="email"
            placeholder="Digite seu e-mail..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            disabled
          />
        )

      case 'number':
        return (
          <input
            type="number"
            placeholder="Digite um número..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            disabled
          />
        )

      case 'single':
        return (
          <div className="min-w-0 space-y-2 overflow-x-hidden">
            {question.options?.map((option, index) => (
              <label key={option.id} className="flex min-w-0 items-start space-x-2">
                <input type="radio" name="preview-radio" disabled className="mt-1 shrink-0" />
                <span className="min-w-0 whitespace-normal break-words [overflow-wrap:anywhere]">
                  {stripHtml(option.text)}
                </span>
              </label>
            ))}
          </div>
        )

      case 'checkbox':
      case 'multiple':
        return (
          <div className="min-w-0 space-y-2 overflow-x-hidden">
            {question.options?.map((option, index) => (
              <label key={option.id} className="flex min-w-0 items-start space-x-2">
                <input type="checkbox" disabled className="mt-1 shrink-0" />
                <span className="min-w-0 whitespace-normal break-words [overflow-wrap:anywhere]">
                  {stripHtml(option.text)}
                </span>
              </label>
            ))}
          </div>
        )

      case 'dropdown':
        return (
          <select className="w-full px-3 py-2 border border-gray-300 rounded-lg" disabled>
            <option>Selecione uma opção...</option>
            {question.options?.map((option, index) => (
              <option key={option.id}>{stripHtml(option.text)}</option>
            ))}
          </select>
        )

      case 'rating':
        return (
          <div className="min-w-0 flex flex-wrap items-center gap-1 overflow-x-hidden">
            {Array.from({ length: question.maxValue || 5 }, (_, i) => (
              <button key={i} className="text-2xl text-gray-300 hover:text-yellow-400" disabled>
                &#9733;
              </button>
            ))}
            <div className="min-w-0 text-sm text-gray-500 whitespace-normal break-words [overflow-wrap:anywhere] sm:ml-4">
              {stripHtml(question.minLabel || '')} ... {stripHtml(question.maxLabel || '')}
            </div>
          </div>
        )

      case 'slider':
        return (
          <div>
            <input
              type="range"
              min={question.minValue || 0}
              max={question.maxValue || 100}
              step={question.stepValue || 1}
              className="w-full"
              disabled
            />
            <div className="mt-2 flex flex-wrap justify-between gap-2 text-sm text-gray-500">
              <span className="min-w-0 whitespace-normal break-words [overflow-wrap:anywhere]">{stripHtml(question.minLabel || '')}</span>
              <span className="min-w-0 whitespace-normal break-words [overflow-wrap:anywhere] text-right">{stripHtml(question.maxLabel || '')}</span>
            </div>
          </div>
        )

      case 'datetime':
        return (
          <input
            type={question.dateFormat === 'time' ? 'time' : question.dateFormat === 'datetime' ? 'datetime-local' : 'date'}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            disabled
          />
        )

      case 'multitext':
        return (
          <div className="space-y-4">
            {question.textBoxes?.map((box, index) => (
              <div key={box.id}>
                <label className="mb-1 block min-w-0 text-sm font-medium text-gray-700 whitespace-normal break-words [overflow-wrap:anywhere]">
                  {stripHtml(box.label)} {box.required && <span className="text-red-500">*</span>}
                </label>
                <input
                  type={box.type}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={box.placeholder}
                  disabled
                />
              </div>
            ))}
            {question.sumValidation?.enabled && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  A soma dos campos deve ser igual a {question.sumValidation.targetSum}
                </p>
              </div>
            )}
          </div>
        )

      default:
        return (
          <div className="p-4 bg-gray-100 rounded-lg text-center text-gray-500">
            Preview não disponível para este tipo de pergunta
          </div>
        )
    }
  }

  const stripHtml = (html: string) => {
    const tmp = document.createElement('div')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120] p-4"
      onClick={handleBackdropClick}
    >
      <div 
        className="w-full max-w-2xl max-h-[90vh] overflow-x-hidden overflow-y-auto rounded-xl bg-white"
        onClick={handleModalClick}
      >
        <div className="min-w-0 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Preview da Pergunta</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2"
              type="button"
            >
              <X size={20} />
            </button>
          </div>

          <div className="min-w-0 space-y-4 overflow-x-hidden">
            <div className="min-w-0">
              <h3 className="mb-2 min-w-0 whitespace-normal break-words text-lg font-semibold text-gray-900 [overflow-wrap:anywhere]">
                {stripHtml(question.title)}
                {question.required && <span className="text-red-500 ml-1">*</span>}
              </h3>
              {question.description && (
                <p className="mb-4 min-w-0 whitespace-normal break-words text-gray-600 [overflow-wrap:anywhere]">
                  {stripHtml(question.description)}
                </p>
              )}
            </div>

            {question.imageUrl && (
              <div className={`overflow-hidden rounded-xl border border-gray-200 bg-gray-50 p-3 ${getImageDisplayWidthClass(question.imageDisplaySize)}`}>
                <img
                  src={question.imageUrl}
                  alt={stripHtml(question.title)}
                  className="h-auto w-full rounded-lg object-contain"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Tamanho configurado: {getImageDisplayHint(question.imageDisplaySize)}
                </p>
              </div>
            )}

            <div className="min-w-0 overflow-x-hidden rounded-lg border border-gray-200 p-4">
              {renderQuestionPreview()}
            </div>

            <div className="text-sm text-gray-500">
              <span className="font-medium">Tipo:</span> {question.type}
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              type="button"
            >
              Fechar Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface PublicLinkModalProps {
  survey: Survey
  onClose: () => void
}

const PublicLinkModal: React.FC<PublicLinkModalProps> = ({ survey, onClose }) => {
  const link = `${CLIENT_RESULTS_PUBLIC_BASE_URL}/responder-pesquisa/${survey.publicResponseToken}`
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(link)}`

  const handleCopyLink = () => {
    navigator.clipboard.writeText(link)
    toast.success('Link copiado com sucesso!')
  }

  const handleDownloadQR = async () => {
    try {
      const response = await fetch(qrCodeUrl)
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = `qrcode_${survey.title.toLowerCase().replace(/\s+/g, '_') || 'pesquisa'}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(blobUrl)
      toast.success('QR Code baixado!')
    } catch (err) {
      console.error(err)
      window.open(qrCodeUrl, '_blank')
    }
  }

  const handlePrintQR = () => {
    const printWindow = window.open('', '_blank', 'width=600,height=600')
    if (!printWindow) {
      toast.error('Não foi possível abrir a janela de impressão.')
      return
    }
    printWindow.document.write(`
      <html>
        <head>
          <title>QR Code - ${survey.title}</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              height: 100vh;
              margin: 0;
              text-align: center;
            }
            h1 {
              font-size: 1.5rem;
              color: #1e293b;
              margin-bottom: 2rem;
              max-width: 80%;
            }
            img {
              width: 250px;
              height: 250px;
              box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 12px;
            }
            p {
              font-size: 0.875rem;
              color: #64748b;
              margin-top: 1.5rem;
            }
          </style>
        </head>
        <body>
          <h1>${survey.title}</h1>
          <img src="${qrCodeUrl}" alt="QR Code" />
          <p>Escaneie o QR Code acima para responder à pesquisa pública.</p>
          <script>
            window.onload = () => {
              window.print();
              setTimeout(() => window.close(), 500);
            };
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl border border-gray-100 transition-all transform scale-100 duration-300">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/50 px-6 py-4">
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
              <Share2 size={20} />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Link e QR Code</h3>
              <p className="text-xs text-gray-500">Respostas públicas da pesquisa</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Survey Title */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Pesquisa</h4>
            <p className="text-sm font-medium text-gray-800 mt-1 line-clamp-2">{survey.title}</p>
          </div>

          {/* Link Section */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">
              Link de Resposta Pública
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={link}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:outline-none"
              />
              <button
                type="button"
                onClick={handleCopyLink}
                className="flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 transition-colors whitespace-nowrap shadow-sm hover:shadow"
              >
                <Copy size={16} />
                <span>Copiar</span>
              </button>
            </div>
          </div>

          {/* QR Code Section */}
          <div className="flex flex-col items-center justify-center py-4 border-t border-gray-100">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-4 self-start">
              Código QR para Impressão ou Divulgação
            </span>
            <div className="relative group p-4 bg-white border border-gray-200 rounded-2xl shadow-inner transition-transform duration-300 hover:scale-[1.02]">
              <img
                src={qrCodeUrl}
                alt="QR Code"
                className="w-48 h-48 block"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://placehold.co/200x200?text=QR+Code'
                }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center px-4">
              Aponte a câmera do celular para este código para acessar o formulário diretamente.
            </p>

            <div className="flex w-full gap-3 mt-5">
              <button
                type="button"
                onClick={handleDownloadQR}
                className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
              >
                <Download size={16} />
                <span>Baixar Imagem</span>
              </button>
              <button
                type="button"
                onClick={handlePrintQR}
                className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
              >
                <Printer size={16} />
                <span>Imprimir</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-gray-100 bg-gray-50/50 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300 transition-colors"
            type="button"
          >
            Fechar
          </button>
        </div>

      </div>
    </div>
  )
}

export default SurveyManager